import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import GIF from "gif.js";
import "./InSpektViewer.css";
import {
  LayoutGrid, LogOut, User, Layers,
  Maximize2, Shield, Loader2, X, Info, Search, Palette, Ruler,
  PenLine, Hexagon, Type, Save, Trash2, Edit3, CheckCircle, ChevronDown, ChevronRight,
  Crosshair, BarChart2, BoxSelect,
} from "lucide-react";
import Select from "react-select";
import ProjectSelector from "./ProjectSelector";
import InSpektLoader from "./InSpektLoader";
import { OnboardingModal, OnboardingTour } from "./Onboarding";
import { ClientLayerUpload } from "./ClientLayerUpload";
import TrendLine from "./TrendLine";
import SectionProfileChart from "../functions/SectionProfileChart";
import Surface3DChart      from "../functions/Surface3DChart";
import { useDuckDB } from "../hooks/useDuckDB";
import neCountriesData from "../data/ne_countries.json";
import nePlacesData    from "../data/ne_places.json";

// ── Fixed tool panel (InSpekt-branded, sidebar-aware) ─────────────────────────
function ToolPanel({ title, children, onClose, sidebarOpen = false, width = 400 }) {
  return (
    <div className="tool-panel" style={{ left: 10, width }}>
      <div className="tool-panel-header">
        <span className="tool-panel-title">{title}</span>
        <button className="tool-panel-close" onClick={onClose}><X size={14} /></button>
      </div>
      <div className="tool-panel-body">{children}</div>
    </div>
  );
}

const PORTAL_API_URL = process.env.REACT_APP_PORTAL_API_URL || "https://portal.geospekter.com";
const SAT_URLS = {
  S1:    process.env.REACT_APP_SAT_URL_S1    || "https://sentinel.esa.int/web/sentinel/missions/sentinel-1",
  NISAR: process.env.REACT_APP_SAT_URL_NISAR || "https://nisar.jpl.nasa.gov",
};


// Served from public/cesium/ (copied from node_modules/cesium/Build/Cesium at build time)
const CESIUM_BASE      = "/cesium";
const CESIUM_ION_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI5ODZiNzZiMy1iNGViLTQ5YTEtOWQ1ZS0yMDUzYWRlNGUwY2EiLCJpZCI6NDA4NDYwLCJpYXQiOjE3NzQzNjUyNjZ9.jeuMZTTmpS0E7jZp_QNeOSMlly5R8xICnznMVLy3FbQ";

// Google Photorealistic 3D Tiles — Cesium Ion asset 2275207
// Requires the asset to be added to the Ion account linked to CESIUM_ION_TOKEN.
const GOOGLE_TILES_ION_ASSET_ID = 2275207;

// ── Rounded-card billboard (shared by P1/P2, Start/End, corner labels) ────────
function makeLabelBillboard(text, { pixelOffsetY = -8 } = {}) {
  const Cesium  = window.Cesium;
  const dpr     = Math.min(window.devicePixelRatio || 1, 2);
  const font    = "700 10px 'Inter','Helvetica Neue',sans-serif";
  const pad     = { x: 9, y: 5 };
  const lineGap = 3;
  const radius  = 5;
  const lineH   = 14;
  const lines   = text.split("\n");

  const tmp = document.createElement("canvas");
  const tc  = tmp.getContext("2d");
  tc.font   = font;
  const logW = Math.ceil(Math.max(...lines.map(l => tc.measureText(l).width)) + pad.x * 2);
  const logH = lines.length * lineH + (lines.length - 1) * lineGap + pad.y * 2;

  const canvas = document.createElement("canvas");
  canvas.width  = logW * dpr;
  canvas.height = logH * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(logW - radius, 0);
  ctx.quadraticCurveTo(logW, 0, logW, radius);
  ctx.lineTo(logW, logH - radius);
  ctx.quadraticCurveTo(logW, logH, logW - radius, logH);
  ctx.lineTo(radius, logH);
  ctx.quadraticCurveTo(0, logH, 0, logH - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.fillStyle = "rgba(255,255,255,0.96)";
  ctx.fill();

  ctx.font         = font;
  ctx.fillStyle    = "#111827";
  ctx.textBaseline = "middle";
  lines.forEach((line, i) => {
    ctx.fillText(line, pad.x, pad.y + lineH / 2 + i * (lineH + lineGap));
  });

  return {
    image:                    canvas,
    width:                    logW,
    height:                   logH,
    verticalOrigin:           Cesium.VerticalOrigin.BOTTOM,
    pixelOffset:              new Cesium.Cartesian2(0, pixelOffsetY),
    disableDepthTestDistance: Number.POSITIVE_INFINITY,
  };
}

// Maps Nominatim class/type to a display emoji and short category label.
function nominatimMeta(cls, typ) {
  if (cls === "place") {
    if (typ === "country")                              return { emoji: "🌍", label: "Country" };
    if (["state","region","province"].includes(typ))   return { emoji: "🗺️", label: "Region"  };
    if (["city","municipality"].includes(typ))         return { emoji: "🏙️", label: "City"    };
    if (typ === "town")                                 return { emoji: "🏘️", label: "Town"    };
    if (["village","hamlet","suburb"].includes(typ))   return { emoji: "🏡", label: "Village" };
    if (typ === "island")                               return { emoji: "🏝️", label: "Island"  };
  }
  if (cls === "boundary")                               return { emoji: "🗺️", label: "Area"    };
  if (cls === "aeroway" && typ === "aerodrome")         return { emoji: "✈️",  label: "Airport" };
  if (cls === "railway")                                return { emoji: "🚉", label: "Station" };
  if (cls === "amenity") {
    if (["restaurant","cafe","fast_food"].includes(typ)) return { emoji: "🍽️", label: "Food"      };
    if (["hotel","hostel","motel"].includes(typ))        return { emoji: "🏨", label: "Hotel"     };
    if (["hospital","clinic"].includes(typ))             return { emoji: "🏥", label: "Medical"   };
    if (["school","university","college"].includes(typ)) return { emoji: "🎓", label: "Education" };
    if (typ === "fuel")                                  return { emoji: "⛽", label: "Fuel"      };
    if (typ === "parking")                               return { emoji: "🅿️", label: "Parking"   };
    if (typ === "bank")                                  return { emoji: "🏦", label: "Bank"      };
    if (typ === "pharmacy")                              return { emoji: "💊", label: "Pharmacy"  };
    return { emoji: "📍", label: "Amenity" };
  }
  if (cls === "tourism") {
    if (["attraction","museum","gallery"].includes(typ)) return { emoji: "🏛️", label: "Attraction" };
    if (typ === "viewpoint")                             return { emoji: "👁️", label: "Viewpoint"  };
    if (["hotel","hostel","motel"].includes(typ))        return { emoji: "🏨", label: "Hotel"      };
    return { emoji: "📸", label: "Tourism" };
  }
  if (cls === "historic")  return { emoji: "🏛️", label: "Historic" };
  if (cls === "natural")   return { emoji: "🌿", label: "Nature"   };
  if (cls === "shop")      return { emoji: "🛍️", label: "Shop"     };
  if (cls === "highway")   return { emoji: "🛣️", label: "Road"     };
  return { emoji: "📍", label: "" };
}

// Returns camera altitude for a Nominatim result — bbox-based when available,
// otherwise a type-based fallback so POIs zoom in closer than cities.
function nominatimAltitude(bbox, cls, typ) {
  if (bbox) {
    const latSpan = Math.abs(parseFloat(bbox[1]) - parseFloat(bbox[0]));
    const lonSpan = Math.abs(parseFloat(bbox[3]) - parseFloat(bbox[2]));
    const spanDeg = Math.max(latSpan, lonSpan);
    if (spanDeg > 0.002) return Math.max(300, Math.min(3000000, spanDeg * 111000 * 1.4));
  }
  if (cls === "place") {
    if (typ === "country")                            return 2000000;
    if (["state","region","province"].includes(typ)) return 400000;
    if (["city","municipality"].includes(typ))       return 12000;
    if (typ === "town")                               return 5000;
    if (["village","hamlet","suburb"].includes(typ)) return 2000;
  }
  if (cls === "boundary") return 80000;
  return 400; // POI default — close-up street view
}

// ── Area & Perimeter tool — geodesic calculations ─────────────────────────────
function geodesicAreaM2(verts) {
  if (verts.length < 3) return 0;
  const R = 6378137;
  let area = 0;
  const n = verts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = verts[i].lat * Math.PI / 180;
    const lat2 = verts[j].lat * Math.PI / 180;
    const dLon = (verts[j].lon - verts[i].lon) * Math.PI / 180;
    area += dLon * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return Math.abs(area * R * R / 2);
}
function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6378137;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function geodesicPerimeterM(verts) {
  let total = 0;
  const n = verts.length;
  for (let i = 0; i < n; i++) total += haversineM(verts[i].lat, verts[i].lon, verts[(i + 1) % n].lat, verts[(i + 1) % n].lon);
  return total;
}
function pointInPoly(lat, lon, verts) {
  let inside = false;
  const n = verts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = verts[i].lon, yi = verts[i].lat;
    const xj = verts[j].lon, yj = verts[j].lat;
    if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

// Formats a corner label in planar (metres from SW corner) or geographic mode.
function formatCornerLabel(lon, lat, minLon, minLat, mPerLon, mPerLat, mode) {
  if (mode === "coordinates") {
    const fLat = v => `${Math.abs(v).toFixed(4)}°${v >= 0 ? "N" : "S"}`;
    const fLon = v => `${Math.abs(v).toFixed(4)}°${v >= 0 ? "E" : "W"}`;
    return `${fLat(lat)}\n${fLon(lon)}`;
  }
  const x = Math.round((lon - minLon) * mPerLon);
  const y = Math.round((lat - minLat) * mPerLat);
  return `${x} m E\n${y} m N`;
}

// Natural Earth data bundled directly — avoids Cloudflare Pages static-file routing issues

// ── Globe visual helpers ──────────────────────────────────────────────────────
function applySceneAtmosphere(viewer) {
  viewer.scene.skyBox.show                = true;
  viewer.scene.skyAtmosphere.show         = true;
  // Reduced from 20 — atmospheric scatter is a full GPU pass; 5 is visually fine
  viewer.scene.skyAtmosphere.atmosphereScatteringIntensity = 5.0;
  viewer.scene.skyAtmosphere.hueShift        =  0.0;
  viewer.scene.skyAtmosphere.saturationShift = -0.2;
  viewer.scene.skyAtmosphere.brightnessShift =  0.4;
  // Globe is hidden (Google 3D tiles used instead) — fog adds GPU cost with no benefit
  viewer.scene.fog.enabled = false;
}


// ── Haversine distance (km) between two lat/lon points ───────────────────────
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const fmtKm = km => km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(2)} km`;

// ── Linear interpolation for null gaps in section profile ────────────────────
function interpolateNulls(samples, prop) {
  for (let i = 0; i < samples.length; i++) {
    if (samples[i][prop] !== null) continue;
    let prev = -1, next = -1;
    for (let j = i - 1; j >= 0; j--) { if (samples[j][prop] !== null) { prev = j; break; } }
    for (let j = i + 1; j < samples.length; j++) { if (samples[j][prop] !== null) { next = j; break; } }
    if (prev === -1 && next === -1) continue;
    if (prev === -1) { samples[i][prop] = samples[next][prop]; continue; }
    if (next === -1) { samples[i][prop] = samples[prev][prop]; continue; }
    const t = (i - prev) / (next - prev);
    samples[i][prop] = samples[prev][prop] + t * (samples[next][prop] - samples[prev][prop]);
  }
}

// InSpekt: epoch/time-slider features are not included in this build.

// ── Palette definitions (5 stops each, hex) ──────────────────────────────────
export const PALETTES = [
  { id: "default",  name: "Default",         stops: ["#ce0202", "#ece10a", "#15e189", "#24d7e0", "#1641dc"] },
  { id: "rdbu",     name: "Red–Blue",      stops: ["#b2182b", "#ef8a62", "#f7f7f7", "#67a9cf", "#2166ac"] },
  { id: "rdylgn",   name: "Red–Yel–Green", stops: ["#d73027", "#fc8d59", "#ffffbf", "#91cf60", "#1a9850"] },
  { id: "spectral", name: "Spectral",      stops: ["#d53e4f", "#fc8d59", "#ffffbf", "#99d594", "#3288bd"] },
  { id: "magma",    name: "Magma",         stops: ["#000004", "#721f81", "#cb4679", "#f8765c", "#fcfdbf"] },
  { id: "viridis",  name: "Viridis",       stops: ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"] },
];

export const DEFAULT_PALETTE = PALETTES[0];

// hex "#rrggbb" → [r,g,b] each 0–1
function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

// CSS gradient string from hex stops array
export function stopsToGradient(stops) {
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

// ── 5-stop velocity colormap — accepts optional hex stops array ───────────────
function velocityColor(v, minV, maxV, hexStops) {
  const Cesium = window.Cesium;
  const range  = Math.max(Math.abs(minV), Math.abs(maxV), 0.001);
  const t      = Math.max(-1, Math.min(1, v / range));

  const stops = (hexStops || DEFAULT_PALETTE.stops).map(hexToRgb);

  const pos = (t + 1) / 2 * (stops.length - 1);
  const i   = Math.min(Math.floor(pos), stops.length - 2);
  const f   = pos - i;
  const a   = stops[i], b = stops[i + 1];
  return new Cesium.Color(
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
    a[2] + (b[2] - a[2]) * f,
    0.9
  );
}

// ── Legend Range Slider ───────────────────────────────────────────────────────
function fmtDCol(col) {
  const m = col?.match(/D_(\d{4})(\d{2})(\d{2})/i);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : (col || null);
}

function LegendRangeSlider({ colorStops, filter, onChange, onChangeEnd }) {
  const barRef         = useRef(null);
  const draggingRef    = useRef(null);
  const filterRef      = useRef(filter);
  filterRef.current    = filter;
  const onChangeRef    = useRef(onChange);
  onChangeRef.current  = onChange;
  const onChangeEndRef = useRef(onChangeEnd);
  onChangeEndRef.current = onChangeEnd;

  const startDrag = (handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = handle;

    const onMove = (ev) => {
      const bar = barRef.current;
      if (!bar || !draggingRef.current) return;
      const rect = bar.getBoundingClientRect();
      const x    = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      const f    = filterRef.current;
      if (draggingRef.current === 'lo') {
        onChangeRef.current({ lo: Math.min(x, f.hi - 0.04), hi: f.hi });
      } else {
        onChangeRef.current({ lo: f.lo, hi: Math.max(x, f.lo + 0.04) });
      }
    };
    const onUp = () => {
      draggingRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      onChangeEndRef.current(filterRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const { lo, hi } = filter;
  const grad = stopsToGradient(colorStops);

  return (
    <div ref={barRef} style={{ position: 'relative', height: 9, borderRadius: 4, userSelect: 'none', touchAction: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 4, background: grad }} />
      {lo > 0.001 && (
        <div style={{ position: 'absolute', left: 0, top: 0, width: `${lo * 100}%`, height: '100%',
          background: 'rgba(160,160,160,0.85)', borderRadius: '4px 0 0 4px' }} />
      )}
      {hi < 0.999 && (
        <div style={{ position: 'absolute', left: `${hi * 100}%`, top: 0, right: 0, height: '100%',
          background: 'rgba(160,160,160,0.85)', borderRadius: '0 4px 4px 0' }} />
      )}
      <div onPointerDown={e => startDrag('lo', e)}
        style={{ position: 'absolute', left: `${lo * 100}%`, top: '50%',
          transform: 'translate(-50%, -50%)', width: 12, height: 12, borderRadius: '50%',
          background: '#fff', border: '2px solid #374151', cursor: 'ew-resize', zIndex: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
      <div onPointerDown={e => startDrag('hi', e)}
        style={{ position: 'absolute', left: `${hi * 100}%`, top: '50%',
          transform: 'translate(-50%, -50%)', width: 12, height: 12, borderRadius: '50%',
          background: '#fff', border: '2px solid #374151', cursor: 'ew-resize', zIndex: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
    </div>
  );
}

// ── Legend Style Panel ────────────────────────────────────────────────────────
function LegendStylePanel({ layerName, currentStops, onApply, onClose }) {
  const [customStops, setCustomStops] = useState([...currentStops]);
  const [pos, setPos] = useState({ x: 335, y: 120 });
  const dragOrigin = useRef(null);
  const activePreset = PALETTES.find(p => JSON.stringify(p.stops) === JSON.stringify(customStops));

  const applyPreset = (p) => {
    setCustomStops([...p.stops]);
    onApply([...p.stops]);
  };

  const updateCustomStop = (idx, hex) => {
    const next = [...customStops];
    next[idx] = hex;
    setCustomStops(next);
    onApply(next);
  };

  const onPanelMouseDown = (e) => {
    if (e.target.closest("button") || e.target.closest("input")) return;
    dragOrigin.current = { startX: e.clientX - pos.x, startY: e.clientY - pos.y };
    const onMove = (ev) => setPos({ x: ev.clientX - dragOrigin.current.startX, y: ev.clientY - dragOrigin.current.startY });
    const onUp = () => { dragOrigin.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const STOP_LABELS = ["Neg max", "Neg mid", "Zero", "Pos mid", "Pos max"];

  return (
    <div style={{
      position: "fixed", zIndex: 10000,
      left: pos.x, top: pos.y,
      width: 280,
      background: "#fff",
      borderRadius: 8,
      boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", borderBottom: "1px solid #e5e7eb",
        background: "#f9fafb", cursor: "grab",
      }} onMouseDown={onPanelMouseDown}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
          Legend Style — {layerName}
        </span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 2, display: "flex" }}>
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: "12px 14px" }}>
        {/* Presets */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#6b7280", marginBottom: 8 }}>
          Presets
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
          {PALETTES.map(p => {
            const isActive = activePreset?.id === p.id;
            return (
              <div key={p.id} onClick={() => applyPreset(p)} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 8px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${isActive ? "#1a73e8" : "#e5e7eb"}`,
                background: isActive ? "#e8f0fe" : "#fff",
                transition: "all 0.15s",
              }}>
                <div style={{ flex: 1, height: 10, borderRadius: 3, background: stopsToGradient(p.stops) }} />
                <span style={{ fontSize: 11, color: isActive ? "#1a73e8" : "#374151", fontWeight: isActive ? 600 : 400, whiteSpace: "nowrap", minWidth: 90 }}>
                  {p.name}
                </span>
                {isActive && <span style={{ color: "#1a73e8", fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
            );
          })}
        </div>

        {/* Custom */}
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#6b7280", marginBottom: 8 }}>
          Custom
        </div>
        <div style={{ height: 10, borderRadius: 3, background: stopsToGradient(customStops), marginBottom: 10 }} />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {customStops.map((hex, idx) => (
            <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <input
                type="color"
                value={hex}
                onChange={e => updateCustomStop(idx, e.target.value)}
                style={{ width: 28, height: 28, border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: 1 }}
              />
              <span style={{ fontSize: 9, color: "#9ca3af", textAlign: "center", lineHeight: 1.2 }}>{STOP_LABELS[idx]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Color a point by GeoNames feature_class (first letter of feature_code) ───
function featureColor(featureCode) {
  const Cesium = window.Cesium;
  const fc = (featureCode || "").charAt(0).toUpperCase();
  const map = {
    P: "#ff6b35",  // populated places — orange
    T: "#a0856b",  // terrain (mountains, hills) — brown
    H: "#4488ff",  // hydrography (rivers, lakes) — blue
    V: "#44cc66",  // vegetation (forest, heath) — green
    S: "#ffcc00",  // spots (buildings, farms) — yellow
    A: "#cc44ff",  // administrative — purple
    L: "#88cc44",  // land use — lime
    R: "#aaaaaa",  // roads — gray
  };
  return Cesium.Color.fromCssColorString(map[fc] || "#ffffff").withAlpha(0.8);
}


async function initNativeLabels(viewer, onProgress, refs = {}) {
  const Cesium = window.Cesium;
  const D2R    = Math.PI / 180;

  const FILL   = Cesium.Color.WHITE;
  const HALO   = Cesium.Color.BLACK;
  const FONT_B = '600 16px "Inter","Helvetica Neue",sans-serif';
  const FONT_R = '500 15px "Inter","Helvetica Neue",sans-serif';

  const countryLabels = viewer.scene.primitives.add(new Cesium.LabelCollection());
  const placeLabels   = viewer.scene.primitives.add(new Cesium.LabelCollection());
  const osmLabels     = viewer.scene.primitives.add(new Cesium.LabelCollection());

  // EllipsoidalOccluder — hides country names on the far side of the globe.
  const occluder = new Cesium.EllipsoidalOccluder(Cesium.Ellipsoid.WGS84, new Cesium.Cartesian3());

  // ── Country names (174 items) ────────────────────────────────────────────
  onProgress?.(83, "Loading country labels…");
  neCountriesData.features.forEach(f => {
    const p = f.properties;
    if (!p?.NAME && !p?.name) return;
    if ((p.ISO_A3_EH || p.iso_a3_eh || "") === "-99") return;
    const name = p.NAME_EN || p.name_en || p.NAME || p.name || p.ADMIN || "";
    const lon  = p.LABEL_X ?? p.label_x ?? (f.bbox ? (f.bbox[0] + f.bbox[2]) / 2 : 0);
    const lat  = p.LABEL_Y ?? p.label_y ?? (f.bbox ? (f.bbox[1] + f.bbox[3]) / 2 : 0);
    countryLabels.add({
      position:                 Cesium.Cartesian3.fromDegrees(lon, lat, 0),
      text:                     name.toUpperCase(),
      font:                     FONT_B,
      style:                    Cesium.LabelStyle.FILL_AND_OUTLINE,
      fillColor:                FILL,
      outlineColor:             HALO,
      outlineWidth:             4,
      horizontalOrigin:         Cesium.HorizontalOrigin.CENTER,
      verticalOrigin:           Cesium.VerticalOrigin.CENTER,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(3e6, 20e6),
      disableDepthTestDistance: Infinity,
      show:                     false, // updated by occluder each frame
    });
  });

  // ── Populated places (4 966 items, batched so loader stays responsive) ───
  const neSmallLabels = []; // toggled off when OSM data arrives
  onProgress?.(85, "Loading city labels…");
  const placeFeatures = nePlacesData.features.filter(
    f => (f.properties?.NAME || f.properties?.name) && f.geometry?.coordinates
  );
  const PLACE_BATCH = 1000;
  for (let i = 0; i < placeFeatures.length; i++) {
    if (viewer.isDestroyed()) return () => {};
    const f   = placeFeatures[i];
    const p   = f.properties;
    const [lon, lat] = f.geometry.coordinates;
    const name = p.NAME_EN || p.name_en || p.NAME || p.name || "";
    const pop  = p.POP_MAX || p.pop_max || 0;
    const isCapital = (p.FEATURECLA || p.featurecla || "").toLowerCase().includes("capital");

    let minDist, maxDist, font;
    if (isCapital)       { minDist = 50e3; maxDist = 3e6;   font = FONT_B; }
    else if (pop >= 5e5) { minDist = 50e3; maxDist = 800e3; font = FONT_R; }
    else if (pop >= 5e4) { minDist = 50e3; maxDist = 200e3; font = FONT_R; }
    else                 { minDist = 0;    maxDist = 50e3;  font = FONT_R; }

    const lbl = placeLabels.add({
      position:                 Cesium.Cartesian3.fromDegrees(lon, lat, 0),
      text:                     name,
      font,
      style:                    Cesium.LabelStyle.FILL_AND_OUTLINE,
      fillColor:                FILL,
      outlineColor:             HALO,
      outlineWidth:             4,
      horizontalOrigin:         Cesium.HorizontalOrigin.CENTER,
      verticalOrigin:           Cesium.VerticalOrigin.CENTER,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(minDist, maxDist),
      disableDepthTestDistance: Infinity,
    });

    if (!isCapital && pop < 5e4) neSmallLabels.push(lbl);

    if ((i + 1) % PLACE_BATCH === 0) {
      onProgress?.(85 + Math.round((i + 1) / placeFeatures.length * 3), "Loading city labels…");
      await new Promise(r => setTimeout(r, 0));
    }
  }
  onProgress?.(88, "Preparing map labels…");

  // ── OSM village/hamlet fetch via Overpass (triggered at < 50 km altitude) ──
  const scratchCartographic = new Cesium.Cartographic();
  const scratchRect         = new Cesium.Rectangle();
  let osmFetchBbox  = null;
  let osmPending    = false;
  let osmRetryAfter = 0;
  let osmLastFetch  = 0;       // timestamp of last successful request start
  let osmLoaded     = false;
  const OSM_MIN_INTERVAL = 30_000; // minimum ms between successive Overpass requests
  // Public Overpass mirrors — rotated on failure since each is often busy (429/504)
  const OSM_ENDPOINTS = [
    "https://overpass-api.de/api/interpreter",
    "https://overpass.kumi.systems/api/interpreter",
    "https://overpass.private.coffee/api/interpreter",
  ];
  let osmEndpointIdx = 0;

  const needsOsmFetch = (vr) => {
    if (!vr || vr.west > vr.east) return false;
    if (!osmFetchBbox) return true;
    const s = vr.south / D2R, n = vr.north / D2R;
    const w = vr.west  / D2R, e = vr.east  / D2R;
    return s < osmFetchBbox.s || n > osmFetchBbox.n || w < osmFetchBbox.w || e > osmFetchBbox.e;
  };

  const fetchOsmPlaces = (vr) => {
    if (osmPending || !vr || vr.west > vr.east) return;
    if (Date.now() < osmRetryAfter) return;
    if (Date.now() - osmLastFetch < OSM_MIN_INTERVAL) return;
    osmPending = true;
    const PAD = 0.5;
    const s = Math.max(-90,  (vr.south / D2R) - PAD).toFixed(4);
    const n = Math.min( 90,  (vr.north / D2R) + PAD).toFixed(4);
    const w =               ((vr.west  / D2R) - PAD).toFixed(4);
    const e =               ((vr.east  / D2R) + PAD).toFixed(4);
    osmFetchBbox  = { s: +s, n: +n, w: +w, e: +e };
    osmLastFetch  = Date.now();
    const query = `[out:json][timeout:25];node["place"~"^(city|town|village|hamlet|suburb|municipality|borough)$"](${s},${w},${n},${e});out body;`;
    // Try each mirror in sequence within one cycle — a single busy mirror
    // fails over silently; only when all mirrors fail do we back off and warn.
    const attempt = (triesLeft) =>
      fetch(`${OSM_ENDPOINTS[osmEndpointIdx]}?data=${encodeURIComponent(query)}`)
        .then(r => {
          if (!r.ok) throw new Error(`Overpass HTTP ${r.status} (${new URL(OSM_ENDPOINTS[osmEndpointIdx]).host})`);
          return r.json();
        })
        .catch(err => {
          osmEndpointIdx = (osmEndpointIdx + 1) % OSM_ENDPOINTS.length;
          if (triesLeft > 1) return attempt(triesLeft - 1);
          throw err;
        });

    attempt(OSM_ENDPOINTS.length)
      .then(data => {
        const isLatin = (s) => /^[ -~À-ɏḀ-ỿ\s'-]+$/.test(s);
        osmLabels.removeAll();
        (data.elements || [])
          .filter(el => {
            if (!el.tags?.name) return false;
            const en = el.tags["name:en"];
            return en ? true : isLatin(el.tags.name);
          })
          .forEach(el => {
            osmLabels.add({
              position:                 Cesium.Cartesian3.fromDegrees(el.lon, el.lat, 0),
              text:                     el.tags["name:en"] || el.tags.name,
              font:                     FONT_R,
              style:                    Cesium.LabelStyle.FILL_AND_OUTLINE,
              fillColor:                FILL,
              outlineColor:             HALO,
              outlineWidth:             4,
              horizontalOrigin:         Cesium.HorizontalOrigin.CENTER,
              verticalOrigin:           Cesium.VerticalOrigin.CENTER,
              disableDepthTestDistance: Infinity,
            });
          });
        osmLoaded = true;
        neSmallLabels.forEach(l => { l.show = false; });
      })
      .catch(err => {
        console.warn("OSM places fetch failed on all mirrors:", err);
        osmFetchBbox  = null;   // allow this viewport to be retried
        osmRetryAfter = Date.now() + 60_000;
      })
      .finally(() => { osmPending = false; });
  };

  // Above this altitude labels render on top of data points; below it points win.
  const LABEL_ON_TOP_ALT = 200e3; // 200 km
  let labelsOnTop = null; // null = not yet set

  // Minimal postRender: country occlusion + OSM viewport management + label z-order
  const tick = () => {
    if (viewer.isDestroyed()) return;

    const camPos = viewer.camera.positionWC;
    occluder.cameraPosition = camPos;

    // Occlusion for country labels — hides names on the far side of the globe
    for (let i = 0; i < countryLabels.length; i++) {
      const lbl = countryLabels.get(i);
      lbl.show = occluder.isPointVisible(lbl.position);
    }

    // OSM management
    Cesium.Cartographic.fromCartesian(camPos, Cesium.Ellipsoid.WGS84, scratchCartographic);
    const camAlt = scratchCartographic.height;

    // Pre-fetch at < 50 km so data is ready when the user reaches neighbourhood scale
    if (camAlt < 50e3) {
      const vr = viewer.camera.computeViewRectangle(Cesium.Ellipsoid.WGS84, scratchRect);
      if (needsOsmFetch(vr)) fetchOsmPlaces(vr);
    }

    // Show/hide by altitude only — no DistanceDisplayCondition means no circle artefact.
    // Labels appear/disappear at the same threshold regardless of zoom direction or tilt.
    const osmShouldShow = osmLoaded && camAlt < 30e3;
    if (osmLabels.show !== osmShouldShow) {
      osmLabels.show = osmShouldShow;
      neSmallLabels.forEach(l => { l.show = !osmShouldShow; });
    }

    // Label z-order: above 200 km labels rise above data points; below 200 km points win.
    // scene.primitives render in add-order (last = on top). raiseToTop/lowerToBottom only
    // fires when altitude crosses the threshold, not every frame.
    const shouldBeOnTop = camAlt > LABEL_ON_TOP_ALT;
    if (labelsOnTop !== shouldBeOnTop) {
      labelsOnTop = shouldBeOnTop;
      const prims = viewer.scene.primitives;
      if (shouldBeOnTop) {
        prims.raiseToTop(countryLabels);
        prims.raiseToTop(placeLabels);
        prims.raiseToTop(osmLabels);
      } else {
        prims.lowerToBottom(osmLabels);
        prims.lowerToBottom(placeLabels);
        prims.lowerToBottom(countryLabels);
      }
    }
  };

  const removeListener = viewer.scene.postRender.addEventListener(tick);

  // ── Camera tilt guard + dynamic point size ───────────────────────────────
  viewer.scene.screenSpaceCameraController.enableCollisionDetection = true;

  let lastPointSize = 6;
  const dynPtSize = alt => {
    const lo = 2_000, hi = 800_000, minSz = 6, maxSz = 12;
    const t = (Math.log(Math.max(alt, lo)) - Math.log(lo)) / (Math.log(hi) - Math.log(lo));
    return Math.round(minSz + (maxSz - minSz) * Math.min(Math.max(t, 0), 1));
  };

  const onCameraChange = () => {
    if (viewer.isDestroyed()) return;

    // Prevent camera from tilting past horizontal into the globe
    if (viewer.scene.screenSpaceCameraController.enableTilt && viewer.camera.pitch > -0.02) {
      viewer.camera.setView({
        orientation: { heading: viewer.camera.heading, pitch: -0.02, roll: viewer.camera.roll },
      });
    }

    // Dynamic point size: log-scale 3–12 px across 2 km–800 km altitude
    const alt = viewer.camera.positionCartographic.height;
    const wantSize = dynPtSize(alt);
    if (wantSize !== lastPointSize) {
      lastPointSize = wantSize;
      const { collectionsRef, ghostCollectionsRef } = refs;
      if (collectionsRef) {
        for (const coll of Object.values(collectionsRef.current)) {
          for (let i = 0; i < coll.length; i++) coll.get(i).pixelSize = wantSize;
        }
      }
      if (ghostCollectionsRef) {
        for (const coll of Object.values(ghostCollectionsRef.current)) {
          for (let i = 0; i < coll.length; i++) coll.get(i).pixelSize = wantSize;
        }
      }
    }
  };

  viewer.camera.changed.addEventListener(onCameraChange);

  return () => {
    removeListener();
    viewer.camera.changed.removeEventListener(onCameraChange);
    if (!viewer.isDestroyed()) {
      viewer.scene.primitives.remove(countryLabels);
      viewer.scene.primitives.remove(placeLabels);
      viewer.scene.primitives.remove(osmLabels);
    }
  };
}

// ── Surface modeler helpers ───────────────────────────────────────────────────
function parseEpochDate(key) {
  const m = key.match(/^D_(\d{4})(\d{2})(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(0);
}
function subsampleEpochs(dCols, intervalDays = 180) {
  if (dCols.length <= 2) return [...dCols];
  const result = [dCols[0]];
  let lastMs = parseEpochDate(dCols[0]).getTime();
  for (let i = 1; i < dCols.length - 1; i++) {
    const ms = parseEpochDate(dCols[i]).getTime();
    if (ms - lastMs >= intervalDays * 86400000) { result.push(dCols[i]); lastMs = ms; }
  }
  result.push(dCols[dCols.length - 1]);
  return result;
}
// Loop-based min/max — Math.min(...arr) spreads the array into arguments and
// throws "Maximum call stack size exceeded" beyond ~120k elements, which large
// selection areas easily produce.
function _arrMinMax(arr) {
  let mn = Infinity, mx = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  return [mn, mx];
}
function _idwPredict(px, py, xs, ys, zs, power = 2) {
  let num = 0, den = 0;
  for (let i = 0; i < xs.length; i++) {
    const dx = px - xs[i], dy = py - ys[i];
    const d2 = dx * dx + dy * dy;
    if (d2 === 0) return zs[i];
    const w = 1 / Math.pow(d2, power / 2);
    num += w * zs[i]; den += w;
  }
  return den === 0 ? 0 : num / den;
}
// Local (neighbourhood) IDW — GIS-standard: each query interpolates from its
// K nearest points only, so local structure is honoured and a dense far-away
// cluster cannot smear values across sparse areas. A uniform bucket grid over
// the data makes each query touch only nearby points instead of all N.
// Returns predict(px, py, power).
function _makeLocalIdw(xs, ys, zs, K = 12) {
  const n = xs.length;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    if (xs[i] < minX) minX = xs[i]; if (xs[i] > maxX) maxX = xs[i];
    if (ys[i] < minY) minY = ys[i]; if (ys[i] > maxY) maxY = ys[i];
  }
  // Bucket count chosen so the average bucket holds a handful of points
  const G = Math.max(1, Math.min(64, Math.round(Math.sqrt(n / 3)) || 1));
  const spanX = Math.max(maxX - minX, 1e-9), spanY = Math.max(maxY - minY, 1e-9);
  const bx = x => Math.min(G - 1, Math.max(0, Math.floor((x - minX) / spanX * G)));
  const by = y => Math.min(G - 1, Math.max(0, Math.floor((y - minY) / spanY * G)));
  const buckets = Array.from({ length: G * G }, () => []);
  for (let i = 0; i < n; i++) buckets[by(ys[i]) * G + bx(xs[i])].push(i);

  const k = Math.min(K, n);
  return (px, py, power = 2) => {
    // Expand square rings of buckets around the query until enough candidates
    // are gathered, plus one extra ring so a nearer point sitting just across
    // a bucket boundary is not missed.
    const cbx = bx(px), cby = by(py);
    const cand = [];
    let extra = -1;
    for (let ring = 0; ring < G; ring++) {
      const x0 = cbx - ring, x1 = cbx + ring, y0 = cby - ring, y1 = cby + ring;
      for (let yy = Math.max(0, y0); yy <= Math.min(G - 1, y1); yy++) {
        for (let xx = Math.max(0, x0); xx <= Math.min(G - 1, x1); xx++) {
          if (ring > 0 && xx > x0 && xx < x1 && yy > y0 && yy < y1) continue; // interior rings already visited
          const b = buckets[yy * G + xx];
          for (let j = 0; j < b.length; j++) cand.push(b[j]);
        }
      }
      if (cand.length >= k) { extra++; if (extra >= 1) break; }
    }
    // k nearest of the candidate set (small), then IDW over just those
    const byDist = cand.map(i => {
      const dx = px - xs[i], dy = py - ys[i];
      return [dx * dx + dy * dy, i];
    }).sort((a, b) => a[0] - b[0]);
    let num = 0, den = 0;
    const kk = Math.min(k, byDist.length);
    for (let j = 0; j < kk; j++) {
      const [d2, i] = byDist[j];
      if (d2 === 0) return zs[i];
      const w = 1 / Math.pow(d2, power / 2);
      num += w * zs[i]; den += w;
    }
    return den === 0 ? 0 : num / den;
  };
}
function buildDeformationCells(Cesium, points, minLat, maxLat, minLon, maxLon, hexStops, res = 48, power = 2, opacity = 0.82) {
  if (!points.length) return null;
  const midLat  = (minLat + maxLat) / 2;
  const mPerLon = 111320 * Math.cos(midLat * Math.PI / 180);
  const mPerLat = 110540;
  const lon0 = minLon, lat0 = minLat;
  const xs = points.map(p => (p.lon - lon0) * mPerLon);
  const ys = points.map(p => (p.lat - lat0) * mPerLat);
  const zs = points.map(p => p.v);
  const [zMin, zMax] = _arrMinMax(zs);
  const range = Math.max(Math.abs(zMin), Math.abs(zMax), 0.001);
  const stops = hexStops.map(h => {
    const n = parseInt(h.slice(1), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  });
  const dLon = maxLon - minLon, dLat = maxLat - minLat;
  const instances = [];
  for (let row = 0; row < res; row++) {
    for (let col = 0; col < res; col++) {
      const latMin = maxLat - ((row + 1) / res) * dLat;
      const latMax = maxLat - (row / res) * dLat;
      const lonMin = minLon + (col / res) * dLon;
      const lonMax = minLon + ((col + 1) / res) * dLon;
      const px = ((lonMin + lonMax) / 2 - lon0) * mPerLon;
      const py = ((latMin + latMax) / 2 - lat0) * mPerLat;
      const v  = _idwPredict(px, py, xs, ys, zs, power);
      const t   = Math.max(-1, Math.min(1, v / range));
      const pos = (t + 1) / 2 * (stops.length - 1);
      const si  = Math.min(Math.floor(pos), stops.length - 2);
      const f   = pos - si;
      const a   = stops[si], b = stops[si + 1];
      instances.push(new Cesium.GeometryInstance({
        geometry: new Cesium.RectangleGeometry({
          rectangle: Cesium.Rectangle.fromDegrees(lonMin, latMin, lonMax, latMax),
          vertexFormat: Cesium.PerInstanceColorAppearance.VERTEX_FORMAT,
        }),
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(new Cesium.Color(
            a[0] + (b[0] - a[0]) * f,
            a[1] + (b[1] - a[1]) * f,
            a[2] + (b[2] - a[2]) * f,
            opacity
          )),
        },
      }));
    }
  }
  return new Cesium.GroundPrimitive({
    geometryInstances: instances,
    appearance: new Cesium.PerInstanceColorAppearance({ translucent: true }),
    classificationType: Cesium.ClassificationType.BOTH,
    asynchronous: false,
  });
}
// Smooth per-pixel IDW surface. Same 48×48 IDW field as buildDeformationCells
// (identical compute cost), but instead of flat-coloured tiles the VALUES are
// bilinearly interpolated to a 256×256 raster, mapped through the colour ramp
// per pixel, and draped as ONE image-material GroundPrimitive — GPU texture
// filtering then yields fully continuous gradients with no tile edges.
// Values (not colours) are interpolated so the ramp midpoints are honoured:
// a red→green transition passes through the ramp's centre colours instead of
// blending straight across colour space.
// Shared setup for the smooth surface builders: planar point coords, value
// range, and parsed colour stops.
function _surfaceSetup(points, minLat, maxLat, minLon, maxLon, hexStops) {
  const midLat  = (minLat + maxLat) / 2;
  const mPerLon = 111320 * Math.cos(midLat * Math.PI / 180);
  const mPerLat = 110540;
  const xs = points.map(p => (p.lon - minLon) * mPerLon);
  const ys = points.map(p => (p.lat - minLat) * mPerLat);
  const zs = points.map(p => p.v);
  const [zMin, zMax] = _arrMinMax(zs);
  const range = Math.max(Math.abs(zMin), Math.abs(zMax), 0.001);
  const stops = hexStops.map(h => {
    const n = parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  });
  return { mPerLon, mPerLat, xs, ys, zs, range, stops };
}

function buildDeformationSurface(Cesium, points, minLat, maxLat, minLon, maxLon, hexStops, res = 48, power = 2, opacity = 0.82) {
  if (!points.length) return null;
  const { mPerLon, mPerLat, xs, ys, zs, range, stops } = _surfaceSetup(points, minLat, maxLat, minLon, maxLon, hexStops);
  const dLon = maxLon - minLon, dLat = maxLat - minLat;

  // IDW value grid at cell centres — row 0 is the north edge.
  // Local (12-nearest) IDW via a bucket index: honours local structure,
  // avoids regional smearing, and stays fast at fine grid resolutions.
  const predict = _makeLocalIdw(xs, ys, zs, 12);
  const vals = new Float64Array(res * res);
  for (let row = 0; row < res; row++) {
    for (let col = 0; col < res; col++) {
      const px = ((col + 0.5) / res * dLon) * mPerLon;
      const py = (maxLat - (row + 0.5) / res * dLat - minLat) * mPerLat;
      vals[row * res + col] = predict(px, py, power);
    }
  }
  return _surfacePrimFromVals(Cesium, vals, res, range, stops, minLat, maxLat, minLon, maxLon, opacity);
}

// Chunked variant of buildDeformationSurface: yields to the browser between
// row batches so a progress indicator can render and the page stays
// responsive during large builds. onProgress receives 0..1; a true
// shouldAbort() cancels the build (resolves null).
async function buildDeformationSurfaceAsync(Cesium, points, minLat, maxLat, minLon, maxLon, hexStops, res, power, opacity, onProgress, shouldAbort) {
  if (!points.length) return null;
  const { mPerLon, mPerLat, xs, ys, zs, range, stops } = _surfaceSetup(points, minLat, maxLat, minLon, maxLon, hexStops);
  const dLon = maxLon - minLon, dLat = maxLat - minLat;
  const predict = _makeLocalIdw(xs, ys, zs, 12);
  const vals = new Float64Array(res * res);
  const CHUNK = 8; // rows per yield
  for (let row = 0; row < res; row++) {
    for (let col = 0; col < res; col++) {
      const px = ((col + 0.5) / res * dLon) * mPerLon;
      const py = (maxLat - (row + 0.5) / res * dLat - minLat) * mPerLat;
      vals[row * res + col] = predict(px, py, power);
    }
    if ((row + 1) % CHUNK === 0 && row + 1 < res) {
      onProgress?.((row + 1) / res);
      await new Promise(r => setTimeout(r, 0));
      if (shouldAbort?.()) return null;
    }
  }
  onProgress?.(1);
  return _surfacePrimFromVals(Cesium, vals, res, range, stops, minLat, maxLat, minLon, maxLon, opacity);
}

// Texture raster + draped primitive from a computed value grid.
function _surfacePrimFromVals(Cesium, vals, res, range, stops, minLat, maxLat, minLon, maxLon, opacity) {
  // Bilinear value interpolation to the texture raster + per-pixel colour
  const TEX = 256;
  const canvas = document.createElement("canvas");
  canvas.width = TEX; canvas.height = TEX;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(TEX, TEX);
  const alpha = Math.round(opacity * 255);
  for (let y = 0; y < TEX; y++) {
    // Map pixel centre onto the cell-centre grid (clamped to the edges)
    const gy = Math.min(Math.max((y + 0.5) / TEX * res - 0.5, 0), res - 1);
    const y0 = Math.floor(gy), y1 = Math.min(y0 + 1, res - 1), fy = gy - y0;
    for (let x = 0; x < TEX; x++) {
      const gx = Math.min(Math.max((x + 0.5) / TEX * res - 0.5, 0), res - 1);
      const x0 = Math.floor(gx), x1 = Math.min(x0 + 1, res - 1), fx = gx - x0;
      const v =
        vals[y0 * res + x0] * (1 - fx) * (1 - fy) +
        vals[y0 * res + x1] * fx       * (1 - fy) +
        vals[y1 * res + x0] * (1 - fx) * fy +
        vals[y1 * res + x1] * fx       * fy;
      const t   = Math.max(-1, Math.min(1, v / range));
      const pos = (t + 1) / 2 * (stops.length - 1);
      const si  = Math.min(Math.floor(pos), stops.length - 2);
      const f   = pos - si;
      const a   = stops[si], b = stops[si + 1];
      const o   = (y * TEX + x) * 4;
      img.data[o]     = a[0] + (b[0] - a[0]) * f;
      img.data[o + 1] = a[1] + (b[1] - a[1]) * f;
      img.data[o + 2] = a[2] + (b[2] - a[2]) * f;
      img.data[o + 3] = alpha;
    }
  }
  ctx.putImageData(img, 0, 0);

  // 3. One draped rectangle with the raster as its material
  return new Cesium.GroundPrimitive({
    geometryInstances: new Cesium.GeometryInstance({
      geometry: new Cesium.RectangleGeometry({
        rectangle: Cesium.Rectangle.fromDegrees(minLon, minLat, maxLon, maxLat),
      }),
    }),
    appearance: new Cesium.MaterialAppearance({
      material: Cesium.Material.fromType("Image", { image: canvas }),
      translucent: true,
    }),
    classificationType: Cesium.ClassificationType.BOTH,
    asynchronous: false,
  });
}

// ── Annotation layer display-name cache (localStorage) ───────────────────────
// The R2 listing API derives names from filenames ("AOI_-_London"), not from the
// parquet __meta__ row ("AOI - London"). Cache display names locally so the
// correct name appears in the list before the layer is activated.
const ANNO_NAMES_KEY = "gw_anno_names";
function cacheAnnoName(key, name) {
  if (!key || !name) return;
  try {
    const s = JSON.parse(localStorage.getItem(ANNO_NAMES_KEY) || "{}");
    s[key] = name;
    localStorage.setItem(ANNO_NAMES_KEY, JSON.stringify(s));
  } catch (_) {}
}
function evictAnnoName(key) {
  if (!key) return;
  try {
    const s = JSON.parse(localStorage.getItem(ANNO_NAMES_KEY) || "{}");
    delete s[key];
    localStorage.setItem(ANNO_NAMES_KEY, JSON.stringify(s));
  } catch (_) {}
}
function readAnnoNameCache() {
  try { return JSON.parse(localStorage.getItem(ANNO_NAMES_KEY) || "{}"); }
  catch (_) { return {}; }
}

export default function InSpektViewer({ onBack, userId, onLogout, isAdmin, onSwitchToAdmin, onOpenAccount, authToken, isDemoUser }) {
  const containerRef      = useRef(null);
  const viewerRef         = useRef(null);
  const collectionsRef          = useRef({});  // key → PointPrimitiveCollection (depth-tested, full opacity)
  const ghostCollectionsRef     = useRef({});  // key → PointPrimitiveCollection (no depth test, faded)
  const clientGeoJsonSourcesRef = useRef({});  // key → Cesium.GeoJsonDataSource (polygon/line client layers)
  const clientLayerPropsRef     = useRef({});  // key → Map<"lat_lon" → {prop: val}> for point client layers
  const clientPointLayerKeysRef = useRef(new Set()); // keys that are client point layers
  const clientLayerClickRef     = useRef(null);      // Cesium event handler for client-layer feature clicks
  const layerSchemasRef   = useRef({});  // key → { latCol, lonCol, heightCol } — cached per layer
  const identifyHandlerRef        = useRef(null);  // Cesium click handler for identify mode
  const identifyLabelEntitiesRef  = useRef([]);     // Cesium label entities (P1, P2, …)
  const pointCounterRef           = useRef(1);
  const multiSelectRef            = useRef(false);  // mirror of multiSelect for handler closure

  // Profile Viewer tool
  const sectionClickHandlerRef    = useRef(null);
  const sectionMoveHandlerRef     = useRef(null);
  const sectionEntitiesRef        = useRef([]);     // all Cesium entities for section tool
  const sectionStepRef            = useRef(0);      // 0=waiting start, 1=waiting end, 2=done
  const sectionStartRef           = useRef(null);   // { lat, lon }
  const sectionStartPosRef        = useRef(null);   // Cartesian3 exact pick position of Start
  const sectionPreviewPosRef      = useRef([]);     // positions for live preview line
  const sectionSamplesRef         = useRef([]);     // last computed samples for hover detection
  const sectionRawCacheRef        = useRef({});     // { [layerKey]: { allDCols, pts:[...raw rows] } }
  const sectionHoverEntityRef     = useRef(null);   // white dot that follows hover on globe
  const sectionHoveredIndexRef    = useRef(null);   // avoids setState on every mouse pixel
  const layerStatesRef            = useRef({});     // latest layerStates for async handlers

  const { getToken } = useAuth();
  const { user }     = useUser();
  const authTokenRef = useRef(authToken); // stable ref so async callbacks use latest value
  authTokenRef.current = authToken;
  const { ready: dbReady, error: dbError, dbRef } = useDuckDB();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  const [cesiumLoaded,      setCesiumLoaded]      = useState(false);
  const [loadProgress,      setLoadProgress]      = useState(5);
  const [loadStep,          setLoadStep]          = useState("Initializing InSpekt…");
  const [appReady,          setAppReady]          = useState(false);

  // Loading gate: app opens only after tiles, projects AND labels are all ready
  const tilesReadyRef    = useRef(false);
  const projectsReadyRef = useRef(false);
  const labelsReadyRef   = useRef(false);
  const maybeDoneRef     = useRef(null);
  maybeDoneRef.current   = () => {
    if (tilesReadyRef.current && projectsReadyRef.current && labelsReadyRef.current) {
      setLoadProgress(100);
      setLoadStep("Ready to explore");
      setTimeout(() => setAppReady(true), 300);
    }
  };

  const [isSidebarOpen,     setIsSidebarOpen]     = useState(true);
  const [selectedProject,   setSelectedProject]   = useState(null);
  const [projectCount,      setProjectCount]      = useState(null);
  const [coords,            setCoords]            = useState({ lat: null, lng: null });
  const [lineTooltip,       setLineTooltip]       = useState(null); // {x,y,text} | null
  const [is3DView, setIs3DView] = useState(true); // false = tilt locked top-down, true = free tilt
  const is3DViewRef = useRef(true);
  const [showAttribution,   setShowAttribution]   = useState(false);
  const [hoveredLayer,      setHoveredLayer]      = useState(null);
  const [legendStyleKey,    setLegendStyleKey]    = useState(null);  // key of layer whose panel is open
  const [layerFilters,      setLayerFilters]      = useState({});    // { [key]: { lo: 0..1, hi: 0..1 } }
  const filterRafRef = useRef({});  // { [key]: rafId } — one pending RAF per layer during drag
  const [layerInfoOpen,     setLayerInfoOpen]     = useState({});    // { [key]: bool } — info panel expanded
  const [onboardPhase, setOnboardPhase] = useState(
    () => localStorage.getItem("inspekt_onboarded") ? "done" : "modal"
  );
  const [clientLayers,      setClientLayers]      = useState([]);   // user-uploaded layers for current project
  const [clientUsage,       setClientUsage]        = useState({ used: 0, limit: 262144000 });
  const [showClientUpload,  setShowClientUpload]  = useState(false);
  const [clientFeatureCard, setClientFeatureCard] = useState(null); // { name, props } shown on click
  const [editingClientId,   setEditingClientId]   = useState(null); // layer id being renamed
  const [showSearch,        setShowSearch]        = useState(false);
  const [searchQuery,       setSearchQuery]       = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const searchDebounceRef  = useRef(null);
  const searchEntityRef    = useRef([]);   // Cesium entities for current search result (marker or boundary)

  // Per-layer state: { [key]: { status: "idle"|"loading"|"loaded"|"error", count: 0 } }
  const [layerStates, setLayerStates] = useState({});

  // Point Inspector tool
  const [isIdentifyMode, setIsIdentifyMode] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [multiSelect,    setMultiSelect]    = useState(false);

  // Profile Viewer tool
  const [isSectionMode,       setIsSectionMode]       = useState(false);
  const [sectionProfileData,  setSectionProfileData]  = useState([]);
  const [sectionHoveredIndex, setSectionHoveredIndex] = useState(null);
  const [sectionEpochs,       setSectionEpochs]       = useState([]);
  const [sectionSelectedEpoch,setSectionSelectedEpoch]= useState(null);
  const [sectionEpochsLoading,setSectionEpochsLoading]= useState(false);

  // Zone Modeler tool
  const polygonClickHandlerRef    = useRef(null);
  const polygonMoveHandlerRef     = useRef(null);
  const polygonEntitiesRef        = useRef([]);   // Cesium entities: outline + fill
  const polygonStepRef            = useRef(0);    // 0=waiting start, 1=waiting end, 2=done
  const polygonStartRef           = useRef(null); // { lat, lon } of first click
  const polygonBboxRef            = useRef(null); // { minLat, maxLat, minLon, maxLon } of drawn rect
  const polygonCornerEntitiesRef  = useRef([]);   // { entity, lon, lat } for the 4 corner labels
  const polygonFillEntityRef      = useRef(null); // fill entity for the drawn polygon
  const surfaceOverlayPrimRef     = useRef(null); // GroundPrimitive draping IDW grid on globe
  const surfaceDataRef            = useRef([]);   // stable ref to surfaceData for async callbacks
  const surfaceIdwPowerRef        = useRef(2);    // stable ref to IDW power
  const surfaceOpacityRef         = useRef(0.82); // stable ref to surface opacity
  const surfaceDiffRef            = useRef({ a: null, b: null }); // stable ref to diff epoch pair

  const [surfaceAxisMode, setSurfaceAxisMode] = useState("distance");
  const surfaceAxisModeRef = useRef("distance"); // stable ref for use inside effects
  const surfaceRawCacheRef     = useRef({});
  const layerRawDataRef        = useRef({});   // { [layerKey]: { rows, latCol, lonCol } }
  const [isPolygonMode,        setIsPolygonMode]        = useState(false);

  // Area & Perimeter tool
  const [isAreaMode,        setIsAreaMode]        = useState(false);
  const areaClickHandlerRef = useRef(null);
  const areaMoveHandlerRef  = useRef(null);
  const areaVerticesRef     = useRef([]);   // [{lat, lon}]
  const areaEntitiesRef     = useRef([]);   // Cesium entities
  const [areaResult,        setAreaResult]         = useState(null);

  // Distance tool
  const [isDistanceMode,    setIsDistanceMode]    = useState(false);
  const distanceClickHandlerRef = useRef(null);
  const distanceMoveHandlerRef  = useRef(null);
  const distanceEntitiesRef     = useRef([]);
  const distanceP1Ref           = useRef(null);   // {lat, lon} | null
  const [distanceStep,      setDistanceStep]      = useState(0); // 0=idle, 1=awaiting B
  const [distanceResult,    setDistanceResult]    = useState(null); // {dist, p1, p2}

  const [surfaceData,          setSurfaceData]          = useState([]);
  const [surfaceLabel,         setSurfaceLabel]         = useState("Deformation");
  const [surfaceEpochs,        setSurfaceEpochs]        = useState([]);
  const [surfaceSelectedEpoch, setSurfaceSelectedEpoch] = useState(null);
  const [surfaceEpochsLoading, setSurfaceEpochsLoading] = useState(false);
  const [surfaceIdwPower,      setSurfaceIdwPower]      = useState(2);
  const [surfaceBuildProgress, setSurfaceBuildProgress] = useState(null); // { x, y, pct } while a large surface builds
  const [surfaceOpacity,       setSurfaceOpacity]       = useState(0.82);
  const [surfacePointsOpacity, setSurfacePointsOpacity] = useState(1.0);
  const [surfaceDiffEpochA,    setSurfaceDiffEpochA]    = useState(null);
  const [surfaceDiffEpochB,    setSurfaceDiffEpochB]    = useState(null);

  // ── Draw tool ──────────────────────────────────────────────────────────────
  const [drawTool,          setDrawTool]          = useState(null);    // 'line'|'polygon'|null
  const [isDrawGroupOpen,   setIsDrawGroupOpen]   = useState(false);
  const [drawFeatures,      setDrawFeatures]      = useState([]);      // saved features in current session
  const [showSaveDrawing,   setShowSaveDrawing]   = useState(false);
  const [saveDrawingName,   setSaveDrawingName]   = useState("");
  const [saveDrawingTarget, setSaveDrawingTarget] = useState("new"); // "new" | existing layer key
  const [savingDrawing,     setSavingDrawing]     = useState(false);
  const [drawEditLabel,     setDrawEditLabel]     = useState(null);    // { id } of feature being labelled
  const drawVerticesRef     = useRef([]);   // in-progress vertices [{lon,lat}]
  const drawEntitiesRef     = useRef([]);   // all rendered Cesium entities for drawn features
  const drawPreviewRef      = useRef(null); // { line, fill } preview entities during drawing
  const drawClickHandlerRef = useRef(null);
  const drawMoveHandlerRef  = useRef(null);
  const drawPreviewPosRef   = useRef(null); // {lon,lat} cursor position
  const drawToolRef         = useRef(null); // stable ref for use inside handlers
  const [drawColor,         setDrawColor]         = useState("#3b82f6");
  const drawColorRef           = useRef("#3b82f6");
  const drawInProgressDotsRef  = useRef([]);  // vertex dots during active drawing
  const vertexDotsMapRef       = useRef({}); // {featureId: [entities]}
  const vertexDotRegistryRef   = useRef({}); // {entityId: {featureId, vertexIdx, source}}
  const vertexDragHandlerRef   = useRef(null);
  const activeVertexDragRef    = useRef(null);
  const drawFeaturesRef        = useRef([]);
  const annoEditKeyRef         = useRef(null);
  const featureVerticesRefMap  = useRef({});  // featureId → mutable vertices array for CallbackProperty live drag
  const featureEntityMapRef    = useRef({});  // featureId → [entities] for in-place material updates (no re-render)
  const toolPolylineEntitiesRef = useRef(new WeakMap()); // entity → length label, for hover tooltip
  const autoSaveTimerRef       = useRef(null);
  const areaIsClosedRef        = useRef(false);
  const isAreaModeRef          = useRef(false);
  const annoEditLayerNameRef   = useRef("");

  // ── Annotation layers (per-project, loaded from R2) ───────────────────────
  const [annoLayers,        setAnnoLayers]        = useState([]);      // [{key,name,lastModified}]
  const [annoLayerStates,   setAnnoLayerStates]   = useState({});      // {key: 'idle'|'loading'|'loaded'|'error'}
  const annoEntitiesRef     = useRef({});   // {key: [Cesium entities]}
  const annoFeaturesRef     = useRef({});   // {key: [{id,type,vertices,label,color}]}
  const [annoEditKey,       setAnnoEditKey]       = useState(null);
  const [annoEditFeatures,  setAnnoEditFeatures]  = useState([]);
  const [annoEditLayerName, setAnnoEditLayerName] = useState("");
  const [savingAnnoEdit,    setSavingAnnoEdit]    = useState(false);
  const [hoveredAnnoBtn,    setHoveredAnnoBtn]    = useState(null); // {key, btn: 'edit'|'delete'}

  useEffect(() => {
    is3DViewRef.current = is3DView;
  }, [is3DView]);

  // ── Advance loader when DuckDB becomes ready ──────────────────────────────
  useEffect(() => {
    if (dbReady) {
      setLoadProgress(prev => Math.max(prev, 75));
      setLoadStep("Fetching your projects…");
    }
  }, [dbReady]);

  // ── Load CesiumJS from local /cesium/ once ────────────────────────────────
  useEffect(() => {
    const link  = document.createElement("link");
    link.rel    = "stylesheet";
    link.href   = `${CESIUM_BASE}/Widgets/widgets.css`;
    document.head.appendChild(link);

    const existing = document.querySelector("script[data-cesium]");

    const init = async () => {
      const Cesium = window.Cesium;
      if (!Cesium || !containerRef.current) return;

      setLoadProgress(prev => Math.max(prev, 30));
      setLoadStep("Loading 3D environment…");

      Cesium.Ion.defaultAccessToken = CESIUM_ION_TOKEN;

      // ── Terrain provider (height sampling only — not rendered, globe is hidden) ──
      // Guard: if api.cesium.com is unreachable (DNS failure, offline, etc.) fall back
      // to flat-ellipsoid terrain so the rest of init() can still complete.
      let terrain3DProvider;
      try {
        terrain3DProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1, {
          requestVertexNormals: true,
          requestWaterMask:     true,
        });
      } catch (e) {
        console.warn("Cesium Ion terrain unavailable, falling back to ellipsoid:", e);
        terrain3DProvider = new Cesium.EllipsoidTerrainProvider();
      }

      const viewer = new Cesium.Viewer(containerRef.current, {
        baseLayer:            false,
        sceneMode:            Cesium.SceneMode.SCENE3D,
        scene3DOnly:          true,
        baseLayerPicker:      false,
        navigationHelpButton: false,
        animation:            false,
        timeline:             false,
        fullscreenButton:     false,
        geocoder:             false,
        homeButton:           false,
        sceneModePicker:      false,
        selectionIndicator:   false,
        infoBox:              false,
      });

      // ── Google Photorealistic 3D Tiles ────────────────────────────────────────
      // Replaces globe entirely — provides terrain, buildings, and imagery in one mesh.
      // Globe is hidden; terrain provider is kept only for geoid offset sampling.
      viewer.scene.globe.show                     = false;
      viewer.scene.highDynamicRange               = false;
      viewer.scene.logarithmicDepthBuffer         = false;

      // ── Rendering resolution ─────────────────────────────────────────────────
      // Render at full device pixel ratio so tiles and labels are sharp on every
      // display — Retina (dpr=2) gets native 2× pixels, standard DPI stays 1×.
      viewer.resolutionScale = 1.0;
      // MSAA 2× instead of default 4× — halves rasterisation cost, still anti-aliased.
      viewer.scene.msaaSamples = 2;
      // FXAA is redundant when MSAA is active and adds a post-process pass.
      viewer.scene.postProcessStages.fxaa.enabled = false;
      // requestRenderMode intentionally NOT set — postRender canvas labels and entity
      // billboards require continuous rendering to stay in sync with async tool updates.
      viewer.scene.screenSpaceCameraController.enableTilt = true;
      // Right-drag → tilt/orbit; scroll wheel → zoom; left-drag → pan/rotate
      viewer.scene.screenSpaceCameraController.tiltEventTypes = [
        Cesium.CameraEventType.RIGHT_DRAG,
        Cesium.CameraEventType.MIDDLE_DRAG,
        Cesium.CameraEventType.PINCH,
        { eventType: Cesium.CameraEventType.LEFT_DRAG, modifier: Cesium.KeyboardEventModifier.CTRL },
      ];
      viewer.scene.screenSpaceCameraController.zoomEventTypes = [
        Cesium.CameraEventType.WHEEL,
        Cesium.CameraEventType.PINCH,
      ];
      // Clamp camera pitch so it can never look at or above the horizon.
      // pitch=0 is horizontal; values > -0.04 (~-2°) mean nearly-horizontal or sky-facing.
      // This fires every frame but is a single comparison so the cost is negligible.
      viewer.scene.preRender.addEventListener(() => {
        if (viewer.camera.pitch > -0.04) {
          viewer.camera.setView({
            orientation: {
              heading: viewer.camera.heading,
              pitch:   -0.04,
              roll:    viewer.camera.roll,
            },
          });
        }
      });
      // Google tile CDN uses HTTP/2 — raise concurrent request limit beyond the
      // default 18 so the tile cache fills faster when navigating.
      Cesium.RequestScheduler.maximumRequestsPerServer = 64;

      try {
        const googleTileset = await Cesium.Cesium3DTileset.fromIonAssetId(GOOGLE_TILES_ION_ASSET_ID);
        googleTileset.preloadAncestors          = true;
        googleTileset.preloadFlightDestinations = true;
        googleTileset.maximumScreenSpaceError   = 16;
        googleTileset.maximumMemoryUsage        = 1024;
        viewer.scene.primitives.add(googleTileset);
        viewerRef._googleTileset = googleTileset;
        // Signal loading gate: tiles ready once the initial visible set has loaded
        googleTileset.initialTilesLoaded.addEventListener(() => {
          tilesReadyRef.current = true;
          maybeDoneRef.current?.();
        });
      } catch (e) {
        console.warn("Google Photorealistic 3D Tiles unavailable:", e);
        // Tileset failed — don't block the app forever
        tilesReadyRef.current = true;
        maybeDoneRef.current?.();
      }

      // Initial camera — top-down view centered on India
      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(78.0, 22.0, 25000000),
        orientation: { heading: 0.0, pitch: -Math.PI / 2, roll: 0.0 },
      });

      setLoadProgress(prev => Math.max(prev, 55));
      setLoadStep("Preparing visualization…");

      applySceneAtmosphere(viewer);

      // ── Custom star field ─────────────────────────────────────────────────────
      // Placed on a sphere at 1e12m — visible as fixed background dots behind globe.
      // Cesium's own skyBox is off (shows black), so we replicate with point prims.
      const starCollection = new Cesium.PointPrimitiveCollection();
      const STAR_COUNT = 800;
      for (let i = 0; i < STAR_COUNT; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi   = Math.acos(2 * Math.random() - 1);
        const r     = 1e12;
        const b     = 0.4 + Math.random() * 0.6; // vary brightness 40–100%
        starCollection.add({
          position: new Cesium.Cartesian3(
            r * Math.sin(phi) * Math.cos(theta),
            r * Math.sin(phi) * Math.sin(theta),
            r * Math.cos(phi)
          ),
          color:     new Cesium.Color(b, b, b, 1.0),
          pixelSize: Math.random() < 0.15 ? 2.0 : 1.0, // ~15% slightly bigger
        });
      }
      viewer.scene.primitives.add(starCollection);

      viewerRef.current = viewer;

      // Native Cesium LabelCollections — initialised during loading screen so labels
      // are GPU-ready before the globe appears. Progress updates feed into the loader.
      viewerRef._cleanupCanvasLabels = await initNativeLabels(viewer, (p, step) => {
        setLoadProgress(prev => Math.max(prev, p));
        setLoadStep(step);
      }, { collectionsRef, ghostCollectionsRef });
      labelsReadyRef.current = true;
      maybeDoneRef.current?.();

      // Expose terrain provider for geoid offset sampling in apply3DModeToLoadedLayers
      viewerRef._terrain3DProvider = terrain3DProvider;

      // Ghost visibility — only meaningful at building/street scale (< 3 km) where
      // terrain occlusion is perceptible and the in-viewport point count is small.
      let ghostsShown = null;
      const removeGhostAltListener = viewer.scene.postRender.addEventListener(() => {
        if (viewer.isDestroyed()) return;
        const h = viewer.camera.positionCartographic.height;
        const shouldShow = h < 3e3;
        if (ghostsShown !== shouldShow) {
          ghostsShown = shouldShow;
          Object.values(ghostCollectionsRef.current).forEach(g => { g.show = shouldShow; });
        }
      });
      viewerRef._removeGhostAltListener = removeGhostAltListener;

      // Track mouse coordinates — use pickPosition so coords work over 3D tile mesh
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction(movement => {
        const cartesian = viewer.scene.pickPosition(movement.endPosition);
        if (cartesian) {
          const carto = Cesium.Cartographic.fromCartesian(cartesian);
          setCoords({
            lat: Cesium.Math.toDegrees(carto.latitude).toFixed(4),
            lng: Cesium.Math.toDegrees(carto.longitude).toFixed(4),
          });
        }

      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      setCesiumLoaded(true);
    };

    const safeInit = () => init().catch(e => console.error("Cesium init failed:", e));

    if (existing) {
      if (window.Cesium) safeInit();
      else existing.addEventListener("load", safeInit);
    } else {
      const script = document.createElement("script");
      script.src   = `${CESIUM_BASE}/Cesium.js`;
      script.setAttribute("data-cesium", "1");
      script.onload = safeInit;
      document.head.appendChild(script);
    }

    return () => {
      document.head.removeChild(link);
      viewerRef._cleanupCanvasLabels?.();
      viewerRef._removeGhostAltListener?.();
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        Object.values(collectionsRef.current).forEach(col => viewerRef.current.scene.primitives.remove(col));
        Object.values(ghostCollectionsRef.current).forEach(col => viewerRef.current.scene.primitives.remove(col));
      }
      collectionsRef.current = {};
      ghostCollectionsRef.current = {};
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Basemap switchers ─────────────────────────────────────────────────────
  // Samples Cesium terrain at up to 20 evenly-spaced points from the dataset to
  // compute the average offset between data heights and Cesium ellipsoidal heights.
  // InSAR heights are typically above the geoid (MSL); Cesium uses WGS84 ellipsoid.
  // The geoid undulation is ~30–60 m in Europe — large enough to hide all points.
  const estimateHeightOffset = async (terrainProvider, layerData) => {
    const Cesium = window.Cesium;
    const { count, lats, lons, heights } = layerData;
    if (!terrainProvider || !heights) return 0;
    const step = Math.max(1, Math.floor(count / 20));
    const cartos = [], dataHs = [];
    for (let i = 0; i < count; i += step) {
      const h = heights[i];
      if (!Number.isFinite(h)) continue;
      cartos.push(Cesium.Cartographic.fromDegrees(lons[i], lats[i]));
      dataHs.push(h);
      if (cartos.length >= 20) break;
    }
    if (cartos.length === 0) return 0;
    try {
      await Cesium.sampleTerrainMostDetailed(terrainProvider, cartos);
      const diffs = cartos.map((c, i) => (c.height || 0) - dataHs[i]).filter(Number.isFinite);
      if (diffs.length === 0) return 0;
      // Top-25% of diffs: ground-level scatterers have the largest terrain–data difference
      // and give the true geoid undulation; rooftop scatterers would underestimate it.
      const sorted = [...diffs].sort((a, b) => b - a);
      const topN = Math.max(1, Math.ceil(sorted.length * 0.25));
      return sorted.slice(0, topN).reduce((a, b) => a + b) / topN;
    } catch (_) { return 0; }
  };


  const apply3DModeToLoadedLayers = async () => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer) return;

    for (const key of Object.keys(ghostCollectionsRef.current)) {
      const g = ghostCollectionsRef.current[key];
      if (g && !viewer.isDestroyed()) viewer.scene.primitives.remove(g);
    }
    ghostCollectionsRef.current = {};

    for (const key of Object.keys(collectionsRef.current)) {
      const collection = collectionsRef.current[key];
      const cached = layerRawDataRef.current[key];
      if (!collection || !cached) continue;
      const { lats, lons, heights } = cached;

      const rawOffset = cached.cachedOffset ?? await estimateHeightOffset(
        viewerRef._terrain3DProvider, cached
      );
      const offset = 2 + rawOffset;

      collection.show = false;

      const ghostCollection = new Cesium.PointPrimitiveCollection({
        blendOption: Cesium.BlendOption.TRANSLUCENT,
        show: false,
      });
      viewer.scene.primitives.add(ghostCollection);
      ghostCollectionsRef.current[key] = ghostCollection;

      const REPOSITION_BATCH = 5000;
      for (let i = 0; i < collection.length; i++) {
        const primitive = collection.get(i);
        if (!primitive) continue;
        const h = heights ? heights[i] : NaN;
        const hasH = Number.isFinite(h);
        const pos = Cesium.Cartesian3.fromDegrees(lons[i], lats[i], hasH ? h + offset : 0);
        const hr  = hasH ? Cesium.HeightReference.NONE : Cesium.HeightReference.CLAMP_TO_GROUND;

        primitive.position = pos;
        primitive.heightReference = hr;
        primitive.disableDepthTestDistance = Number.POSITIVE_INFINITY;

        ghostCollection.add({
          position:                 pos,
          color:                    primitive.color.withAlpha(0.25),
          pixelSize:                6,
          heightReference:          hr,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        });

        if ((i + 1) % REPOSITION_BATCH === 0) await new Promise(r => setTimeout(r, 0));
      }

      collection.show = true;
      const altNow = viewer.camera.positionCartographic.height;
      ghostCollection.show = altNow < 3e3;
    }
  };

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    const Cesium = window.Cesium;
    viewer.scene.screenSpaceCameraController.enableTilt = is3DView;
    viewer.scene.screenSpaceCameraController.enableLook = is3DView;
    if (!is3DView) {
      // Pick the world point at the centre of the screen so we stay above it after going top-down.
      // When the camera is tilted, camera.position is behind the scene centre, so using it directly
      // would place the area of interest at the bottom edge after the reset.
      const canvas = viewer.scene.canvas;
      const screenCenter = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
      const groundPos = viewer.scene.pickPosition(screenCenter);

      let destination;
      if (groundPos) {
        const carto = Cesium.Cartographic.fromCartesian(groundPos);
        const height = viewer.camera.positionCartographic.height;
        destination = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, height);
      } else {
        destination = viewer.camera.position;
      }

      viewer.camera.flyTo({
        destination,
        orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0.0 },
        duration: 1.0,
      });
    }
  }, [is3DView]);

  // ── Generic R2 point layer system ────────────────────────────────────────

  // Deterministic color per layer key
  const LAYER_COLORS = ["#00ccff", "#ff8844", "#44ee88", "#ffcc00", "#cc44ff", "#ff4466"];
  const layerColor = key =>
    LAYER_COLORS[[...key].reduce((s, c) => s + c.charCodeAt(0), 0) % LAYER_COLORS.length];

  const setLayerState = (key, patch) =>
    setLayerStates(prev => ({ ...prev, [key]: { status: "idle", count: 0, ...prev[key], ...patch } }));

  const removeLayer = (key) => {
    const viewer = viewerRef.current;
    const col = collectionsRef.current[key];
    if (col && viewer && !viewer.isDestroyed()) viewer.scene.primitives.remove(col);
    delete collectionsRef.current[key];
    const ghost = ghostCollectionsRef.current[key];
    if (ghost && viewer && !viewer.isDestroyed()) viewer.scene.primitives.remove(ghost);
    delete ghostCollectionsRef.current[key];
    delete layerRawDataRef.current[key];
    // Client point layer cleanup
    delete clientLayerPropsRef.current[key];
    clientPointLayerKeysRef.current.delete(key);
    setClientFeatureCard(null);
    setLayerState(key, { status: "idle", count: 0 });
    setLayerFilters(prev => { const n = { ...prev }; delete n[key]; return n; });
    setLayerInfoOpen(prev => { const n = { ...prev }; delete n[key]; return n; });

    // Clear all tools — layer data is gone so any open results are stale
    clearIdentifyTool();
    setIsIdentifyMode(false);
    setMultiSelect(false);

    clearSectionTool();
    setIsSectionMode(false);

    clearPolygonTool();
    setIsPolygonMode(false);

    clearAreaTool();
    setIsAreaMode(false);
  };

  // ── GeoJSON data-source layer (polygons / lines from client uploads) ────────

  const loadClientGeoJsonLayer = async (key, name) => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !Cesium) return;
    setLayerState(key, { status: "loading" });
    try {
      const token    = authTokenRef.current || await getToken();
      const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
      const res      = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const geojson  = await res.json();
      const color    = Cesium.Color.fromCssColorString(layerColor(key));
      const ds       = new Cesium.GeoJsonDataSource(name || key);
      await ds.load(geojson, {
        clampToGround: true,
        stroke:        color,
        fill:          color.withAlpha(0.35),
        strokeWidth:   2,
        markerSymbol:  "marker",
      });
      await viewer.dataSources.add(ds);
      // Tag every entity so the client-layer click handler can identify them
      for (const entity of ds.entities.values) entity._clientLayerKey = key;
      clientGeoJsonSourcesRef.current[key] = ds;
      setLayerState(key, { status: "loaded" });
    } catch (err) {
      setLayerState(key, { status: "error", error: err.message });
    }
  };

  const removeClientGeoJsonLayer = (key) => {
    const viewer = viewerRef.current;
    const ds     = clientGeoJsonSourcesRef.current[key];
    if (ds && viewer && !viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    delete clientGeoJsonSourcesRef.current[key];
    setLayerState(key, { status: "idle" });
    setClientFeatureCard(null);
  };

  // ── Client point layers (parquet, clamped to ground, property identify) ────

  const loadClientPointLayer = async (key, name) => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !dbReady || !dbRef.current) return;
    setLayerState(key, { status: "loading" });
    try {
      const token    = authTokenRef.current || await getToken();
      const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
      const conn     = await dbRef.current.connect();

      // Discover columns
      const schema = await conn.query(`SELECT * FROM read_parquet('${proxyUrl}') LIMIT 0`);
      const cols   = schema.schema.fields.map(f => f.name);
      const latCol = cols.find(c => /^lat/i.test(c)) || cols.find(c => /lat/i.test(c)) || cols[0];
      const lonCol = cols.find(c => /^lon/i.test(c)) || cols.find(c => /lon/i.test(c)) || cols[1];
      const propCols = cols.filter(c => c !== latCol && c !== lonCol);

      const result = await conn.query(`SELECT * FROM read_parquet('${proxyUrl}')`);
      await conn.close();
      const rows = result.toArray();
      const count = rows.length;
      if (!count) { setLayerState(key, { status: "error", error: "Layer contains no features" }); return; }

      // Store per-point properties keyed by "lat_lon" for O(1) identify lookup
      const propsMap = new Map();
      const lats = new Float64Array(count);
      const lons  = new Float64Array(count);

      for (let i = 0; i < count; i++) {
        const row = rows[i];
        lats[i] = Number(row[latCol]);
        lons[i]  = Number(row[lonCol]);
        const props = {};
        for (const c of propCols) { const v = row[c]; if (v != null) props[c] = String(v); }
        propsMap.set(`${lats[i].toFixed(6)}_${lons[i].toFixed(6)}`, props);
      }
      clientLayerPropsRef.current[key] = propsMap;
      clientPointLayerKeysRef.current.add(key);

      // Render as single-color PointPrimitive clamped to ground (height = 0)
      const color      = Cesium.Color.fromCssColorString(layerColor(key));
      const collection = new Cesium.PointPrimitiveCollection();
      const BATCH = 25000;
      for (let i = 0; i < count; i++) {
        if (!isFinite(lats[i]) || !isFinite(lons[i])) continue;
        collection.add({
          position:                 Cesium.Cartesian3.fromDegrees(lons[i], lats[i], 0),
          color,
          pixelSize:                7,
          outlineColor:             Cesium.Color.WHITE.withAlpha(0.5),
          outlineWidth:             1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          id:                       { key, lat: lats[i], lon: lons[i], isClientPoint: true },
        });
        if ((i + 1) % BATCH === 0 || i === count - 1) {
          setLayerState(key, { status: "loading", count: 0, progress: Math.round((i + 1) / count * 100) });
          await new Promise(r => setTimeout(r, 0));
        }
      }

      if (collectionsRef.current[key]) viewer.scene.primitives.remove(collectionsRef.current[key]);
      viewer.scene.primitives.add(collection);
      collection.show = true;
      collectionsRef.current[key] = collection;
      layerRawDataRef.current[key] = { count, lats, lons, heights: null, featureCodes: null, colData: new Map(), colNames: propCols, latCol, lonCol, heightCol: null };
      setLayerState(key, { status: "loaded", count, isClientLayer: true });
    } catch (err) {
      setLayerState(key, { status: "error", error: err.message });
    }
  };

  const loadLayer = async (key) => {
    const viewer = viewerRef.current;
    if (!viewer || !dbReady || !dbRef.current) return;
    setLayerState(key, { status: "loading", count: 0, progress: 0 });
    try {
      const token    = authTokenRef.current || await getToken();
      const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
      const conn     = await dbRef.current.connect();

      // Detect lat/lon/velocity column names from schema (case-insensitive).
      // LIMIT 0 leaves the parquet footer cached in DuckDB's internal state so
      // the subsequent SELECT reuses it — no second HTTP fetch for metadata.
      const schemaResult = await conn.query(`SELECT * FROM read_parquet('${proxyUrl}') LIMIT 0`);
      const colMap  = {};
      schemaResult.schema.fields.forEach(f => { colMap[f.name.toLowerCase()] = f.name; });
      const latKey  = Object.keys(colMap).find(c => /^(lat|latitude|y)$/.test(c));
      const lonKey  = Object.keys(colMap).find(c => /^(lon|lng|longitude|x)$/.test(c));
      if (!latKey || !lonKey) throw new Error(`No lat/lon columns found. Available: ${Object.values(colMap).join(", ")}`);
      const latCol  = colMap[latKey];
      const lonCol  = colMap[lonKey];
      const hasFCode = "feature_code" in colMap;
      const heightKey = Object.keys(colMap).find(c => /^(height|z|elevation|elev|alt|altitude|h)$/.test(c));
      const heightCol = heightKey ? colMap[heightKey] : null;
      // Prefer last D_YYYYMMDD column; fall back to a named velocity column
      const allColNames = Object.values(colMap);
      const dCols = allColNames.filter(c => /^D_\d{8}$/i.test(c)).sort();
      const lastDCol = dCols.length ? dCols[dCols.length - 1] : null;
      const velKeyFallback = !hasFCode && !lastDCol && Object.keys(colMap).find(c => /^(velocity|vel|v|rate|mean_vel|los_velocity|def_rate|displacement)$/.test(c));
      const valueCol = lastDCol || (velKeyFallback ? colMap[velKeyFallback] : null);

      // Cache schema for Point Inspector (avoids re-DESCRIBE on click)
      layerSchemasRef.current[key] = { latCol, lonCol, heightCol };

      // Select only the columns needed for initial rendering — minimal payload.
      // D_ epoch history is fetched lazily by tools (targeted bbox query).
      const selectColsList = [`"${latCol}"`, `"${lonCol}"`];
      if (heightCol) selectColsList.push(`"${heightCol}"`);
      if (hasFCode) {
        selectColsList.push("feature_code");
      } else if (lastDCol) {
        selectColsList.push(`"${lastDCol}"`);  // latest epoch for point coloring
      } else if (valueCol) {
        selectColsList.push(`"${valueCol}"`);
      }
      // Also include scalar attribute columns — tiny (one value/row) but used by
      // the initial section profile and Point Inspector's quick-render path.
      const SCALAR_PATTERNS = [
        /^velocity$/i, /^vel$/i, /^mean_vel$/i, /^los_velocity$/i, /^def_rate$/i,
        /^vprecision$/i, /^coherence$/i, /^coh$/i,
        /^total_displacement$/i, /^t_displacement$/i, /^cumulative$/i,
        /^los_dir$/i, /^type_motn$/i, /^alert$/i,
      ];
      for (const [lc, orig] of Object.entries(colMap)) {
        const q = `"${orig}"`;
        if (!selectColsList.includes(q) && SCALAR_PATTERNS.some(p => p.test(lc))) {
          selectColsList.push(q);
        }
      }
      // Detect layer metadata columns: satellite platform (string), processing method (string), ALOS angle (numeric)
      const _findOrig = re => { const k = Object.keys(colMap).find(c => re.test(c)); return k ? colMap[k] : null; };
      const satColOrig    = _findOrig(/^sat$/i);
      const methodColOrig = _findOrig(/^method$/i);
      const alosColOrig   = _findOrig(/^alos$/i);
      for (const c of [satColOrig, methodColOrig, alosColOrig]) {
        if (c && !selectColsList.includes(`"${c}"`)) selectColsList.push(`"${c}"`);
      }
      const selectCols = selectColsList.join(", ");

      const result = await conn.query(
        `SELECT ${selectCols}
         FROM read_parquet('${proxyUrl}')
         WHERE "${latCol}" IS NOT NULL AND "${lonCol}" IS NOT NULL`
      );
      await conn.close();

      // ── Columnar typed arrays from Arrow result ───────────────────────────────
      // Replaces 100K plain-object allocations + 600K [key,val] temp arrays that
      // the old Object.fromEntries(colNames.map(...)) approach created on the main thread.
      const resColNames = result.schema.fields.map(f => f.name);
      const arrowRows   = result.toArray();
      const count       = arrowRows.length;
      const Cesium      = window.Cesium;

      // Extract per-layer scalar metadata from first data row (constant across all rows)
      const metaSat    = satColOrig    ? String(arrowRows[0]?.[satColOrig]    ?? '').trim() || null : null;
      const metaMethod = methodColOrig ? String(arrowRows[0]?.[methodColOrig] ?? '').trim() || null : null;
      const metaAlosRaw = alosColOrig  ? arrowRows[0]?.[alosColOrig]                               : null;
      const metaAlos   = (metaAlosRaw != null && isFinite(Number(metaAlosRaw))) ? Number(metaAlosRaw) : null;

      const lats         = new Float64Array(count);
      const lons         = new Float64Array(count);
      const heights      = heightCol ? new Float64Array(count) : null;
      const featureCodes = hasFCode  ? new Array(count)        : null;

      const _strMetaCols = new Set([satColOrig, methodColOrig].filter(Boolean));
      const numericCols  = resColNames.filter(c => c !== latCol && c !== lonCol && c !== heightCol && c !== 'feature_code' && !_strMetaCols.has(c));
      const colData      = new Map(numericCols.map(c => [c, new Float64Array(count)]));

      let minV = 0, maxV = 0;
      let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;

      for (let i = 0; i < count; i++) {
        const r  = arrowRows[i];
        const la = Number(r[latCol]);
        const lo = Number(r[lonCol]);
        lats[i]  = la; lons[i] = lo;
        if (la < minLat) minLat = la; if (la > maxLat) maxLat = la;
        if (lo < minLon) minLon = lo; if (lo > maxLon) maxLon = lo;
        if (heights)      heights[i]      = Number(r[heightCol]);
        if (featureCodes) featureCodes[i] = r.feature_code ?? "";
        for (const c of numericCols) colData.get(c)[i] = Number(r[c]);
        if (valueCol) {
          const v = colData.get(valueCol)?.[i] ?? NaN;
          if (isFinite(v)) { if (v < minV) minV = v; if (v > maxV) maxV = v; }
        }
      }

      const defColor   = Cesium.Color.fromCssColorString(layerColor(key));
      const colorStops = layerStates[key]?.colorStops || DEFAULT_PALETTE.stops;
      const valArr     = valueCol ? colData.get(valueCol) : null;

      const flatDegs = new Array(count * 2);
      for (let i = 0; i < count; i++) { flatDegs[i * 2] = lons[i]; flatDegs[i * 2 + 1] = lats[i]; }
      const positions = Cesium.Cartesian3.fromDegreesArray(flatDegs);

      if (collectionsRef.current[key]) viewer.scene.primitives.remove(collectionsRef.current[key]);
      const oldGhost = ghostCollectionsRef.current[key];
      if (oldGhost && !viewer.isDestroyed()) { viewer.scene.primitives.remove(oldGhost); delete ghostCollectionsRef.current[key]; }

      const collection = new Cesium.PointPrimitiveCollection({ show: false });
      const BATCH = 25000;
      for (let i = 0; i < count; i++) {
        const v = valArr ? valArr[i] : NaN;
        const h = heights ? heights[i] : NaN;
        collection.add({
          position:                 positions[i],
          color:                    hasFCode ? featureColor(featureCodes[i])
                                  : (valArr && isFinite(v)) ? velocityColor(v, minV, maxV, colorStops)
                                  : defColor,
          pixelSize:                6,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          id:                       { key, lat: lats[i], lon: lons[i], h: Number.isFinite(h) ? h : null },
        });
        if ((i + 1) % BATCH === 0 || i === count - 1) {
          const progress = Math.round((i + 1) / count * 100);
          setLayerState(key, { status: "loading", count: 0, progress });
          await new Promise(r => setTimeout(r, 0));
        }
      }
      viewer.scene.primitives.add(collection);
      collectionsRef.current[key] = collection;

      const colNames = numericCols;
      layerRawDataRef.current[key] = { count, lats, lons, heights, featureCodes, colData, colNames, latCol, lonCol, heightCol };

      if (viewerRef._terrain3DProvider) {
        estimateHeightOffset(viewerRef._terrain3DProvider, layerRawDataRef.current[key])
          .then(offset => {
            if (layerRawDataRef.current[key]) layerRawDataRef.current[key].cachedOffset = offset;
          });
      }

      apply3DModeToLoadedLayers();

      setLayerState(key, { status: "loaded", count, minV, maxV, hasVel: !!valueCol, valueColLabel: valueCol || null, minLat, maxLat, minLon, maxLon, dCols, activeCol: valueCol || null, colorStops, satPlatform: metaSat, processingMethod: metaMethod, alosLos: metaAlos, startDate: dCols[0] ?? null, endDate: dCols[dCols.length - 1] ?? null });

      // Fly to the layer's bounding box once all points are on the map and the layer is marked loaded
      if (minLat <= maxLat && minLon <= maxLon) {
        const centerLon = (minLon + maxLon) / 2;
        const centerLat = (minLat + maxLat) / 2;
        const center = Cesium.Cartesian3.fromDegrees(centerLon, centerLat);
        const corner = Cesium.Cartesian3.fromDegrees(minLon, minLat);
        const radius = Cesium.Cartesian3.distance(center, corner);
        viewer.camera.flyToBoundingSphere(
          new Cesium.BoundingSphere(center, radius),
          { duration: 2, offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, 0) }
        );
      }
    } catch (e) {
      console.error(`Layer load failed (${key}):`, e);
      setLayerState(key, { status: "error", count: 0 });
    }
  };

  const toggleLayer = (key) => {
    const clientLayer = clientLayers.find(cl => cl.key === key);
    if (clientLayer?.file_type === "geojson") {
      // Polygon/line client layer — Cesium GeoJsonDataSource
      const st = layerStates[key]?.status ?? "idle";
      if (st === "loaded") removeClientGeoJsonLayer(key);
      else if (st !== "loading") loadClientGeoJsonLayer(key, clientLayer.name);
      return;
    }
    if (clientLayer) {
      // Point client layer — single-color PointPrimitive with property identify
      const st = layerStates[key]?.status ?? "idle";
      if (st === "loaded") removeLayer(key);
      else if (st !== "loading") loadClientPointLayer(key, clientLayer.name);
      return;
    }
    // Admin / InSAR layer
    const st = layerStates[key]?.status ?? "idle";
    if (st === "loaded") removeLayer(key);
    else if (st !== "loading") loadLayer(key);
  };

  const recolorLayer = async (key, newCol, hexStops) => {
    const cached = layerRawDataRef.current[key];
    const collection = collectionsRef.current[key];
    const Cesium = window.Cesium;
    if (!cached || !collection || !Cesium) return;
    const { count, lats, lons, colData, latCol, lonCol } = cached;
    const col = newCol || layerStates[key]?.activeCol;

    // Lazy-fetch this epoch column if it wasn't loaded with the layer
    if (col && count > 0 && !colData.has(col) && dbReady && dbRef.current) {
      try {
        const token    = authTokenRef.current || await getToken();
        const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
        const conn     = await dbRef.current.connect();
        const result   = await conn.query(`SELECT "${latCol}", "${lonCol}", "${col}" FROM read_parquet('${proxyUrl}')`);
        await conn.close();
        const freshRows = result.toArray();
        const idx       = new Map(freshRows.map(r => [`${r[latCol]}_${r[lonCol]}`, Number(r[col])]));
        const newVals   = new Float64Array(count);
        for (let i = 0; i < count; i++) {
          const v = idx.get(`${lats[i]}_${lons[i]}`);
          newVals[i] = v !== undefined ? v : NaN;
        }
        colData.set(col, newVals);
      } catch (e) {
        console.error(`Epoch column lazy-load failed (${key}, ${col}):`, e);
        return;
      }
    }

    const valArr = colData.get(col);
    if (!valArr) return;
    const stops = hexStops || layerStates[key]?.colorStops || DEFAULT_PALETTE.stops;
    let minV = 0, maxV = 0;
    for (let i = 0; i < count; i++) {
      const v = valArr[i];
      if (isFinite(v)) { if (v < minV) minV = v; if (v > maxV) maxV = v; }
    }
    // Restore all point visibility before recoloring (clears any active filter)
    for (let i = 0; i < collection.length; i++) {
      const pt = collection.get(i);
      pt.show = true;
      const v = valArr[i];
      const c = isFinite(v) ? velocityColor(v, minV, maxV, stops) : Cesium.Color.GRAY.withAlpha(0.8);
      pt.color = c;
      const ghost = ghostCollectionsRef.current[key];
      if (ghost) { const gp = ghost.get(i); if (gp) { gp.show = true; gp.color = c.withAlpha(0.25); } }
    }
    // Reset filter state — the new epoch has a different value distribution
    setLayerFilters(prev => ({ ...prev, [key]: { lo: 0, hi: 1 } }));
    setLayerState(key, { activeCol: col, minV, maxV, valueColLabel: col, ...(hexStops ? { colorStops: hexStops } : {}) });
  };

  const applyLayerFilter = (key, lo, hi) => {
    const Cesium     = window.Cesium;
    const collection = collectionsRef.current[key];
    const ghost      = ghostCollectionsRef.current[key];
    const st         = layerStates[key];
    const raw        = layerRawDataRef.current[key];
    if (!collection || !st || !raw || !Cesium) return;

    const { colData, featureCodes } = raw;
    const valArr   = colData.get(st.activeCol);
    const stops    = st.colorStops || DEFAULT_PALETTE.stops;
    const minV     = st.minV ?? 0, maxV = st.maxV ?? 0;
    const noFilter = lo <= 0 && hi >= 1;
    // Use the same coordinate system as velocityColor: range = max(|minV|,|maxV|),
    // visual position p ∈ [0,1] maps to value = (p*2-1) * range
    const range    = Math.max(Math.abs(minV), Math.abs(maxV), 0.001);
    const rangeMin = (lo * 2 - 1) * range;
    const rangeMax = (hi * 2 - 1) * range;
    const defColor = Cesium.Color.fromCssColorString(layerColor(key));

    for (let i = 0, n = collection.length; i < n; i++) {
      const v       = valArr ? valArr[i] : NaN;
      const inRange = noFilter || (isFinite(v) && v >= rangeMin && v <= rangeMax);
      const pt      = collection.get(i);
      pt.show = inRange;
      if (inRange) {
        pt.color = featureCodes?.length
          ? featureColor(featureCodes[i] ?? '')
          : valArr && isFinite(v) ? velocityColor(v, minV, maxV, stops)
          : defColor;
      }
      if (ghost) {
        const gpt = ghost.get(i);
        if (gpt) { gpt.show = inRange; if (inRange) gpt.color = pt.color.withAlpha(0.25); }
      }
    }
  };

  // ── Draw tool helpers ────────────────────────────────────────────────────────

  const featureToWKT = (feature) => {
    if (feature.type === "line") {
      const pts = feature.vertices.map(v => `${v.lon} ${v.lat} ${v.alt ?? 0}`).join(",");
      return `LINESTRING Z(${pts})`;
    }
    const ring = [...feature.vertices, feature.vertices[0]];
    const pts  = ring.map(v => `${v.lon} ${v.lat} ${v.alt ?? 0}`).join(",");
    return `POLYGON Z((${pts}))`;
  };

  const wktToVertices = (wkt) => {
    const inner = wkt.replace(/^(LINESTRING|POLYGON)\s*Z?\s*\(+/i, "").replace(/\)+$/, "");
    return inner.split(",").map(pair => {
      const parts = pair.trim().split(/\s+/).map(Number);
      return { lon: parts[0], lat: parts[1], alt: parts.length > 2 ? parts[2] : undefined };
    });
  };

  // Render one drawn feature as Cesium entities and return them.
  // Positions use CallbackProperty so vertex drag updates the line live without re-render.
  const renderDrawFeature = (feature) => {
    const Cesium  = window.Cesium;
    const viewer  = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return [];
    const entities = [];
    const color    = Cesium.Color.fromCssColorString(feature.color || "#3b82f6");
    const hasAlt   = feature.vertices.some(v => v.alt != null);
    const labelHRef = hasAlt ? Cesium.HeightReference.NONE : Cesium.HeightReference.CLAMP_TO_GROUND;

    // Mutable copy used by CallbackProperty so MOUSE_MOVE during drag updates the line in real-time
    featureVerticesRefMap.current[feature.id] = [...feature.vertices];
    const fid = feature.id;

    const getPositions = () =>
      (featureVerticesRefMap.current[fid] || feature.vertices)
        .map(v => Cesium.Cartesian3.fromDegrees(v.lon, v.lat, v.alt ?? 0));

    if (feature.type === "line") {
      const lineEnt = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(getPositions, false),
          width: 5,
          material: new Cesium.PolylineDashMaterialProperty({
            color, gapColor: Cesium.Color.BLACK.withAlpha(0.5), dashLength: 20,
          }),
          clampToGround: true,
        },
      });
      entities.push(lineEnt);
      {
        let km = 0;
        for (let i = 0; i < feature.vertices.length - 1; i++)
          km += haversineKm(feature.vertices[i].lat, feature.vertices[i].lon, feature.vertices[i+1].lat, feature.vertices[i+1].lon);
      }
      if (feature.label) {
        const mi = Math.floor(feature.vertices.length / 2);
        const mv = feature.vertices[mi];
        entities.push(viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(mv.lon, mv.lat, mv.alt ?? 0),
          billboard: {
            ...makeLabelBillboard(feature.label, { pixelOffsetY: -14 }),
            heightReference: labelHRef,
          },
        }));
      }
    } else {
      const polyEnt = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const pts = getPositions();
            return pts.length > 0 ? [...pts, pts[0]] : pts;
          }, false),
          width: 5,
          material: new Cesium.PolylineDashMaterialProperty({
            color, gapColor: Cesium.Color.BLACK.withAlpha(0.5), dashLength: 20,
          }),
          clampToGround: true,
        },
      });
      entities.push(polyEnt);
      entities.push(viewer.entities.add({
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() =>
            new Cesium.PolygonHierarchy(getPositions()), false),
          material: color.withAlpha(0.22),
          classificationType: Cesium.ClassificationType.BOTH,
        },
      }));
      if (feature.label) {
        const n  = feature.vertices.length;
        const cx = feature.vertices.reduce((s, v) => s + v.lon, 0) / n;
        const cy = feature.vertices.reduce((s, v) => s + v.lat, 0) / n;
        const ca = feature.vertices.reduce((s, v) => s + (v.alt ?? 0), 0) / n;
        entities.push(viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(cx, cy, ca),
          billboard: {
            ...makeLabelBillboard(feature.label, { pixelOffsetY: 0 }),
            heightReference: labelHRef,
          },
        }));
      }
    }
    return entities;
  };

  const clearDrawEntities = () => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      drawEntitiesRef.current.forEach(e => { try { viewer.entities.remove(e); } catch (_) {} });
    }
    drawEntitiesRef.current = [];
    drawFeaturesRef.current.forEach(f => clearVertexDots(f.id));
  };

  const reRenderDrawFeatures = (features) => {
    clearDrawEntities();
    features.forEach(f => {
      const entities = renderDrawFeature(f);
      drawEntitiesRef.current.push(...entities);
      renderVertexDots(f, "draw");
    });
  };

  const reRenderAnnoLayer = (key, features) => {
    const viewer = viewerRef.current;
    const old = annoEntitiesRef.current[key] || [];
    if (viewer && !viewer.isDestroyed()) {
      old.forEach(e => { try { viewer.entities.remove(e); } catch (_) {} });
    }
    const next = [];
    features.forEach(f => {
      const ents = renderDrawFeature(f);
      featureEntityMapRef.current[f.id] = ents;
      next.push(...ents);
      renderVertexDots(f, key);
    });
    annoEntitiesRef.current[key] = next;
  };

  // ── Vertex dot helpers ────────────────────────────────────────────────────
  const renderVertexDots = (feature, source) => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || viewer.isDestroyed() || !Cesium || !feature?.vertices?.length) return;
    (vertexDotsMapRef.current[feature.id] || []).forEach(e => {
      delete vertexDotRegistryRef.current[e.id];
      try { viewer.entities.remove(e); } catch(_) {}
    });
    const dots = [];
    feature.vertices.forEach((v, idx) => {
      const e = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(v.lon, v.lat, v.alt ?? 0),
        point: {
          pixelSize: 7, color: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString(feature.color || "#3b82f6"),
          outlineWidth: 2, disableDepthTestDistance: Infinity,
          heightReference: v.alt != null ? Cesium.HeightReference.NONE : Cesium.HeightReference.CLAMP_TO_GROUND,
        },
      });
      vertexDotRegistryRef.current[e.id] = { featureId: feature.id, vertexIdx: idx, source };
      dots.push(e);
    });
    vertexDotsMapRef.current[feature.id] = dots;
  };

  const clearVertexDots = (featureId) => {
    const viewer = viewerRef.current;
    (vertexDotsMapRef.current[featureId] || []).forEach(e => {
      delete vertexDotRegistryRef.current[e.id];
      if (viewer && !viewer.isDestroyed()) try { viewer.entities.remove(e); } catch(_) {}
    });
    delete vertexDotsMapRef.current[featureId];
    delete featureVerticesRefMap.current[featureId];
    delete featureEntityMapRef.current[featureId];
  };

  const setupVertexDragHandler = () => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || viewer.isDestroyed() || !Cesium) return;
    if (vertexDragHandlerRef.current && !vertexDragHandlerRef.current.isDestroyed()) {
      vertexDragHandlerRef.current.destroy();
    }
    const canvas  = viewer.scene.canvas;
    const handler = new Cesium.ScreenSpaceEventHandler(canvas);

    // Returns true if a vertex dot (by source) is currently in an editable state
    const isDraggable = (info) =>
      (info.source === "draw" && (drawFeaturesRef.current.length > 0 || !!drawToolRef.current)) ||
      info.source === annoEditKeyRef.current ||
      (info.source === "__area__" && isAreaModeRef.current);

    // Find the nearest vertex dot within pixelRadius; returns { entity, dist } or null
    const findNearest = (screenPos, pixelRadius) => {
      let nearest = null, minDist = pixelRadius;
      for (const dots of Object.values(vertexDotsMapRef.current)) {
        for (const entity of dots) {
          const pos3d = entity.position?.getValue(viewer.clock.currentTime);
          if (!pos3d) continue;
          const sp = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pos3d);
          if (!sp) continue;
          const d = Math.hypot(sp.x - screenPos.x, sp.y - screenPos.y);
          if (d < minDist) { minDist = d; nearest = entity; }
        }
      }
      return nearest;
    };

    handler.setInputAction((e) => {
      const nearest = findNearest(e.position, 12);
      if (!nearest) return;
      const info = vertexDotRegistryRef.current[nearest.id];
      if (!info || !isDraggable(info)) return;
      activeVertexDragRef.current = { ...info, entity: nearest };
      viewer.scene.screenSpaceCameraController.enableRotate    = false;
      viewer.scene.screenSpaceCameraController.enableTranslate = false;
      viewer.scene.screenSpaceCameraController.enableZoom      = false;
      canvas.style.setProperty("cursor", "grabbing", "important");
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction((e) => {
      let cart = viewer.scene.pickPosition(e.endPosition);
      if (!Cesium.defined(cart)) cart = viewer.camera.pickEllipsoid(e.endPosition, Cesium.Ellipsoid.WGS84);

      if (activeVertexDragRef.current) {
        // ── Active drag: update position live ──────────────────────────────────
        if (!cart) return;
        const c   = Cesium.Cartographic.fromCartesian(cart);
        const lon = Cesium.Math.toDegrees(c.longitude);
        const lat = Cesium.Math.toDegrees(c.latitude);
        const alt = c.height;
        const { featureId, vertexIdx, source, entity } = activeVertexDragRef.current;
        // Move the dot entity
        entity.position = new Cesium.ConstantPositionProperty(Cesium.Cartesian3.fromDegrees(lon, lat, alt));
        activeVertexDragRef.current.newPos = { lon, lat, alt };
        // Update the mutable vertices so CallbackProperty-based lines update immediately
        if (source === "__area__") {
          if (areaVerticesRef.current[vertexIdx]) areaVerticesRef.current[vertexIdx] = { ...areaVerticesRef.current[vertexIdx], lon, lat, alt };
        } else if (featureVerticesRefMap.current[featureId]) {
          featureVerticesRefMap.current[featureId][vertexIdx] = { lon, lat, alt };
        }
        canvas.style.setProperty("cursor", "grabbing", "important");
        return;
      }

      // ── No active drag: show pointer cursor when hovering a draggable dot ──
      const nearest = findNearest(e.endPosition, 12);
      if (nearest) {
        const info = vertexDotRegistryRef.current[nearest.id];
        if (info && isDraggable(info)) {
          canvas.style.setProperty("cursor", "pointer", "important");
          return;
        }
      }
      // Restore default (CSS crosshair or browser default)
      canvas.style.removeProperty("cursor");
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      const drag = activeVertexDragRef.current;
      activeVertexDragRef.current = null;
      if (viewer && !viewer.isDestroyed()) {
        viewer.scene.screenSpaceCameraController.enableRotate    = true;
        viewer.scene.screenSpaceCameraController.enableTranslate = true;
        viewer.scene.screenSpaceCameraController.enableZoom      = true;
      }
      canvas.style.removeProperty("cursor");
      if (!drag?.newPos) return;
      const { featureId, vertexIdx, source, newPos } = drag;
      if (source === "draw") {
        const updated = drawFeaturesRef.current.map(f => {
          if (f.id !== featureId) return f;
          const verts = [...f.vertices]; verts[vertexIdx] = newPos;
          return { ...f, vertices: verts };
        });
        drawFeaturesRef.current = updated;
        setDrawFeatures(updated);
        // Lines already correct via CallbackProperty; dots already repositioned in MOUSE_MOVE — no re-render needed
      } else if (source === "__area__") {
        // areaVerticesRef already updated live in MOUSE_MOVE; re-render dots and recalc result
        renderVertexDots({ id: "__area__", vertices: [...areaVerticesRef.current], color: "#ffffff" }, "__area__");
        if (areaIsClosedRef.current) {
          const verts = areaVerticesRef.current;
          const area  = geodesicAreaM2(verts);
          const perim = geodesicPerimeterM(verts);
          let count = 0, velSum = 0, velCount = 0;
          Object.entries(layerRawDataRef.current).forEach(([key, cached]) => {
            const ls = layerStatesRef.current[key];
            if (!ls || ls.status !== "loaded") return;
            const { lats, lons } = cached;
            const velCol = ls.activeCol;
            const velArr = velCol && cached.colData ? cached.colData.get(velCol) : null;
            for (let i = 0; i < lats.length; i++) {
              if (pointInPoly(lats[i], lons[i], verts)) {
                count++;
                if (velArr && isFinite(velArr[i])) { velSum += velArr[i]; velCount++; }
              }
            }
          });
          setAreaResult({ area, perim, count, avgVel: velCount > 0 ? velSum / velCount : null });
        }
      } else {
        const updatedFeatures = (annoFeaturesRef.current[source] || []).map(f => {
          if (f.id !== featureId) return f;
          const verts = [...f.vertices]; verts[vertexIdx] = newPos;
          return { ...f, vertices: verts };
        });
        annoFeaturesRef.current[source] = updatedFeatures;
        setAnnoEditFeatures(updatedFeatures);
        // Lines already correct via CallbackProperty; dots already repositioned in MOUSE_MOVE — no re-render needed
        scheduleAutoSave(source);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    vertexDragHandlerRef.current = handler;
  };

  const stopDrawTool = () => {
    const viewer = viewerRef.current;
    if (drawClickHandlerRef.current && !drawClickHandlerRef.current.isDestroyed()) drawClickHandlerRef.current.destroy();
    if (drawMoveHandlerRef.current  && !drawMoveHandlerRef.current.isDestroyed())  drawMoveHandlerRef.current.destroy();
    drawClickHandlerRef.current = null;
    drawMoveHandlerRef.current  = null;
    // Remove preview entities
    if (viewer && !viewer.isDestroyed() && drawPreviewRef.current) {
      if (drawPreviewRef.current.line) try { viewer.entities.remove(drawPreviewRef.current.line); } catch (_) {}
      if (drawPreviewRef.current.fill) try { viewer.entities.remove(drawPreviewRef.current.fill); } catch (_) {}
    }
    drawPreviewRef.current  = null;
    drawVerticesRef.current = [];
    drawPreviewPosRef.current = null;
    drawToolRef.current = null;
    setDrawTool(null);
    // Clear in-progress vertex dots
    if (viewer && !viewer.isDestroyed()) {
      drawInProgressDotsRef.current.forEach(e => { try { viewer.entities.remove(e); } catch(_) {} });
    }
    drawInProgressDotsRef.current = [];
  };

  const startDrawTool = (tool) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;

    // Deactivate other tools
    clearIdentifyTool(); setIsIdentifyMode(false);
    clearSectionTool();  setIsSectionMode(false);
    clearPolygonTool();  setIsPolygonMode(false);
    clearAreaTool();     setIsAreaMode(false);
    clearDistanceTool(); setIsDistanceMode(false);
    setAnnoEditKey(null);

    stopDrawTool();
    drawToolRef.current = tool;
    drawVerticesRef.current = [];

    const ellipsoid = Cesium.Ellipsoid.WGS84;

    // ── Preview line ──
    const previewLine = viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          const verts = drawVerticesRef.current;
          if (verts.length === 0 || !drawPreviewPosRef.current) return [];
          const pts = verts.map(v => Cesium.Cartesian3.fromDegrees(v.lon, v.lat, 0));
          pts.push(Cesium.Cartesian3.fromDegrees(drawPreviewPosRef.current.lon, drawPreviewPosRef.current.lat, 0));
          if (drawToolRef.current === "polygon" && verts.length >= 2) {
            pts.push(Cesium.Cartesian3.fromDegrees(verts[0].lon, verts[0].lat, 0));
          }
          return pts;
        }, false),
        width: 3,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.WHITE, gapColor: new Cesium.Color(0, 0, 0, 0.5), dashLength: 20,
        }),
        clampToGround: true,
      },
    });
    const previewFill = viewer.entities.add({
      polygon: {
        hierarchy: new Cesium.CallbackProperty(() => {
          const verts = drawVerticesRef.current;
          if (drawToolRef.current !== "polygon" || verts.length < 2) return new Cesium.PolygonHierarchy([]);
          const pts = verts.map(v => Cesium.Cartesian3.fromDegrees(v.lon, v.lat, 0));
          return new Cesium.PolygonHierarchy(pts);
        }, false),
        material: Cesium.Color.WHITE.withAlpha(0.10),
        classificationType: Cesium.ClassificationType.BOTH,
      },
    });
    drawPreviewRef.current = { line: previewLine, fill: previewFill };

    // ── Mouse move ──
    const moveHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    moveHandler.setInputAction((e) => {
      let cart = viewer.scene.pickPosition(e.endPosition);
      if (!Cesium.defined(cart)) cart = viewer.camera.pickEllipsoid(e.endPosition, ellipsoid);
      if (!cart) return;
      const c = Cesium.Cartographic.fromCartesian(cart);
      drawPreviewPosRef.current = { lon: Cesium.Math.toDegrees(c.longitude), lat: Cesium.Math.toDegrees(c.latitude) };
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    drawMoveHandlerRef.current = moveHandler;

    // ── Click handler ──
    const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandler.setInputAction((click) => {
      let cart = viewer.scene.pickPosition(click.position);
      if (!Cesium.defined(cart)) cart = viewer.camera.pickEllipsoid(click.position, ellipsoid);
      if (!cart) return;
      const c   = Cesium.Cartographic.fromCartesian(cart);
      const lon = Cesium.Math.toDegrees(c.longitude);
      const lat = Cesium.Math.toDegrees(c.latitude);
      const alt = c.height;
      const verts = drawVerticesRef.current;

      // Close polygon if clicking near first vertex (within 15px screen distance)
      if (drawToolRef.current === "polygon" && verts.length >= 3) {
        const firstScreen = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene,
          Cesium.Cartesian3.fromDegrees(verts[0].lon, verts[0].lat, verts[0].alt ?? 0));
        if (firstScreen) {
          const dx = firstScreen.x - click.position.x, dy = firstScreen.y - click.position.y;
          if (Math.sqrt(dx * dx + dy * dy) < 15) { finishDrawing(); return; }
        }
      }
      drawVerticesRef.current = [...verts, { lon, lat, alt }];
      const dot = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lon, lat, alt),
        point: {
          pixelSize: 7, color: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString(drawColorRef.current),
          outlineWidth: 2, disableDepthTestDistance: Infinity,
          heightReference: Cesium.HeightReference.NONE,
        },
      });
      drawInProgressDotsRef.current = [...drawInProgressDotsRef.current, dot];
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Double-click → finish
    clickHandler.setInputAction(() => {
      if (drawVerticesRef.current.length >= 2) finishDrawing();
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    drawClickHandlerRef.current = clickHandler;
  };

  const finishDrawing = () => {
    const verts = [...drawVerticesRef.current];
    if (verts.length < 2) { stopDrawTool(); return; }
    // Remove duplicate last vertex added by double-click
    if (verts.length >= 2) {
      const last = verts[verts.length - 1], prev = verts[verts.length - 2];
      if (Math.abs(last.lon - prev.lon) < 0.00001 && Math.abs(last.lat - prev.lat) < 0.00001) verts.pop();
    }
    if (verts.length < 2) { stopDrawTool(); return; }
    if (drawToolRef.current === "polygon" && verts.length < 3) { stopDrawTool(); return; }

    const feature = {
      id:       crypto.randomUUID(),
      type:     drawToolRef.current,
      vertices: verts,
      label:    "",
      color:    drawColorRef.current,
    };

    stopDrawTool();

    const newFeatures = [...drawFeatures, feature];
    setDrawFeatures(newFeatures);
    const entities = renderDrawFeature(feature);
    drawEntitiesRef.current.push(...entities);
    renderVertexDots(feature, "draw");
    // Prompt for label
    setDrawEditLabel(feature.id);
  };

  const updateFeatureLabel = (id, label) => {
    const updated = drawFeatures.map(f => f.id === id ? { ...f, label } : f);
    setDrawFeatures(updated);
    reRenderDrawFeatures(updated);
    setDrawEditLabel(null);
  };

  const updateFeatureColor = (id, color) => {
    const updated = drawFeatures.map(f => f.id === id ? { ...f, color } : f);
    setDrawFeatures(updated);
    reRenderDrawFeatures(updated);
  };

  const deleteDrawFeature = (id) => {
    clearVertexDots(id);
    const updated = drawFeatures.filter(f => f.id !== id);
    setDrawFeatures(updated);
    reRenderDrawFeatures(updated);
  };

  // ── Save drawn features as Parquet to R2 ─────────────────────────────────
  const saveAnnotationLayer = async (name) => {
    const Cesium = window.Cesium;
    if (!dbRef.current || !selectedProject || !name.trim()) return;
    setSavingDrawing(true);
    try {
      const token     = authTokenRef.current || await getToken();
      const projectId = String(selectedProject.id);
      const safeKey   = name.trim().replace(/[^a-zA-Z0-9_\-]/g, "_");
      const r2Key     = `annotations/${projectId}/${userEmail}/${safeKey}.parquet`;

      const conn = await dbRef.current.connect();
      await conn.query(`DROP TABLE IF EXISTS _anno_tmp`);
      await conn.query(`CREATE TABLE _anno_tmp (
        id VARCHAR, type VARCHAR, geometry VARCHAR, label VARCHAR,
        color VARCHAR, created_by VARCHAR, created_at VARCHAR
      )`);
      const now = new Date().toISOString();
      const displayName = name.trim();
      await conn.query(`INSERT INTO _anno_tmp VALUES ('__meta__','meta','','${displayName.replace(/'/g,"''")}','','${userEmail}','${now}')`);
      for (const f of drawFeatures) {
        const wkt = featureToWKT(f);
        await conn.query(
          `INSERT INTO _anno_tmp VALUES ('${f.id}','${f.type}','${wkt.replace(/'/g,"''")}',
           '${(f.label||"").replace(/'/g,"''")}','${f.color}','${userEmail}','${now}')`
        );
      }
      const outFile = `anno_${Date.now()}.parquet`;
      await conn.query(`COPY _anno_tmp TO '${outFile}' (FORMAT PARQUET)`);
      await conn.close();

      const bytes = await dbRef.current.copyFileToBuffer(outFile);
      const res   = await fetch(
        `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(r2Key)}&t=${encodeURIComponent(token)}`,
        { method: "PUT", body: bytes, headers: { "Content-Type": "application/octet-stream" } }
      );
      if (!res.ok) throw new Error(await res.text());

      // Refresh annotation layer list and add the new entry
      cacheAnnoName(r2Key, displayName);
      setAnnoLayers(prev => {
        const filtered = prev.filter(l => l.key !== r2Key);
        return [...filtered, { key: r2Key, name: displayName, lastModified: now }];
      });
      setDrawFeatures([]);
      clearDrawEntities();
      setShowSaveDrawing(false);
      setSaveDrawingName("");
    } catch (e) {
      console.error("Save annotation failed:", e);
      alert(`Save failed: ${e.message}`);
    } finally {
      setSavingDrawing(false);
    }
  };

  // ── Append drawn features to an existing annotation layer ────────────────
  const appendDrawToAnnoLayer = async (key) => {
    if (!key || !drawFeatures.length) return;
    const merged = [...(annoFeaturesRef.current[key] || []), ...drawFeatures];
    annoFeaturesRef.current[key] = merged;
    reRenderAnnoLayer(key, merged);
    const layer = annoLayers.find(l => l.key === key);
    const displayName = layer?.name || "";
    setAnnoEditKey(key);
    setAnnoEditFeatures(merged);
    setAnnoEditLayerName(displayName);
    setDrawFeatures([]);
    clearDrawEntities();
    setShowSaveDrawing(false);
    await saveAnnoLayerChanges(key, displayName);
  };

  // ── List annotation layers for current project ───────────────────────────
  const fetchAnnoLayers = async (project) => {
    if (!project) { setAnnoLayers([]); return; }
    try {
      const token     = authTokenRef.current || await getToken();
      const projectId = String(project.id);
      const res = await fetch(
        `${PORTAL_API_URL}/api/r2-stream?list=annotations/${projectId}/&t=${encodeURIComponent(token)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      const cached = readAnnoNameCache();
      setAnnoLayers((data.layers || []).map(l => ({ ...l, name: cached[l.key] || l.name })));
    } catch (_) {}
  };

  // ── Load an annotation layer from R2 ─────────────────────────────────────
  const loadAnnoLayer = async (key) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed() || !dbRef.current) return;
    setAnnoLayerStates(prev => ({ ...prev, [key]: "loading" }));
    try {
      const token    = authTokenRef.current || await getToken();
      const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}&_ts=${Date.now()}`;
      const conn     = await dbRef.current.connect();
      const result   = await conn.query(`SELECT id, type, geometry, label, color FROM read_parquet('${proxyUrl}')`);
      await conn.close();

      const rows        = result.toArray();
      const metaRow     = rows.find(r => r.id === "__meta__");
      const displayName = metaRow?.label?.trim() || null;
      const features    = rows.filter(r => r.id !== "__meta__").map(r => ({
        id:       r.id,
        type:     r.type,
        vertices: wktToVertices(r.geometry),
        label:    r.label || "",
        color:    r.color || "#3b82f6",
      }));
      annoFeaturesRef.current[key] = features;
      if (displayName) {
        cacheAnnoName(key, displayName);
        setAnnoLayers(prev => prev.map(l => l.key === key ? { ...l, name: displayName } : l));
      }

      // Render
      const entities = [];
      features.forEach(f => {
        const ents = renderDrawFeature(f);
        featureEntityMapRef.current[f.id] = ents;
        entities.push(...ents);
        renderVertexDots(f, key);
      });
      annoEntitiesRef.current[key] = entities;
      setAnnoLayerStates(prev => ({ ...prev, [key]: "loaded" }));

      // Fly to layer extent (same pattern as regular point layers)
      const allVerts = features.flatMap(f => f.vertices);
      if (allVerts.length > 0) {
        const lons   = allVerts.map(v => v.lon);
        const lats   = allVerts.map(v => v.lat);
        const minLon = Math.min(...lons), maxLon = Math.max(...lons);
        const minLat = Math.min(...lats), maxLat = Math.max(...lats);
        const center = Cesium.Cartesian3.fromDegrees((minLon + maxLon) / 2, (minLat + maxLat) / 2);
        const corner = Cesium.Cartesian3.fromDegrees(minLon, minLat);
        const radius = Math.max(Cesium.Cartesian3.distance(center, corner), 500);
        viewer.camera.flyToBoundingSphere(
          new Cesium.BoundingSphere(center, radius),
          { duration: 1.5, offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, 0) }
        );
      }
    } catch (e) {
      console.error("Load annotation layer failed:", e);
      setAnnoLayerStates(prev => ({ ...prev, [key]: "error" }));
    }
  };

  const unloadAnnoLayer = (key) => {
    const viewer = viewerRef.current;
    const entities = annoEntitiesRef.current[key] || [];
    if (viewer && !viewer.isDestroyed()) {
      entities.forEach(e => { try { viewer.entities.remove(e); } catch (_) {} });
    }
    (annoFeaturesRef.current[key] || []).forEach(f => clearVertexDots(f.id));
    delete annoEntitiesRef.current[key];
    delete annoFeaturesRef.current[key];
    setAnnoLayerStates(prev => ({ ...prev, [key]: "idle" }));
    setAnnoEditKey(prev => prev === key ? null : prev);
  };

  const deleteAnnoLayer = async (key) => {
    unloadAnnoLayer(key);
    evictAnnoName(key);
    try {
      const token = authTokenRef.current || await getToken();
      await fetch(
        `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`,
        { method: "DELETE" }
      );
    } catch (_) {}
    setAnnoLayers(prev => prev.filter(l => l.key !== key));
  };

  // ── Annotation layer editing ──────────────────────────────────────────────

  // Called live while picker is dragging — updates Cesium entities in-place, no React re-render
  const updateAnnoFeatureColorInPlace = (featureId, newColor) => {
    const Cesium = window.Cesium;
    if (!Cesium) return;
    const key = annoEditKeyRef.current;
    if (!key) return;
    // Sync the ref so auto-save uses the latest color
    const features = annoFeaturesRef.current[key] || [];
    annoFeaturesRef.current[key] = features.map(f => f.id === featureId ? { ...f, color: newColor } : f);
    // Update polyline / polygon materials in-place
    const cesColor = Cesium.Color.fromCssColorString(newColor);
    (featureEntityMapRef.current[featureId] || []).forEach(e => {
      if (e.polyline) e.polyline.material = new Cesium.PolylineDashMaterialProperty({
        color: cesColor, gapColor: Cesium.Color.BLACK.withAlpha(0.5), dashLength: 20,
      });
      if (e.polygon) e.polygon.material = new Cesium.ColorMaterialProperty(cesColor.withAlpha(0.22));
    });
    // Update vertex dot outline colour in-place
    (vertexDotsMapRef.current[featureId] || []).forEach(dot => {
      if (dot.point) dot.point.outlineColor = cesColor;
    });
  };

  // Called when picker closes — commits final color to React state + schedules save
  const updateAnnoEditFeatureColor = (featureId, color) => {
    updateAnnoFeatureColorInPlace(featureId, color);
    const key = annoEditKeyRef.current;
    if (!key) return;
    const updated = annoFeaturesRef.current[key]; // already patched by updateAnnoFeatureColorInPlace
    setAnnoEditFeatures([...updated]);
    scheduleAutoSave(key);
  };

  const updateAnnoEditFeatureLabel = (featureId, label) => {
    const updated = annoEditFeatures.map(f => f.id === featureId ? { ...f, label } : f);
    setAnnoEditFeatures(updated);
    if (!annoEditKey) return;
    annoFeaturesRef.current[annoEditKey] = updated;
    scheduleAutoSave(annoEditKey);

    // Try to update the label billboard in-place so vertex dots are not re-rendered.
    // onBlur fires when the user clicks the Cesium canvas to grab a dot, so calling
    // reRenderAnnoLayer here would destroy the entity activeVertexDragRef is holding.
    const oldFeature = annoEditFeatures.find(f => f.id === featureId);
    const newHasLabel = !!(label?.trim());
    if (newHasLabel) {
      const labelEntity = (featureEntityMapRef.current[featureId] || []).find(e => e.billboard);
      if (labelEntity) {
        const bb = makeLabelBillboard(label.trim(), { pixelOffsetY: oldFeature?.type === "line" ? -14 : 0 });
        labelEntity.billboard.image  = bb.image;
        labelEntity.billboard.width  = bb.width;
        labelEntity.billboard.height = bb.height;
        return;
      }
    }
    // Label added to an entity that had none, or label cleared — entity count changes, need full re-render
    reRenderAnnoLayer(annoEditKey, updated);
  };

  const deleteAnnoEditFeature = (featureId) => {
    clearVertexDots(featureId);
    const updated = annoEditFeatures.filter(f => f.id !== featureId);
    setAnnoEditFeatures(updated);
    if (annoEditKey) { annoFeaturesRef.current[annoEditKey] = updated; reRenderAnnoLayer(annoEditKey, updated); }
  };

  const saveAnnoLayerChanges = async (key, displayNameOverride) => {
    if (!dbRef.current || !key) return;
    const features    = annoFeaturesRef.current[key] || [];
    const displayName = (displayNameOverride !== undefined ? displayNameOverride : annoEditLayerNameRef.current)
      || annoLayers.find(l => l.key === key)?.name || "";
    setSavingAnnoEdit(true);
    try {
      const now = new Date().toISOString();
      if (displayName) cacheAnnoName(key, displayName);
      setAnnoLayers(prev => prev.map(l => l.key === key ? { ...l, name: displayName || l.name, lastModified: now } : l));

      if (isDemoUser) return; // demo: update local state only, skip R2

      const token = authTokenRef.current || await getToken();
      const conn  = await dbRef.current.connect();
      await conn.query(`DROP TABLE IF EXISTS _anno_tmp`);
      await conn.query(`CREATE TABLE _anno_tmp (
        id VARCHAR, type VARCHAR, geometry VARCHAR, label VARCHAR,
        color VARCHAR, created_by VARCHAR, created_at VARCHAR
      )`);
      await conn.query(`INSERT INTO _anno_tmp VALUES ('__meta__','meta','','${displayName.replace(/'/g,"''")}','','${userEmail}','${now}')`);
      for (const f of features) {
        const wkt = featureToWKT(f);
        await conn.query(
          `INSERT INTO _anno_tmp VALUES ('${f.id}','${f.type}','${wkt.replace(/'/g,"''")}',
           '${(f.label||"").replace(/'/g,"''")}','${f.color}','${userEmail}','${now}')`
        );
      }
      const outFile = `anno_${Date.now()}.parquet`;
      await conn.query(`COPY _anno_tmp TO '${outFile}' (FORMAT PARQUET)`);
      await conn.close();
      const bytes = await dbRef.current.copyFileToBuffer(outFile);
      const res   = await fetch(
        `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`,
        { method: "PUT", body: bytes, headers: { "Content-Type": "application/octet-stream" } }
      );
      if (!res.ok) throw new Error(await res.text());
    } catch (e) {
      console.error("Save annotation changes failed:", e);
      alert(`Save failed: ${e.message}`);
    } finally {
      setSavingAnnoEdit(false);
    }
  };

  const scheduleAutoSave = (key) => {
    if (!key) return;
    const nameSnapshot = annoEditLayerNameRef.current;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      saveAnnoLayerChanges(key, nameSnapshot);
    }, 800);
  };

  // ── Point Inspector tool ─────────────────────────────────────────────────

  // Keep ref in sync; when switching multi → single, keep last point relabelled P1
  useEffect(() => {
    multiSelectRef.current = multiSelect;
    if (!multiSelect) {
      // Trim selectedPoints to just the last one, renamed P1
      setSelectedPoints(prev => {
        if (!prev.length) return prev;
        return [{ ...prev[prev.length - 1], label: "P1" }];
      });
      // Remove all globe label entities except the last, rename it P1
      const viewer = viewerRef.current;
      if (viewer && !viewer.isDestroyed()) {
        identifyLabelEntitiesRef.current.slice(0, -1).forEach(e => viewer.entities.remove(e));
        const last = identifyLabelEntitiesRef.current[identifyLabelEntitiesRef.current.length - 1];
        if (last?.billboard) last.billboard.image = makeLabelBillboard("P1").image;
      }
      identifyLabelEntitiesRef.current = identifyLabelEntitiesRef.current.slice(-1);
      pointCounterRef.current = 1;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiSelect]);

  const clearIdentifyTool = () => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      identifyLabelEntitiesRef.current.forEach(e => viewer.entities.remove(e));
    }
    identifyLabelEntitiesRef.current = [];
    pointCounterRef.current = 1;
    setSelectedPoints([]);
  };

  const toggleIdentifyMode = () => {
    if (isIdentifyMode) {
      clearIdentifyTool();
      setIsIdentifyMode(false);
    } else {
      deactivateAllTools();
      setIsIdentifyMode(true);
    }
  };

  // Attach / detach the Cesium LEFT_CLICK handler when identify mode changes
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    // Tear down any previous handler
    if (identifyHandlerRef.current && !identifyHandlerRef.current.isDestroyed()) {
      identifyHandlerRef.current.destroy();
      identifyHandlerRef.current = null;
    }
    if (!isIdentifyMode) return;

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction(async (click) => {
      // drillPick searches through all rendered objects at the pixel.
      // scene.pick() alone returns the Google 3D tile mesh on top and never
      // reaches our point primitives underneath it.
      const picks = viewer.scene.drillPick(click.position);
      const picked = picks.find(p => p?.id?.key);
      if (!picked) return;

      const { key, lat, lon } = picked.id;
      // Client point layers are handled by the always-on client-layer click handler
      if (picked.id?.isClientPoint) return;

      // In single-select mode: clear previous label and reset counter
      if (!multiSelectRef.current) {
        identifyLabelEntitiesRef.current.forEach(e => viewer.entities.remove(e));
        identifyLabelEntitiesRef.current = [];
        pointCounterRef.current = 1;
      }

      const label = `P${pointCounterRef.current++}`;

      // Use the primitive's actual rendered Cartesian3 position so the label
      // sits exactly on the point, including any terrain datum offset applied
      // in 3D mode (raw h alone would place it at the wrong elevation).
      const entity = viewer.entities.add({
        position:  picked.primitive.position,
        billboard: makeLabelBillboard(label),
      });
      identifyLabelEntitiesRef.current.push(entity);

      // Re-query R2 for the full row (time series + all properties)
      if (!dbReady || !dbRef.current) return;
      try {
        const schema = layerSchemasRef.current[key];
        if (!schema) return;
        const { latCol, lonCol } = schema;

        const token    = authTokenRef.current || await getToken();
        const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
        const conn     = await dbRef.current.connect();
        const result   = await conn.query(
          `SELECT * FROM read_parquet('${proxyUrl}')
           WHERE abs("${latCol}" - ${lat}) < 1e-5
             AND abs("${lonCol}" - ${lon}) < 1e-5
           LIMIT 1`
        );
        await conn.close();

        const rows = result.toArray();
        if (!rows.length) return;
        const row = rows[0];

        // Parse D_YYYYMMDD columns into time-series arrays
        const timeSeries = [];
        Object.keys(row).forEach(col => {
          const m = col.match(/^D_(\d{4})(\d{2})(\d{2})$/i);
          if (m) timeSeries.push({ date: `${m[1]}-${m[2]}-${m[3]}`, val: Number(row[col]) });
        });
        timeSeries.sort((a, b) => a.date.localeCompare(b.date));

        const findCol = (...patterns) => Object.keys(row).find(c => patterns.some(p => p.test(c)));
        const velKey    = findCol(/^velocity$/i, /^vel$/i, /^mean_vel$/i, /^los_velocity$/i, /^def_rate$/i);
        const vprecKey  = findCol(/^vprecision$/i);
        const cohKey    = findCol(/^coherence$/i, /^coh$/i);
        const tDispKey  = findCol(/^total_displacement$/i, /^t_displacement$/i, /^cumulative$/i);

        const point = {
          label,
          lat,
          lon,
          time:          timeSeries.map(d => d.date),
          displacement:  timeSeries.map(d => d.val),
          velocity:      velKey    ? Number(row[velKey])    : null,
          vprecision:    vprecKey  ? Number(row[vprecKey])  : null,
          coherence:     cohKey    ? Number(row[cohKey])    : null,
          tDisplacement: tDispKey  ? Number(row[tDispKey])  : null,
          los_dir:       row.los_dir    ?? null,
          type_motn:     row.type_motn  ?? null,
          alert:         row.alert      ?? null,
          showTrendLine: false,
        };

        setSelectedPoints(prev => multiSelectRef.current ? [...prev, point] : [point]);
      } catch (e) {
        console.error("Point identify failed:", e);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    identifyHandlerRef.current = handler;
    return () => { if (!handler.isDestroyed()) handler.destroy(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIdentifyMode, cesiumLoaded]);

  // ── Vertex drag handler — set up once when Cesium loads ──────────────────
  useEffect(() => {
    if (!cesiumLoaded) return;
    setupVertexDragHandler();
    return () => {
      if (vertexDragHandlerRef.current && !vertexDragHandlerRef.current.isDestroyed()) {
        vertexDragHandlerRef.current.destroy();
        vertexDragHandlerRef.current = null;
      }
    };
  }, [cesiumLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vertex dots are rendered in loadAnnoLayer and reRenderAnnoLayer — no extra action on panel open.

  // ── Profile Viewer tool ───────────────────────────────────────────────────

  // Keep layerStates accessible inside async Cesium handlers without stale closure
  useEffect(() => { layerStatesRef.current = layerStates; }, [layerStates]);

  // ── Always-on click handler: client layer feature → property card ────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !cesiumLoaded || !Cesium) return;

    if (clientLayerClickRef.current && !clientLayerClickRef.current.isDestroyed()) {
      clientLayerClickRef.current.destroy();
    }

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((click) => {
      // Check PointPrimitive client point layers
      const picks = viewer.scene.drillPick(click.position);
      const clientPick = picks.find(p => p?.id?.isClientPoint);
      if (clientPick) {
        const { key, lat, lon } = clientPick.id;
        const propsMap = clientLayerPropsRef.current[key];
        const props    = propsMap?.get(`${lat.toFixed(6)}_${lon.toFixed(6)}`);
        const cl       = clientLayers.find(c => c.key === key);
        setClientFeatureCard({ name: cl?.name || key, lat, lon, props: props || {} });
        return;
      }
      // Check GeoJSON DataSource entity clicks
      const entityPick = picks.find(p => p?.id?._clientLayerKey);
      if (entityPick) {
        const entity = entityPick.id;
        const key    = entity._clientLayerKey;
        const cl     = clientLayers.find(c => c.key === key);
        const raw    = entity.properties?.getValue?.(viewer.clock.currentTime) || {};
        const props  = {};
        for (const [k, v] of Object.entries(raw)) {
          if (v != null && String(v).trim()) props[k] = String(v);
        }
        setClientFeatureCard({ name: cl?.name || key, props });
        return;
      }
      // Click outside any client feature — dismiss card
      if (clientFeatureCard) setClientFeatureCard(null);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    clientLayerClickRef.current = handler;
    return () => { if (!handler.isDestroyed()) handler.destroy(); };
  }, [cesiumLoaded, clientLayers, clientFeatureCard]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { drawFeaturesRef.current = drawFeatures; }, [drawFeatures]);
  useEffect(() => { annoEditKeyRef.current = annoEditKey; }, [annoEditKey]);
  useEffect(() => { isAreaModeRef.current = isAreaMode; }, [isAreaMode]);
  const isDistanceModeRef = useRef(false);
  useEffect(() => { isDistanceModeRef.current = isDistanceMode; }, [isDistanceMode]);
  useEffect(() => { annoEditLayerNameRef.current = annoEditLayerName; }, [annoEditLayerName]);

  useEffect(() => { surfaceAxisModeRef.current = surfaceAxisMode; }, [surfaceAxisMode]);


  // Surface position for a profile sample, so the hover dot rides the draped
  // line. Two stages, re-computed every hover (no cache — tile refinements
  // after the line was drawn must be picked up):
  //  1. sampleHeight lifts the sample onto the loaded 3D tile mesh.
  //  2. pickPosition at that point's own screen pixel refines to the RENDERED
  //     surface — exactly what the draped line is drawn on — since the loaded
  //     LOD sampleHeight hits can differ from the rendered one. A distance
  //     guard rejects the refinement when a foreground ridge occludes the
  //     pixel, which would otherwise teleport the dot onto the occluder.
  const sectionSurfacePos = (s) => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || viewer.isDestroyed() || !Cesium) return s.pos;
    let pos = s.pos;
    if (viewer.scene.sampleHeightSupported) {
      let h;
      try { h = viewer.scene.sampleHeight(Cesium.Cartographic.fromDegrees(s.lng, s.lat), [...sectionEntitiesRef.current]); } catch (_) {}
      if (Number.isFinite(h)) pos = Cesium.Cartesian3.fromDegrees(s.lng, s.lat, h);
    }
    if (pos) {
      const win = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, pos);
      const refined = win && viewer.scene.pickPosition(win);
      if (Cesium.defined(refined)) {
        // Accept only same-spot corrections (any vertical delta — LOD or
        // canopy differences can exceed 30 m); reject when the pixel resolves
        // to a different location, i.e. an occluding foreground ridge.
        const a = Cesium.Cartographic.fromCartesian(refined);
        const b = Cesium.Cartographic.fromCartesian(pos);
        if (Math.abs(a.latitude - b.latitude) < 2e-5 && Math.abs(a.longitude - b.longitude) < 2e-5) return refined;
      }
    }
    return pos;
  };

  const clearSectionTool = () => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      sectionEntitiesRef.current.forEach(e => viewer.entities.remove(e));
    }
    sectionEntitiesRef.current   = [];
    sectionStepRef.current       = 0;
    sectionStartRef.current      = null;
    sectionStartPosRef.current   = null;
    sectionPreviewPosRef.current = [];
    sectionSamplesRef.current      = [];
    sectionRawCacheRef.current     = {};
    sectionHoverEntityRef.current  = null;
    sectionHoveredIndexRef.current = null;
    setSectionProfileData([]);
    setSectionHoveredIndex(null);
    setSectionEpochs([]);
    setSectionSelectedEpoch(null);
    setSectionEpochsLoading(false);
  };


  const toggleSectionMode = () => {
    if (isSectionMode) {
      clearSectionTool();
      setIsSectionMode(false);
    } else {
      deactivateAllTools();
      setIsSectionMode(true);
    }
  };

  const clearPolygonTool = () => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      polygonEntitiesRef.current.forEach(e => viewer.entities.remove(e));
      if (surfaceOverlayPrimRef.current) {
        try { viewer.scene.primitives.remove(surfaceOverlayPrimRef.current); } catch (_) {}
        surfaceOverlayPrimRef.current = null;
      }
    }
    polygonEntitiesRef.current        = [];
    polygonFillEntityRef.current      = null;
    polygonCornerEntitiesRef.current  = [];
    polygonStepRef.current            = 0;
    polygonStartRef.current           = null;
    polygonBboxRef.current            = null;
    setSurfaceAxisMode("distance");
    surfaceAxisModeRef.current        = "distance";
    surfaceRawCacheRef.current   = {};
    setSurfaceData([]);
    surfaceDataRef.current = [];
    setSurfaceLabel("Deformation");
    setSurfaceEpochs([]);
    setSurfaceSelectedEpoch(null);
    setSurfaceEpochsLoading(false);
    setSurfaceIdwPower(2);
    surfaceIdwPowerRef.current = 2;
    setSurfaceOpacity(0.82);
    surfaceOpacityRef.current = 0.82;
    setSurfacePointsOpacity(1.0);
    setSurfaceDiffEpochA(null);
    setSurfaceDiffEpochB(null);
    surfaceDiffRef.current = { a: null, b: null };
  };

  const clearAreaTool = () => {
    const viewer = viewerRef.current;
    clearVertexDots("__area__");
    areaIsClosedRef.current = false;
    if (viewer && !viewer.isDestroyed()) {
      areaEntitiesRef.current.forEach(e => {
        try { viewer.entities.remove(e); } catch (_) {}
      });
    }
    areaEntitiesRef.current = [];
    areaVerticesRef.current = [];
    setAreaResult(null);
  };

  const toggleAreaMode = () => {
    if (isAreaMode) {
      clearAreaTool();
      setIsAreaMode(false);
    } else {
      deactivateAllTools();
      setIsAreaMode(true);
    }
  };

  const clearDistanceTool = () => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      distanceEntitiesRef.current.forEach(e => {
        if (e._col) { try { viewer.scene.primitives.remove(e._col); } catch (_) {} }
        else        { try { viewer.entities.remove(e); } catch (_) {} }
      });
    }
    distanceEntitiesRef.current = [];
    distanceP1Ref.current = null;
    setDistanceStep(0);
    setDistanceResult(null);
  };

  const deactivateAllTools = () => {
    clearIdentifyTool();   setIsIdentifyMode(false);
    clearSectionTool();    setIsSectionMode(false);
    clearPolygonTool();    setIsPolygonMode(false);
    clearAreaTool();       setIsAreaMode(false);
    clearDistanceTool();   setIsDistanceMode(false);
    stopDrawTool();
    setAnnoEditKey(null);
  };

  const toggleDistanceMode = () => {
    if (isDistanceMode) {
      clearDistanceTool();
      setIsDistanceMode(false);
    } else {
      deactivateAllTools();
      setIsDistanceMode(true);
    }
  };


  const togglePolygonMode = () => {
    if (isPolygonMode) {
      clearPolygonTool();
      setIsPolygonMode(false);
    } else {
      deactivateAllTools();
      setIsPolygonMode(true);
    }
  };

  // ── Sync refs for surface modeler ──────────────────────────────────────────
  useEffect(() => { surfaceIdwPowerRef.current = surfaceIdwPower; }, [surfaceIdwPower]);
  useEffect(() => { surfaceOpacityRef.current = surfaceOpacity; }, [surfaceOpacity]);
  useEffect(() => { surfaceDataRef.current = surfaceData; }, [surfaceData]);
  useEffect(() => { surfaceDiffRef.current = { a: surfaceDiffEpochA, b: surfaceDiffEpochB }; }, [surfaceDiffEpochA, surfaceDiffEpochB]);

  // ── PS points opacity — update alpha on all loaded point collections ────────
  useEffect(() => {
    for (const coll of Object.values(collectionsRef.current)) {
      for (let i = 0; i < coll.length; i++) {
        const pt = coll.get(i);
        const base = pt.color.clone();
        pt.color = base.withAlpha(surfacePointsOpacity);
      }
    }
  }, [surfacePointsOpacity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Surface overlay — GroundPrimitive draped on terrain + 3D tiles ──────────
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    if (surfaceOverlayPrimRef.current) {
      try { viewer.scene.primitives.remove(surfaceOverlayPrimRef.current); } catch (_) {}
      surfaceOverlayPrimRef.current = null;
    }
    if (polygonFillEntityRef.current) polygonFillEntityRef.current.show = true;

    if (!surfaceData.length || !polygonBboxRef.current) return;

    let points;
    if (surfaceDiffEpochA && surfaceDiffEpochB) {
      points = surfaceData.map(p => {
        const va = p.epochs?.[surfaceDiffEpochA];
        const vb = p.epochs?.[surfaceDiffEpochB];
        return (va != null && vb != null && isFinite(va) && isFinite(vb))
          ? { lat: p.lat, lon: p.lon, v: vb - va } : null;
      }).filter(Boolean);
    } else {
      points = surfaceData.map(p => {
        const v = (surfaceSelectedEpoch && p.epochs) ? p.epochs[surfaceSelectedEpoch] : p.value;
        return (v != null && isFinite(v)) ? { lat: p.lat, lon: p.lon, v } : null;
      }).filter(Boolean);
    }
    if (points.length < 4) return;

    const { minLat, maxLat, minLon, maxLon } = polygonBboxRef.current;
    const loadedEntry = Object.entries(layerStates).find(([, s]) => s.status === "loaded");
    const hexStops = loadedEntry?.[1]?.colorStops || DEFAULT_PALETTE.stops;

    let cancelled = false;
    try {
      // Smooth textured surface where supported; flat-tile grid as fallback.
      // The texture path affords a finer 96 value grid; the instance-based
      // fallback stays at 48 to cap geometry count.
      const smooth = Cesium.GroundPrimitive.supportsMaterials(viewer.scene);
      const addPrim = (prim) => {
        if (!prim || cancelled || viewer.isDestroyed()) return;
        viewer.scene.primitives.add(prim);
        surfaceOverlayPrimRef.current = prim;
        if (polygonFillEntityRef.current) polygonFillEntityRef.current.show = false;
      };

      // Small selections build in tens of ms — synchronous, no indicator
      if (!smooth || points.length <= 20000) {
        const build = smooth ? buildDeformationSurface : buildDeformationCells;
        addPrim(build(Cesium, points, minLat, maxLat, minLon, maxLon, hexStops, smooth ? 96 : 48, surfaceIdwPower, surfaceOpacity));
      } else {
        // Large build: chunked async with a % indicator centred on the
        // rectangle (screen position of the bbox centre on the tile mesh)
        const cLon = (minLon + maxLon) / 2, cLat = (minLat + maxLat) / 2;
        let cAlt = 0;
        try {
          const h = viewer.scene.sampleHeight(Cesium.Cartographic.fromDegrees(cLon, cLat));
          if (Number.isFinite(h)) cAlt = h;
        } catch (_) {}
        const centerCart = Cesium.Cartesian3.fromDegrees(cLon, cLat, cAlt);
        const centerWin = () => {
          try { return Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, centerCart); }
          catch (_) { return null; }
        };
        const w0 = centerWin();
        setSurfaceBuildProgress({ x: w0?.x ?? null, y: w0?.y ?? null, pct: 0 });
        (async () => {
          const prim = await buildDeformationSurfaceAsync(
            Cesium, points, minLat, maxLat, minLon, maxLon, hexStops, 96, surfaceIdwPower, surfaceOpacity,
            (f) => {
              if (cancelled) return;
              const w = centerWin();
              setSurfaceBuildProgress({ x: w?.x ?? null, y: w?.y ?? null, pct: Math.round(f * 100) });
            },
            () => cancelled
          );
          setSurfaceBuildProgress(null);
          addPrim(prim);
        })();
      }
    } catch (_) {}
    return () => { cancelled = true; setSurfaceBuildProgress(null); };
  }, [surfaceData, surfaceSelectedEpoch, cesiumLoaded, layerStates, surfaceIdwPower, surfaceDiffEpochA, surfaceDiffEpochB, surfaceOpacity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── _buildOverlayForPoints helper (used by export callbacks) ────────────────
  const _buildOverlayForPoints = useCallback((viewer, Cesium, pts) => {
    if (!polygonBboxRef.current || pts.length < 4) return;
    const { minLat, maxLat, minLon, maxLon } = polygonBboxRef.current;
    const entry = Object.entries(layerStatesRef.current).find(([, s]) => s.status === "loaded");
    const hexStops = entry?.[1]?.colorStops || DEFAULT_PALETTE.stops;
    if (surfaceOverlayPrimRef.current) {
      try { viewer.scene.primitives.remove(surfaceOverlayPrimRef.current); } catch (_) {}
      surfaceOverlayPrimRef.current = null;
    }
    const smooth = Cesium.GroundPrimitive.supportsMaterials(viewer.scene);
    const build = smooth ? buildDeformationSurface : buildDeformationCells;
    const prim = build(Cesium, pts, minLat, maxLat, minLon, maxLon, hexStops, smooth ? 96 : 48, surfaceIdwPowerRef.current, surfaceOpacityRef.current);
    viewer.scene.primitives.add(prim);
    surfaceOverlayPrimRef.current = prim;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _captureFrame = useCallback(async (viewer) => {
    viewer.scene.render();
    await new Promise(r => setTimeout(r, 80));
    viewer.scene.render();
    return viewer.canvas.toDataURL("image/png");
  }, []);

  const _loadImg = src => new Promise(res => { const i = new Image(); i.onload = () => res(i); i.src = src; });

  const _saveFile = async (blob, suggestedName) => {
    if (typeof window.showSaveFilePicker === "function") {
      const ext = suggestedName.split(".").pop().toLowerCase();
      const mimeType = ext === "gif" ? "image/gif" : "image/png";
      const description = ext === "gif" ? "GIF Animation" : "PNG Image";
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [{ description, accept: { [mimeType]: ["." + ext] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (e) { if (e.name === "AbortError") return; }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = suggestedName; a.click();
    URL.revokeObjectURL(url);
  };

  const _fmtEpochKey = k => k ? k.replace(/^D_(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3") : k;

  // ── GIF export — iterates epochs, rebuilds overlay per frame ────────────────
  const handleSurfaceGifExport = useCallback(async ({ epochs, filename, title, speed, dpi, onProgress }) => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !polygonBboxRef.current || !surfaceDataRef.current.length) return;
    const Cesium = window.Cesium;
    const canvas = viewer.canvas;
    const W = canvas.width, H = canvas.height;
    const delay = Math.max(50, speed || 300);
    const scale = (dpi || 96) / 96;

    const gif = new GIF({
      workers: 2, quality: 6, width: W, height: H,
      workerScript: `${window.location.origin}/gif.worker.js`,
    });

    for (let ei = 0; ei < epochs.length; ei++) {
      const ep = epochs[ei];
      const pts = surfaceDataRef.current.map(p => {
        const v = p.epochs?.[ep];
        return (v != null && isFinite(v)) ? { lat: p.lat, lon: p.lon, v } : null;
      }).filter(Boolean);
      if (pts.length < 4) { onProgress?.(Math.round(((ei + 1) / epochs.length) * 100)); continue; }

      _buildOverlayForPoints(viewer, Cesium, pts);
      const dataUrl = await _captureFrame(viewer);
      const img = await _loadImg(dataUrl);

      const offscreen = document.createElement("canvas");
      offscreen.width = W; offscreen.height = H;
      const ctx = offscreen.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const label = _fmtEpochKey(ep);
      ctx.font = `bold ${Math.round(14 * scale)}px sans-serif`;
      const lw = ctx.measureText(label).width + 16;
      ctx.fillStyle = "rgba(17,24,39,0.75)";
      ctx.fillRect(8, H - 34, lw, 24);
      ctx.fillStyle = "#fff";
      ctx.fillText(label, 16, H - 17);

      if (title) {
        ctx.font = `bold ${Math.round(13 * scale)}px sans-serif`;
        const tw = ctx.measureText(title).width + 16;
        ctx.fillStyle = "rgba(17,24,39,0.75)";
        ctx.fillRect((W - tw) / 2, 8, tw, 24);
        ctx.fillStyle = "#fff";
        ctx.fillText(title, (W - tw) / 2 + 8, 26);
      }

      gif.addFrame(ctx, { copy: true, delay });
      onProgress?.(Math.round(((ei + 1) / epochs.length) * 100));
      await new Promise(r => setTimeout(r, 0));
    }

    // Restore overlay for current epoch
    const curEp = surfaceSelectedEpoch;
    const curPts = surfaceDataRef.current.map(p => {
      const v = (curEp && p.epochs) ? p.epochs[curEp] : p.value;
      return (v != null && isFinite(v)) ? { lat: p.lat, lon: p.lon, v } : null;
    }).filter(Boolean);
    if (curPts.length >= 4) _buildOverlayForPoints(viewer, Cesium, curPts);

    return new Promise((resolve, reject) => {
      gif.on("finished", blob => resolve(blob));
      gif.on("error", reject);
      gif.render();
    });
  }, [_buildOverlayForPoints, _captureFrame, surfaceSelectedEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Diff PNG export — builds diff overlay, captures Cesium canvas ────────────
  const handleSurfaceDiffPngExport = useCallback(async ({ filename, title, showStats, dpi, epochA, epochB }) => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !polygonBboxRef.current || !surfaceDataRef.current.length) return;
    const Cesium = window.Cesium;

    const pts = surfaceDataRef.current.map(p => {
      const va = p.epochs?.[epochA];
      const vb = p.epochs?.[epochB];
      return (va != null && vb != null && isFinite(va) && isFinite(vb))
        ? { lat: p.lat, lon: p.lon, v: vb - va } : null;
    }).filter(Boolean);

    if (pts.length >= 4) _buildOverlayForPoints(viewer, Cesium, pts);

    const dataUrl = await _captureFrame(viewer);
    const img = await _loadImg(dataUrl);

    const W = viewer.canvas.width, H = viewer.canvas.height;
    const scale = (dpi || 96) / 96;
    const TITLE_H = title ? Math.round(28 * scale) : 0;
    const STATS_H = showStats && pts.length ? Math.round(28 * scale) : 0;
    const TOTAL_H = TITLE_H + H + STATS_H;

    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = TOTAL_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, TOTAL_H);

    if (title) {
      ctx.fillStyle = "#111827";
      ctx.font = `bold ${Math.round(W * 0.018)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(title, W / 2, TITLE_H - 8);
      ctx.textAlign = "left";
    }
    ctx.drawImage(img, 0, TITLE_H);
    if (STATS_H) {
      const dzs = pts.map(p => p.v);
      const dAvg = dzs.reduce((s, v) => s + v, 0) / dzs.length;
      const [dMin, dMax] = _arrMinMax(dzs);
      const stats = `Avg Δ: ${dAvg.toFixed(2)} mm  ·  Min Δ: ${dMin.toFixed(2)} mm  ·  Max Δ: ${dMax.toFixed(2)} mm  ·  ${_fmtEpochKey(epochA)} → ${_fmtEpochKey(epochB)}`;
      ctx.fillStyle = "#f9fafb";
      ctx.fillRect(0, TITLE_H + H, W, STATS_H);
      ctx.fillStyle = "#6b7280";
      ctx.font = `${Math.round(10 * scale)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(stats, W / 2, TITLE_H + H + Math.round(18 * scale));
      ctx.textAlign = "left";
    }

    await new Promise(resolve => {
      canvas.toBlob(async blob => {
        await _saveFile(blob, `${(filename || "surface-diff").replace(/\.png$/i, "")}.png`);
        resolve();
      }, "image/png");
    });
  }, [_buildOverlayForPoints, _captureFrame]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load historical epochs on demand ──────────────────────────────────────
  const loadSurfaceEpochs = async (intervalDays = 180) => {
    if (!dbReady || !dbRef.current || !polygonBboxRef.current) return;

    const loadedKeys = Object.entries(layerStatesRef.current)
      .filter(([, s]) => s.status === "loaded").map(([k]) => k);

    setSurfaceEpochsLoading(true);
    await new Promise(r => setTimeout(r, 0));

    const { minLat, maxLat, minLon, maxLon } = polygonBboxRef.current;
    const allPoints = [];
    const collectedEpochs = new Set();

    for (const key of loadedKeys) {
      const cached = surfaceRawCacheRef.current[key];

      if (cached) {
        const dCols = subsampleEpochs(cached.allDCols, intervalDays);
        const lastCol = cached.allDCols[cached.allDCols.length - 1];
        cached.points.forEach(p => {
          const epochs = {};
          dCols.forEach(col => { if (p.allEpochs[col] != null) epochs[col] = p.allEpochs[col]; });
          if (Object.keys(epochs).length) allPoints.push({ lat: p.lat, lon: p.lon, epochs, value: p.allEpochs[lastCol] ?? null });
        });
        dCols.forEach(col => collectedEpochs.add(col));
        continue;
      }

      const rawCached = layerRawDataRef.current[key];
      if (!rawCached) continue;
      const { latCol, lonCol } = rawCached;
      const allDCols = layerStatesRef.current[key]?.dCols || [];
      if (!allDCols.length) continue;

      let rawPoints;
      try {
        const token    = authTokenRef.current || await getToken();
        const proxyUrl = `${PORTAL_API_URL}/api/r2-stream?key=${encodeURIComponent(key)}&t=${encodeURIComponent(token)}`;
        const conn     = await dbRef.current.connect();
        const epochCols = allDCols.map(c => `"${c}"`).join(", ");
        const result    = await conn.query(
          `SELECT "${latCol}", "${lonCol}", ${epochCols}
           FROM read_parquet('${proxyUrl}')
           WHERE "${latCol}" BETWEEN ${minLat} AND ${maxLat}
             AND "${lonCol}" BETWEEN ${minLon} AND ${maxLon}`
        );
        await conn.close();
        rawPoints = result.toArray().map(row => {
          const la = Number(row[latCol]), lo = Number(row[lonCol]);
          const allEpochs = {};
          allDCols.forEach(col => { const v = Number(row[col]); if (isFinite(v)) allEpochs[col] = v; });
          return { lat: la, lon: lo, allEpochs };
        }).filter(p => Object.keys(p.allEpochs).length > 0);
      } catch (e) {
        console.error(`Surface epoch fetch failed (${key}):`, e);
        continue;
      }

      surfaceRawCacheRef.current[key] = { allDCols, points: rawPoints };
      try {
        const dCols = subsampleEpochs(allDCols, intervalDays);
        const lastCol = allDCols[allDCols.length - 1];
        rawPoints.forEach(p => {
          const epochs = {};
          dCols.forEach(col => { if (p.allEpochs[col] != null) epochs[col] = p.allEpochs[col]; });
          if (Object.keys(epochs).length) allPoints.push({ lat: p.lat, lon: p.lon, epochs, value: p.allEpochs[lastCol] ?? null });
        });
        dCols.forEach(col => collectedEpochs.add(col));
      } catch (e) {
        console.error(`Surface epoch query failed (${key}):`, e);
      }
    }

    const epochKeys = [...collectedEpochs].sort();
    setSurfaceData(allPoints);
    surfaceDataRef.current = allPoints;
    setSurfaceEpochs(epochKeys);
    setSurfaceSelectedEpoch(epochKeys.length ? epochKeys[epochKeys.length - 1] : null);
    setSurfaceEpochsLoading(false);
  };

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    // Tear down previous handlers
    if (sectionClickHandlerRef.current && !sectionClickHandlerRef.current.isDestroyed()) {
      sectionClickHandlerRef.current.destroy(); sectionClickHandlerRef.current = null;
    }
    if (sectionMoveHandlerRef.current && !sectionMoveHandlerRef.current.isDestroyed()) {
      sectionMoveHandlerRef.current.destroy(); sectionMoveHandlerRef.current = null;
    }
    if (!isSectionMode) return;

    // Cursor → 3D tile surface point (same chain as area/distance tools):
    // pickPosition (depth buffer) → pickFromRay against the loaded tile mesh →
    // pickEllipsoid last resort with its height lifted to the tile mesh.
    // Own entities are excluded so labels/lines never feed positions back.
    const pickSurface = (screenPos) => {
      let cart = viewer.scene.pickPosition(screenPos);
      if (!Cesium.defined(cart)) {
        try {
          const ray = viewer.camera.getPickRay(screenPos);
          const hit = ray && viewer.scene.pickFromRay(ray, [...sectionEntitiesRef.current]);
          if (hit && Cesium.defined(hit.position)) cart = hit.position;
        } catch (_) {}
      }
      if (!Cesium.defined(cart)) {
        cart = viewer.camera.pickEllipsoid(screenPos, Cesium.Ellipsoid.WGS84);
        if (!Cesium.defined(cart)) return null;
        if (viewer.scene.sampleHeightSupported) {
          const c0 = Cesium.Cartographic.fromCartesian(cart);
          let h;
          try { h = viewer.scene.sampleHeight(c0, [...sectionEntitiesRef.current]); } catch (_) {}
          if (Number.isFinite(h)) cart = Cesium.Cartesian3.fromRadians(c0.longitude, c0.latitude, h);
        }
      }
      return cart;
    };

    // Dashed preview line driven by CallbackProperty
    const previewEntity = viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => sectionPreviewPosRef.current, false),
        width: 3,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.WHITE,
          gapColor: new Cesium.Color(0, 0, 0, 0.5),
          dashLength: 20,
        }),
        clampToGround: true,
      },
    });
    sectionEntitiesRef.current.push(previewEntity);

    // Mouse move — update preview line (step 1) OR hover highlight (step 2)
    const moveHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    moveHandler.setInputAction((movement) => {
      const cart = pickSurface(movement.endPosition);
      if (!cart) return;
      const c   = Cesium.Cartographic.fromCartesian(cart);
      const mLat = Cesium.Math.toDegrees(c.latitude);
      const mLon = Cesium.Math.toDegrees(c.longitude);

      if (sectionStepRef.current === 1 && sectionStartRef.current) {
        sectionPreviewPosRef.current = [
          sectionStartPosRef.current || Cesium.Cartesian3.fromDegrees(sectionStartRef.current.lon, sectionStartRef.current.lat),
          cart,
        ];
      } else if (sectionStepRef.current === 2 && sectionSamplesRef.current.length > 0) {
        // Find nearest sample to mouse → update chart + globe hover dot
        let bestIdx = null, bestD = Infinity;
        sectionSamplesRef.current.forEach((s, i) => {
          const d = (s.lat - mLat) ** 2 + (s.lng - mLon) ** 2;
          if (d < bestD) { bestD = d; bestIdx = i; }
        });
        const newIdx = bestD < 0.05 ** 2 ? bestIdx : null;
        if (newIdx !== sectionHoveredIndexRef.current) {
          sectionHoveredIndexRef.current = newIdx;
          setSectionHoveredIndex(newIdx);
          if (sectionHoverEntityRef.current) {
            sectionHoverEntityRef.current.show = newIdx !== null;
            if (newIdx !== null) {
              const pos = sectionSurfacePos(sectionSamplesRef.current[newIdx]);
              if (pos) {
                sectionHoverEntityRef.current.show = true;
                sectionHoverEntityRef.current.position = new Cesium.ConstantPositionProperty(pos);
              } else {
                sectionHoverEntityRef.current.show = false;
              }
            }
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    sectionMoveHandlerRef.current = moveHandler;

    // Click handler
    const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandler.setInputAction((click) => {
      const cart = pickSurface(click.position);
      if (!cart) return;
      const c   = Cesium.Cartographic.fromCartesian(cart);
      const lon = Cesium.Math.toDegrees(c.longitude);
      const lat = Cesium.Math.toDegrees(c.latitude);

      if (sectionStepRef.current === 2) {
        // ── Re-click after complete line: clear and start fresh ──────────────
        const viewer2 = viewerRef.current;
        if (viewer2 && !viewer2.isDestroyed()) {
          // Skip previewEntity — it lives for the whole tool session
          sectionEntitiesRef.current.forEach(e => { if (e !== previewEntity) viewer2.entities.remove(e); });
        }
        sectionEntitiesRef.current   = [previewEntity]; // keep the live preview entity
        sectionSamplesRef.current    = [];
        sectionHoverEntityRef.current = null;
        sectionHoveredIndexRef.current = null;
        setSectionProfileData([]);
        setSectionHoveredIndex(null);
        setSectionEpochs([]);
        setSectionSelectedEpoch(null);
        setSectionEpochsLoading(false);
        // Fall through to step 0 logic
        sectionStepRef.current = 0;
      }

      if (sectionStepRef.current === 0) {
        // ── First click: place Start label ──────────────────────────────────
        sectionStartRef.current  = { lat, lon };
        sectionStepRef.current   = 1;
        const startPos = cart; // pickSurface result — already on the tile mesh
        sectionStartPosRef.current = startPos;
        const e = viewer.entities.add({ position: startPos, billboard: { ...makeLabelBillboard("Start", { pixelOffsetY: 0 }), heightReference: Cesium.HeightReference.NONE } });
        sectionEntitiesRef.current.push(e);

      } else if (sectionStepRef.current === 1) {
        // ── Second click: place End, draw line, query, profile ───────────────
        sectionStepRef.current = 2;  // lock while processing
        const start = sectionStartRef.current;
        sectionPreviewPosRef.current = [];  // hide preview

        const endPos = cart; // pickSurface result — already on the tile mesh
        const endE = viewer.entities.add({ position: endPos, billboard: { ...makeLabelBillboard("End", { pixelOffsetY: 0 }), heightReference: Cesium.HeightReference.NONE } });
        sectionEntitiesRef.current.push(endE);

        const lineE = viewer.entities.add({
          polyline: {
            positions: [
              sectionStartPosRef.current || Cesium.Cartesian3.fromDegrees(start.lon, start.lat),
              endPos,
            ],
            width: 5,
            material: new Cesium.PolylineDashMaterialProperty({
              color: Cesium.Color.WHITE,
              gapColor: new Cesium.Color(0, 0, 0, 0.5),
              dashLength: 20,
            }),
            clampToGround: true,
          },
        });
        sectionEntitiesRef.current.push(lineE);

        // Adaptive sample count: ~1 pt per 50 m, min 30, max 200
        const lineLenKm = haversineKm(start.lat, start.lon, lat, lon);
        const N = Math.min(200, Math.max(30, Math.round(lineLenKm * 20)));
        const sStartPos = sectionStartPosRef.current;
        const samples = Array.from({ length: N }, (_, i) => {
          const t    = i / (N - 1);
          const sLat = start.lat + (lat - start.lat) * t;
          const sLon = start.lon + (lon - start.lon) * t;
          const pos  = new Cesium.Cartesian3();
          Cesium.Cartesian3.lerp(sStartPos, endPos, t, pos);
          return { lat: sLat, lng: sLon, pos, distance: haversineKm(start.lat, start.lon, sLat, sLon), displacement: null, velocity: null };
        });

        // Query each loaded layer — hybrid snap + IDW per sample
        const loadedKeys = Object.entries(layerStatesRef.current)
          .filter(([, s]) => s.status === "loaded").map(([k]) => k);

        // Use in-memory cached rows (loaded by loadLayer) — zero DB/network latency
        if (loadedKeys.length > 0) {
          const minLat = Math.min(start.lat, lat), maxLat = Math.max(start.lat, lat);
          const minLon = Math.min(start.lon, lon), maxLon = Math.max(start.lon, lon);
          const pad = 0.05;
          const SNAP_M = 150, IDW_RADIUS_M = 3000, IDW_POW = 2;

          for (const key of loadedKeys) {
            const layerCached = layerRawDataRef.current[key];
            if (!layerCached) continue;
            const { count: ldCount, lats: ldLats, lons: ldLons, colData: ldColData, colNames: ldColNames } = layerCached;
            if (!ldCount) continue;
            const st = layerStatesRef.current[key];
            const velKey  = ldColNames.find(c => /^(velocity|vel|mean_vel|los_velocity|def_rate)$/i.test(c)) || null;
            const dCols   = st?.dCols || [];
            const dispKey = dCols.length
              ? dCols[dCols.length - 1]
              : (ldColNames.find(c => /^(total_displacement|t_displacement|cumulative)$/i.test(c)) || null);

            const parseEpochMs = col => { const m = col?.match(/^D_(\d{4})(\d{2})(\d{2})$/); return m ? +new Date(+m[1], +m[2]-1, +m[3]) : null; };
            const firstDCol  = (!velKey && dCols.length >= 2) ? dCols[0]                : null;
            const lastDCol   = (!velKey && dCols.length >= 2) ? dCols[dCols.length - 1] : null;
            const epochYears = (firstDCol && lastDCol)
              ? (parseEpochMs(lastDCol) - parseEpochMs(firstDCol)) / (365.25 * 86400000) : 0;

            const velArr   = velKey    ? ldColData.get(velKey)    : null;
            const dispArr  = dispKey   ? ldColData.get(dispKey)   : null;
            const firstArr = firstDCol ? ldColData.get(firstDCol) : null;
            const lastArr  = lastDCol  ? ldColData.get(lastDCol)  : null;

            // Build bbox-filtered index array once; reuse across all samples
            const ptsIdx = [];
            for (let i = 0; i < ldCount; i++) {
              if (ldLats[i] >= minLat - pad && ldLats[i] <= maxLat + pad &&
                  ldLons[i] >= minLon - pad && ldLons[i] <= maxLon + pad) ptsIdx.push(i);
            }

            samples.forEach(s => {
              s.epochs = {};
              let snapBestIdx = -1, snapBestDm = Infinity;
              let vNum = 0, vDen = 0, dNum = 0, dDen = 0;
              for (const pi of ptsIdx) {
                const dm = haversineKm(s.lat, s.lng, ldLats[pi], ldLons[pi]) * 1000;
                if (dm < snapBestDm) { snapBestDm = dm; snapBestIdx = pi; }
                if (dm <= IDW_RADIUS_M) {
                  const w = 1 / Math.pow(Math.max(dm, 1), IDW_POW);
                  if (velArr) {
                    vNum += w * velArr[pi]; vDen += w;
                  } else if (firstArr && lastArr && epochYears > 0) {
                    const v1 = firstArr[pi], v2 = lastArr[pi];
                    if (isFinite(v1) && isFinite(v2)) { vNum += w * (v2 - v1) / epochYears; vDen += w; }
                  }
                  if (dispArr) { dNum += w * dispArr[pi]; dDen += w; }
                }
              }
              if (snapBestIdx >= 0 && snapBestDm <= SNAP_M) {
                if (velArr && s.velocity === null) {
                  s.velocity = velArr[snapBestIdx];
                } else if (!velKey && firstArr && lastArr && epochYears > 0 && s.velocity === null) {
                  const v1 = firstArr[snapBestIdx], v2 = lastArr[snapBestIdx];
                  if (isFinite(v1) && isFinite(v2)) s.velocity = (v2 - v1) / epochYears;
                }
                if (dispArr && s.displacement === null) s.displacement = dispArr[snapBestIdx];
              } else {
                if (s.velocity === null && vDen > 0) s.velocity = vNum / vDen;
                if (dispArr && s.displacement === null && dDen > 0) s.displacement = dNum / dDen;
              }
            });
          }

          interpolateNulls(samples, "displacement");
          interpolateNulls(samples, "velocity");
        }

        // Store for hover detection and create globe hover dot
        sectionSamplesRef.current = samples;
        const hoverE = viewer.entities.add({
          show: false,
          position: Cesium.Cartesian3.fromDegrees(0, 0, 0),
          point: {
            pixelSize: 12,
            color: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 2,
            // Position is supplied on the tile mesh by sectionSurfacePos —
            // CLAMP_TO_GROUND would re-clamp to the offset terrain provider.
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
        sectionHoverEntityRef.current = hoverE;
        sectionEntitiesRef.current.push(hoverE);

        setSectionProfileData(samples);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    sectionClickHandlerRef.current = clickHandler;

    return () => {
      if (!clickHandler.isDestroyed()) clickHandler.destroy();
      if (!moveHandler.isDestroyed())  moveHandler.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionMode, cesiumLoaded]);

  // ── Zone Modeler handlers ────────────────────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    if (polygonClickHandlerRef.current && !polygonClickHandlerRef.current.isDestroyed()) {
      polygonClickHandlerRef.current.destroy(); polygonClickHandlerRef.current = null;
    }
    if (polygonMoveHandlerRef.current && !polygonMoveHandlerRef.current.isDestroyed()) {
      polygonMoveHandlerRef.current.destroy(); polygonMoveHandlerRef.current = null;
    }
    if (!isPolygonMode) return;

    // Cursor → 3D tile surface point (same chain as the other tools):
    // pickPosition (depth buffer) → pickFromRay against the loaded tile mesh →
    // pickEllipsoid last resort. Only lat/lon are consumed here (everything
    // drawn is clamped/classified), so no height lift is needed.
    const pickSurface = (screenPos) => {
      let cart = viewer.scene.pickPosition(screenPos);
      if (!Cesium.defined(cart)) {
        try {
          const ray = viewer.camera.getPickRay(screenPos);
          const exclude = [...polygonEntitiesRef.current];
          if (surfaceOverlayPrimRef.current) exclude.push(surfaceOverlayPrimRef.current);
          const hit = ray && viewer.scene.pickFromRay(ray, exclude);
          if (hit && Cesium.defined(hit.position)) cart = hit.position;
        } catch (_) {}
      }
      return Cesium.defined(cart) ? cart : viewer.camera.pickEllipsoid(screenPos, Cesium.Ellipsoid.WGS84);
    };

    // Helper: build 5-point ring from two corners
    const rectPositions = (lon0, lat0, lon1, lat1) =>
      Cesium.Cartesian3.fromDegreesArray([
        lon0, lat0, lon1, lat0, lon1, lat1, lon0, lat1, lon0, lat0,
      ]);

    // Mutable ref holding current preview corner — read by CallbackProperty each frame
    const previewCornerRef = { current: null };  // { lon, lat }

    // Live preview entity driven by a CallbackProperty — updates every render frame
    const previewOutline = viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          if (polygonStepRef.current !== 1 || !polygonStartRef.current || !previewCornerRef.current) return [];
          const { lon: lon0, lat: lat0 } = polygonStartRef.current;
          const { lon: lon1, lat: lat1 } = previewCornerRef.current;
          return rectPositions(lon0, lat0, lon1, lat1);
        }, false),
        width: 3,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.WHITE,
          gapColor: new Cesium.Color(0, 0, 0, 0.5),
          dashLength: 20,
        }),
        clampToGround: true,
      },
    });
    polygonEntitiesRef.current.push(previewOutline);

    // Mouse move — update previewCornerRef so CallbackProperty picks it up next frame
    const moveHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    moveHandler.setInputAction((movement) => {
      if (polygonStepRef.current !== 1 || !polygonStartRef.current) return;
      const cart = pickSurface(movement.endPosition);
      if (!cart) return;
      const c = Cesium.Cartographic.fromCartesian(cart);
      previewCornerRef.current = {
        lon: Cesium.Math.toDegrees(c.longitude),
        lat: Cesium.Math.toDegrees(c.latitude),
      };
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    polygonMoveHandlerRef.current = moveHandler;

    // Click handler
    const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandler.setInputAction((click) => {
      const cart = pickSurface(click.position);
      if (!cart) return;
      const c   = Cesium.Cartographic.fromCartesian(cart);
      const lon = Cesium.Math.toDegrees(c.longitude);
      const lat = Cesium.Math.toDegrees(c.latitude);

      if (polygonStepRef.current === 2) {
        // Clear previous result and start fresh
        const viewer2 = viewerRef.current;
        if (viewer2 && !viewer2.isDestroyed()) {
          polygonEntitiesRef.current.forEach(e => { if (e !== previewOutline) viewer2.entities.remove(e); });
        }
        polygonEntitiesRef.current = [previewOutline];
        polygonStepRef.current = 0;
        previewCornerRef.current = null;
        surfaceRawCacheRef.current = {};
        setSurfaceData([]);
        setSurfaceEpochs([]);
        setSurfaceSelectedEpoch(null);
      }

      if (polygonStepRef.current === 0) {
        polygonStartRef.current = { lat, lon };
        polygonStepRef.current  = 1;
        return;
      }

      if (polygonStepRef.current === 1) {
        polygonStepRef.current = 2;
        const { lon: lon0, lat: lat0 } = polygonStartRef.current;

        // Hide preview — clear corner ref so CallbackProperty returns []
        previewCornerRef.current = null;

        // Final outline
        const outlineE = viewer.entities.add({
          polyline: {
            positions: rectPositions(lon0, lat0, lon, lat),
            width: 5,
            material: new Cesium.PolylineDashMaterialProperty({
              color: Cesium.Color.WHITE,
              gapColor: new Cesium.Color(0, 0, 0, 0.5),
              dashLength: 20,
            }),
            clampToGround: true,
          },
        });
        polygonEntitiesRef.current.push(outlineE);

        // Semi-transparent fill
        const fillE = viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(
              Cesium.Cartesian3.fromDegreesArray([
                lon0, lat0, lon, lat0, lon, lat, lon0, lat, lon0, lat0,
              ])
            ),
            material: Cesium.Color.WHITE.withAlpha(0.22),
            classificationType: Cesium.ClassificationType.BOTH,
          },
        });
        polygonEntitiesRef.current.push(fillE);
        polygonFillEntityRef.current = fillE;

        const minLat = Math.min(lat0, lat), maxLat = Math.max(lat0, lat);
        const minLon = Math.min(lon0, lon), maxLon = Math.max(lon0, lon);

        // Store bbox early so the axis-mode effect can read it
        polygonBboxRef.current = { minLat, maxLat, minLon, maxLon };

        // Corner coordinate labels
        const midLat  = (minLat + maxLat) / 2;
        const mPerLon = 111320 * Math.cos(midLat * Math.PI / 180);
        const mPerLat = 111320;
        const cornerDefs = [
          [minLon, maxLat],
          [maxLon, maxLat],
          [minLon, minLat],
          [maxLon, minLat],
        ];
        polygonCornerEntitiesRef.current = [];

        const loadedKeys = Object.entries(layerStatesRef.current)
          .filter(([, s]) => s.status === "loaded").map(([k]) => k);

        const allPoints = [];

        // Use in-memory cached rows (loaded by loadLayer) — zero DB/network latency
        if (loadedKeys.length > 0) {
          for (const key of loadedKeys) {
            const layerCached = layerRawDataRef.current[key];
            if (!layerCached) continue;
            const { count: lc, lats: llats, lons: llons, colData: lcolData } = layerCached;
            const st = layerStatesRef.current[key];
            const dCols = st?.dCols || [];
            const valCol = dCols.length ? dCols[dCols.length - 1] : st?.valueColLabel;
            if (!valCol) continue;
            const valArrS = lcolData.get(valCol);
            if (!valArrS) continue;
            setSurfaceLabel("Deformation");
            for (let i = 0; i < lc; i++) {
              const la = llats[i], lo = llons[i];
              if (la >= minLat && la <= maxLat && lo >= minLon && lo <= maxLon) {
                const v = valArrS[i];
                if (isFinite(v)) allPoints.push({ lat: la, lon: lo, value: v });
              }
            }
          }
        }

        setSurfaceData(allPoints);
        surfaceDataRef.current = allPoints;
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
    polygonClickHandlerRef.current = clickHandler;

    return () => {
      if (!clickHandler.isDestroyed()) clickHandler.destroy();
      if (!moveHandler.isDestroyed())  moveHandler.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolygonMode, cesiumLoaded]);

  // ── Area & Perimeter tool handlers ──────────────────────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    if (areaClickHandlerRef.current && !areaClickHandlerRef.current.isDestroyed()) {
      areaClickHandlerRef.current.destroy(); areaClickHandlerRef.current = null;
    }
    if (areaMoveHandlerRef.current && !areaMoveHandlerRef.current.isDestroyed()) {
      areaMoveHandlerRef.current.destroy(); areaMoveHandlerRef.current = null;
    }
    if (!isAreaMode) return;

    const previewPosRef  = { current: null };
    const isClosedLocal  = { current: false };
    areaIsClosedRef.current = false;

    const ellipsoid = Cesium.Ellipsoid.WGS84;

    // Height at lon/lat from the loaded 3D tile mesh (same surface the clamped
    // lines drape onto). Synchronous. Used when pickPosition misses — its
    // pickEllipsoid fallback yields height≈0, far below the tile surface.
    // Own dots/entities are excluded so a re-sample never reads back a bad dot.
    const tileSurfaceAlt = (lon, lat, fallback) => {
      if (!viewer.scene.sampleHeightSupported) return fallback;
      const exclude = [
        ...(vertexDotsMapRef.current["__area__"] || []),
        ...areaEntitiesRef.current,
      ];
      let h;
      try { h = viewer.scene.sampleHeight(Cesium.Cartographic.fromDegrees(lon, lat), exclude); } catch (_) {}
      return Number.isFinite(h) ? h : fallback;
    };

    // Cursor → 3D tile surface point. pickPosition (depth buffer) first; if
    // that pixel has no rendered depth, ray-cast the cursor ray against the
    // loaded tile mesh (sync) so lat/lon stay exactly under the cursor.
    // pickEllipsoid is the last resort only — its ray continues to sea level,
    // which is horizontally offset from the visible surface in tilted views.
    const pickSurface = (screenPos) => {
      let cart = viewer.scene.pickPosition(screenPos);
      if (Cesium.defined(cart)) return { cart, onSurface: true };
      try {
        const ray = viewer.camera.getPickRay(screenPos);
        const exclude = [
          ...(vertexDotsMapRef.current["__area__"] || []),
          ...areaEntitiesRef.current,
        ];
        const hit = ray && viewer.scene.pickFromRay(ray, exclude);
        if (hit && Cesium.defined(hit.position)) return { cart: hit.position, onSurface: true };
      } catch (_) {}
      cart = viewer.camera.pickEllipsoid(screenPos, ellipsoid);
      return cart ? { cart, onSurface: false } : null;
    };

    // Entity-based dots so they integrate with the vertex drag handler
    const updateAreaDots = () => {
      renderVertexDots(
        { id: "__area__", vertices: [...areaVerticesRef.current], color: "#ffffff" },
        "__area__"
      );
    };

    const createPreviewEntities = () => {
      const pe = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            if (isClosedLocal.current) return [];
            const verts = areaVerticesRef.current;
            if (verts.length === 0) return [];
            const pts = verts.map(v => Cesium.Cartesian3.fromDegrees(v.lon, v.lat, 0));
            if (previewPosRef.current) {
              pts.push(Cesium.Cartesian3.fromDegrees(previewPosRef.current.lon, previewPosRef.current.lat, 0));
            }
            return pts;
          }, false),
          width: 3,
          material: new Cesium.PolylineDashMaterialProperty({
            color: Cesium.Color.WHITE,
            gapColor: new Cesium.Color(0, 0, 0, 0.5),
            dashLength: 20,
          }),
          clampToGround: true,
        },
      });
      const pfe = viewer.entities.add({
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() => {
            if (isClosedLocal.current) return new Cesium.PolygonHierarchy([]);
            const verts = areaVerticesRef.current;
            if (verts.length < 3) return new Cesium.PolygonHierarchy([]);
            return new Cesium.PolygonHierarchy(
              verts.map(v => Cesium.Cartesian3.fromDegrees(v.lon, v.lat, 0))
            );
          }, false),
          material:           Cesium.Color.WHITE.withAlpha(0.22),
          classificationType: Cesium.ClassificationType.BOTH,
        },
      });
      areaEntitiesRef.current.push(pe, pfe);
    };

    createPreviewEntities();

    // Clears all drawing entities and restarts a fresh drawing session in-place
    const resetDrawing = () => {
      clearVertexDots("__area__");
      if (!viewer.isDestroyed()) {
        areaEntitiesRef.current.forEach(e => {
          try { viewer.entities.remove(e); } catch (_) {}
        });
      }
      areaEntitiesRef.current = [];
      areaVerticesRef.current = [];
      isClosedLocal.current = false;
      areaIsClosedRef.current = false;
      setAreaResult(null);
      createPreviewEntities();
    };

    const closePolygon = () => {
      const verts = areaVerticesRef.current;
      if (verts.length < 3) return;
      // Any vertex whose click missed the depth buffer carries ellipsoid
      // height (≈0, far below the tile surface). Tiles are loaded by close
      // time, so re-sample every vertex from the mesh and reposition dots.
      areaVerticesRef.current = verts.map(v => ({ ...v, alt: tileSurfaceAlt(v.lon, v.lat, v.alt ?? 0) }));
      updateAreaDots();
      isClosedLocal.current = true;
      areaIsClosedRef.current = true;

      // Remove preview entities (preview polyline + preview fill ClassificationPrimitive)
      // before adding the closed ones — having two ClassificationType.BOTH primitives
      // simultaneously corrupts Cesium's stencil/depth state and blacks out 3D tiles
      if (!viewer.isDestroyed()) {
        areaEntitiesRef.current.forEach(e => { try { viewer.entities.remove(e); } catch (_) {} });
      }
      areaEntitiesRef.current = [];

      // Closed outline — CallbackProperty so vertex drag updates it live
      const outlineE = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const v = areaVerticesRef.current;
            if (v.length < 2) return [];
            const pts = v.map(p => Cesium.Cartesian3.fromDegrees(p.lon, p.lat, 0));
            return [...pts, pts[0]];
          }, false),
          width: 5,
          material: new Cesium.PolylineDashMaterialProperty({
            color:     Cesium.Color.WHITE,
            gapColor:  new Cesium.Color(0, 0, 0, 0.5),
            dashLength: 20,
          }),
          clampToGround: true,
        },
      });
      areaEntitiesRef.current.push(outlineE);
      {
        const v = areaVerticesRef.current;
        let perim = 0;
        for (let i = 0; i < v.length; i++) {
          const a = v[i], b = v[(i + 1) % v.length];
          perim += haversineKm(a.lat, a.lon, b.lat, b.lon);
        }
      }

      // Semi-transparent fill — also dynamic
      const fillE = viewer.entities.add({
        polygon: {
          hierarchy: new Cesium.CallbackProperty(() => {
            const v = areaVerticesRef.current;
            if (v.length < 3) return new Cesium.PolygonHierarchy([]);
            return new Cesium.PolygonHierarchy(v.map(p => Cesium.Cartesian3.fromDegrees(p.lon, p.lat, 0)));
          }, false),
          material:           Cesium.Color.WHITE.withAlpha(0.22),
          classificationType: Cesium.ClassificationType.BOTH,
        },
      });
      areaEntitiesRef.current.push(fillE);

      // Compute measurements
      const area  = geodesicAreaM2(verts);
      const perim = geodesicPerimeterM(verts);

      // Count data points + average velocity inside polygon
      let count = 0, velSum = 0, velCount = 0;
      Object.entries(layerRawDataRef.current).forEach(([key, cached]) => {
        const ls = layerStatesRef.current[key];
        if (!ls || ls.status !== "loaded") return;
        const { lats, lons } = cached;
        const velCol = ls.activeCol;
        const velArr = velCol && cached.colData ? cached.colData.get(velCol) : null;
        for (let i = 0; i < lats.length; i++) {
          if (pointInPoly(lats[i], lons[i], verts)) {
            count++;
            if (velArr && isFinite(velArr[i])) { velSum += velArr[i]; velCount++; }
          }
        }
      });

      setAreaResult({ area, perim, count, avgVel: velCount > 0 ? velSum / velCount : null });
    };

    // Mouse move — update cursor preview position (same as draw polygon)
    const moveHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    moveHandler.setInputAction((e) => {
      if (isClosedLocal.current) return;
      const p = pickSurface(e.endPosition);
      if (!p) return;
      const c = Cesium.Cartographic.fromCartesian(p.cart);
      previewPosRef.current = { lon: Cesium.Math.toDegrees(c.longitude), lat: Cesium.Math.toDegrees(c.latitude) };
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    areaMoveHandlerRef.current = moveHandler;

    // Click handler — same pattern as draw polygon: immediate add, no debounce
    const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandler.setInputAction((click) => {
      if (isClosedLocal.current) {
        // Polygon complete — reset and start a new drawing from this click
        resetDrawing();
        const p = pickSurface(click.position);
        if (!p) return;
        const c = Cesium.Cartographic.fromCartesian(p.cart);
        const lon0 = Cesium.Math.toDegrees(c.longitude);
        const lat0 = Cesium.Math.toDegrees(c.latitude);
        areaVerticesRef.current = [{ lon: lon0, lat: lat0, alt: p.onSurface ? c.height : tileSurfaceAlt(lon0, lat0, c.height) }];
        updateAreaDots();
        return;
      }
      const p = pickSurface(click.position);
      if (!p) return;
      const c   = Cesium.Cartographic.fromCartesian(p.cart);
      const lon = Cesium.Math.toDegrees(c.longitude);
      const lat = Cesium.Math.toDegrees(c.latitude);
      const alt = p.onSurface ? c.height : tileSurfaceAlt(lon, lat, c.height);
      const verts = areaVerticesRef.current;
      // Close when clicking near first vertex (≥3 verts, within 15px) — same as draw polygon
      if (verts.length >= 3) {
        const firstScreen = Cesium.SceneTransforms.worldToWindowCoordinates(
          viewer.scene, Cesium.Cartesian3.fromDegrees(verts[0].lon, verts[0].lat, verts[0].alt ?? 0)
        );
        if (firstScreen) {
          const dx = firstScreen.x - click.position.x, dy = firstScreen.y - click.position.y;
          if (Math.sqrt(dx * dx + dy * dy) < 15) { closePolygon(); return; }
        }
      }
      areaVerticesRef.current = [...verts, { lon, lat, alt }];
      updateAreaDots();
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Double-click → close polygon; deduplicate last vertex added by the preceding single click
    clickHandler.setInputAction(() => {
      if (isClosedLocal.current) return;
      const verts = areaVerticesRef.current;
      if (verts.length >= 2) {
        const last = verts[verts.length - 1], prev = verts[verts.length - 2];
        if (Math.abs(last.lon - prev.lon) < 0.00001 && Math.abs(last.lat - prev.lat) < 0.00001) {
          areaVerticesRef.current = verts.slice(0, -1);
          updateAreaDots();
        }
      }
      closePolygon();
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    areaClickHandlerRef.current = clickHandler;
  }, [isAreaMode, cesiumLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Distance tool handlers ───────────────────────────────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !cesiumLoaded) return;
    const Cesium = window.Cesium;

    if (distanceClickHandlerRef.current && !distanceClickHandlerRef.current.isDestroyed()) {
      distanceClickHandlerRef.current.destroy(); distanceClickHandlerRef.current = null;
    }
    if (distanceMoveHandlerRef.current && !distanceMoveHandlerRef.current.isDestroyed()) {
      distanceMoveHandlerRef.current.destroy(); distanceMoveHandlerRef.current = null;
    }
    if (!isDistanceMode) return;

    const previewLineRef = { current: null };
    const cursorCartRef  = { current: null }; // Cartesian3 under cursor, updated every MOUSE_MOVE

    // Own dots (PointPrimitiveCollections), labels, and preview line — excluded
    // from ray/height sampling so they can never feed their position back.
    const ownEntities = () => {
      const list = distanceEntitiesRef.current.map(e => e._col || e);
      if (previewLineRef.current) list.push(previewLineRef.current);
      return list;
    };

    // Cursor → 3D tile surface point. pickPosition (depth buffer) first; if
    // that pixel has no rendered depth, ray-cast the cursor ray against the
    // loaded tile mesh (sync) so lat/lon stay exactly under the cursor.
    // pickEllipsoid is the last resort — its ray continues to sea level, so
    // lift its height to the tile mesh before use.
    const pickPos = (screenPos) => {
      let cart = viewer.scene.pickPosition(screenPos);
      if (!Cesium.defined(cart)) {
        try {
          const ray = viewer.camera.getPickRay(screenPos);
          const hit = ray && viewer.scene.pickFromRay(ray, ownEntities());
          if (hit && Cesium.defined(hit.position)) cart = hit.position;
        } catch (_) {}
      }
      if (!Cesium.defined(cart)) {
        cart = viewer.camera.pickEllipsoid(screenPos, Cesium.Ellipsoid.WGS84);
        if (!Cesium.defined(cart)) return null;
        if (viewer.scene.sampleHeightSupported) {
          const c0 = Cesium.Cartographic.fromCartesian(cart);
          let h;
          try { h = viewer.scene.sampleHeight(c0, ownEntities()); } catch (_) {}
          if (Number.isFinite(h)) cart = Cesium.Cartesian3.fromRadians(c0.longitude, c0.latitude, h);
        }
      }
      const carto = Cesium.Cartographic.fromCartesian(cart);
      return { lat: Cesium.Math.toDegrees(carto.latitude), lon: Cesium.Math.toDegrees(carto.longitude), cart };
    };

    // Remove all tracked distance primitives/entities (does not touch P1 ref or step state)
    const clearEntities = () => {
      distanceEntitiesRef.current.forEach(e => {
        if (e._col) { try { viewer.scene.primitives.remove(e._col); } catch (_) {} }
        else        { try { viewer.entities.remove(e); } catch (_) {} }
      });
      distanceEntitiesRef.current = [];
    };

    const addDot = (cart, label) => {
      const col = new Cesium.PointPrimitiveCollection();
      col.add({
        position:                 cart,
        color:                    Cesium.Color.WHITE,
        outlineColor:             Cesium.Color.fromCssColorString("#232231"),
        outlineWidth:             2,
        pixelSize:                10,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
      viewer.scene.primitives.add(col);
      const labelE = viewer.entities.add({
        position: cart,
        billboard: { ...makeLabelBillboard(label, { pixelOffsetY: -16 }) },
      });
      distanceEntitiesRef.current.push({ _col: col }, labelE);
    };

    const clickHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    clickHandler.setInputAction((click) => {
      const pos = pickPos(click.position);
      if (!pos) return;

      if (!distanceP1Ref.current) {
        // First click — clear any previous completed measurement first
        clearEntities();
        if (previewLineRef.current) {
          try { viewer.entities.remove(previewLineRef.current); } catch (_) {}
          previewLineRef.current = null;
        }
        distanceP1Ref.current = pos; // { lat, lon, cart }
        addDot(pos.cart, "A");
        setDistanceStep(1);
        setDistanceResult(null);
      } else {
        // Second click — draw final line and show result
        const p1   = distanceP1Ref.current;
        const dist = haversineKm(p1.lat, p1.lon, pos.lat, pos.lon) * 1000; // metres
        addDot(pos.cart, "B");
        const lineE = viewer.entities.add({
          polyline: {
            positions: [p1.cart, pos.cart],
            width: 5,
            material: new Cesium.PolylineDashMaterialProperty({
              color:      Cesium.Color.WHITE,
              gapColor:   new Cesium.Color(0, 0, 0, 0.5),
              dashLength: 20,
            }),
            clampToGround: true,
          },
        });
        distanceEntitiesRef.current.push(lineE);
        if (previewLineRef.current) {
          try { viewer.entities.remove(previewLineRef.current); } catch (_) {}
          previewLineRef.current = null;
        }
        setDistanceResult({ dist, p1: { lat: p1.lat, lon: p1.lon }, p2: { lat: pos.lat, lon: pos.lon } });
        setDistanceStep(0);
        distanceP1Ref.current = null;
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Dashed preview line from A to cursor while awaiting second click
    const moveHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    moveHandler.setInputAction((move) => {
      if (!distanceP1Ref.current) return;
      const pos = pickPos(move.endPosition);
      if (!pos) return;
      cursorCartRef.current = pos.cart; // mutable — read by CallbackProperty each frame
      if (!previewLineRef.current) {
        previewLineRef.current = viewer.entities.add({
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              if (!distanceP1Ref.current || !cursorCartRef.current) return [];
              return [distanceP1Ref.current.cart, cursorCartRef.current];
            }, false),
            width: 3,
            material: new Cesium.PolylineDashMaterialProperty({
              color:      Cesium.Color.WHITE,
              gapColor:   new Cesium.Color(0, 0, 0, 0.5),
              dashLength: 20,
            }),
            clampToGround: true,
          },
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    distanceClickHandlerRef.current = clickHandler;
    distanceMoveHandlerRef.current  = moveHandler;

    return () => {
      if (previewLineRef.current) { try { viewer.entities.remove(previewLineRef.current); } catch (_) {} }
    };
  }, [isDistanceMode, cesiumLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // When project changes, clear all loaded point layers and identify state
  const handleProjectSelect = (project) => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      Object.values(collectionsRef.current).forEach(col => viewer.scene.primitives.remove(col));
      Object.values(ghostCollectionsRef.current).forEach(col => viewer.scene.primitives.remove(col));
      Object.values(clientGeoJsonSourcesRef.current).forEach(ds => viewer.dataSources.remove(ds, true));
    }
    collectionsRef.current = {};
    ghostCollectionsRef.current = {};
    clientGeoJsonSourcesRef.current = {};
    clientLayerPropsRef.current = {};
    clientPointLayerKeysRef.current = new Set();
    setClientFeatureCard(null);
    setEditingClientId(null);
    layerSchemasRef.current = {};
    setLayerStates({});
    setSelectedProject(project);
    clearIdentifyTool();
    setIsIdentifyMode(false);
    clearSectionTool();
    setIsSectionMode(false);
    clearPolygonTool();
    setIsPolygonMode(false);
    clearAreaTool();
    setIsAreaMode(false);
    stopDrawTool();
    // Clear drawn (unsaved) features
    clearDrawEntities();
    setDrawFeatures([]);
    // Unload all annotation layers and fetch new project's annotations
    Object.keys(annoEntitiesRef.current).forEach(k => unloadAnnoLayer(k));
    setAnnoLayers([]);
    setAnnoLayerStates({});
    setAnnoEditKey(null);
    setAnnoEditFeatures([]);
    fetchAnnoLayers(project);
    setClientLayers([]);
    setShowClientUpload(false);
    fetchClientLayers(project);
  };

  // Fetch annotation layers when project is first set
  useEffect(() => { fetchAnnoLayers(selectedProject); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClientLayers = async (project) => {
    if (!project) { setClientLayers([]); setClientUsage({ used: 0, limit: 262144000 }); return; }
    try {
      const token = authTokenRef.current || await getToken();
      const res   = await fetch(
        `${PORTAL_API_URL}/api/client-layers?project_id=${project.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const { layers, totalUsage, limit } = await res.json();
      // Normalize r2_key → key so sidebar toggle works for both API-loaded and freshly uploaded layers
      setClientLayers((layers || []).map(l => ({ ...l, key: l.key ?? l.r2_key })));
      setClientUsage({ used: totalUsage || 0, limit: limit || 262144000 });
    } catch {}
  };

  // Keep drawToolRef in sync
  useEffect(() => { drawToolRef.current = drawTool; }, [drawTool]);

  // ── Fullscreen ────────────────────────────────────────────────────────────
  const handleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || userId || "User";
  const userEmail   = user?.primaryEmailAddress?.emailAddress || userId || "";

  const allowedTools    = Array.isArray(selectedProject?.tools) ? selectedProject.tools : [];
  const clientLayerKeySet = new Set(clientLayers.map(cl => cl.key));
  const hasLoadedLayer    = Object.values(layerStates).some(s => s.status === "loaded");
  // InSAR-only layers gate the full analysis toolset; client layers only enable identify
  const hasLoadedInSARLayer    = Object.entries(layerStates).some(([k, s]) => s.status === "loaded" && !clientLayerKeySet.has(k));
  const hasLoadedClientLayer   = Object.entries(layerStates).some(([k, s]) => s.status === "loaded" && clientLayerKeySet.has(k));

  // r2_layers comes from getProjects backend; fall back to parsing layers_config directly
  // so the layer panel works even if the backend hasn't deployed the r2_layers field yet
  const projectR2Layers = selectedProject
    ? (selectedProject.r2_layers ?? (() => {
        try {
          const lc = JSON.parse(selectedProject.layers_config || "{}");
          return Array.isArray(lc.r2) ? lc.r2 : [];
        } catch { return []; }
      })())
    : [];

  return (
    <div
      className="map-container cesium-container"
      style={{ position: "relative", width: "100%", height: "100vh" }}
    >
      <style>{`
        .cesium-widget-credits {
          bottom: 4px !important;
          right: 0 !important;
          left: auto !important;
        }
        ${(isIdentifyMode || isSectionMode || isAreaMode || isPolygonMode || isDistanceMode || !!drawTool) ? ".cesium-widget canvas { cursor: crosshair !important; }" : ""}
      `}</style>

      {/* ── Globe container — starts below 52px top header ── */}
      <div ref={containerRef} data-onboard="map" style={{ position: "absolute", top: 52, left: 0, right: 0, bottom: 0 }} />

      {/* ── Surface build progress — centred on the selected rectangle ── */}
      {surfaceBuildProgress && (
        <div style={{
          position: "absolute",
          left: surfaceBuildProgress.x != null ? surfaceBuildProgress.x : "50%",
          top:  surfaceBuildProgress.y != null ? 52 + surfaceBuildProgress.y : "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 50,
          pointerEvents: "none",
          background: "rgba(17,24,39,0.85)",
          borderRadius: 10,
          padding: "10px 14px",
          minWidth: 170,
          fontFamily: "'Inter','Helvetica Neue',sans-serif",
          boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        }}>
          <div style={{ color: "#f9fafb", fontSize: 11, fontWeight: 600, marginBottom: 6, textAlign: "center" }}>
            Building surface… {surfaceBuildProgress.pct}%
          </div>
          <div style={{ height: 4, background: "rgba(255,255,255,0.25)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${surfaceBuildProgress.pct}%`,
              background: "#6366f1",
              borderRadius: 2,
              transition: "width 0.15s linear",
            }} />
          </div>
        </div>
      )}



      {/* ── Loading overlay ── */}
      {!appReady && (
        <InSpektLoader progress={loadProgress} step={loadStep} />
      )}

      {/* ── Top header — matches Geospekter portal nav ── */}
      <header data-onboard="header" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1300,
        background: "#232231", borderBottom: "1px solid #2d2c3f",
        height: 52, display: "flex", alignItems: "center",
        padding: "0 10px", gap: 4, boxSizing: "border-box",
      }}>
        {/* App logo */}
        <img
          src="/inspekt-logo.jpeg"
          alt="InSpekt"
          style={{ height: 32, marginLeft: 4, marginRight: 8, flexShrink: 0, borderRadius: 4 }}
        />

        {/* Divider */}
        <div style={{ width: 1, height: 20, background: "#2d2c3f", margin: "0 4px", flexShrink: 0 }} />

        {/* Tool buttons — only shown after Cesium loads */}
        {cesiumLoaded && (
          <>
            <button className="header-btn" onClick={handleFullscreen} title="Full Screen">
              <Maximize2 size={15} />
            </button>

            <button
              className="header-btn"
              onClick={() => setIs3DView(prev => !prev)}
              title={is3DView ? "Lock to top-down (2D)" : "Enable free tilt (3D)"}
            >
              {is3DView ? "2D" : "3D"}
            </button>

            <button
              className={`header-btn${isAreaMode ? " active" : ""}`}
              onClick={toggleAreaMode}
              title="Area & Perimeter"
            >
              <Hexagon size={15} />
            </button>

            <button
              className={`header-btn${isDistanceMode ? " active" : ""}`}
              onClick={toggleDistanceMode}
              title="Measure Distance"
            >
              <Ruler size={15} />
            </button>

            {/* Separator + analysis tools — visible whenever a project is selected */}
            {selectedProject && (
              <>
                <div style={{ width: 1, height: 20, background: "#2d2c3f", margin: "0 4px", flexShrink: 0 }} />
                <button
                  className={`header-btn${isIdentifyMode ? " active" : ""}`}
                  onClick={(hasLoadedInSARLayer || hasLoadedClientLayer) ? toggleIdentifyMode : undefined}
                  disabled={!hasLoadedInSARLayer && !hasLoadedClientLayer}
                  title={(hasLoadedInSARLayer || hasLoadedClientLayer) ? "Point Inspector" : "Activate a layer to use this tool"}
                >
                  <Crosshair size={15} />
                </button>
                <button
                  className={`header-btn${isSectionMode ? " active" : ""}`}
                  onClick={hasLoadedInSARLayer ? toggleSectionMode : undefined}
                  disabled={!hasLoadedInSARLayer}
                  title={hasLoadedInSARLayer ? "Profile Viewer" : "Activate an InSAR layer to use this tool"}
                >
                  <BarChart2 size={15} />
                </button>
                <button
                  className={`header-btn${isPolygonMode ? " active" : ""}`}
                  onClick={hasLoadedInSARLayer ? togglePolygonMode : undefined}
                  disabled={!hasLoadedInSARLayer}
                  title={hasLoadedInSARLayer ? "3D Surface Modeler" : "Activate an InSAR layer to use this tool"}
                >
                  <BoxSelect size={15} />
                </button>
              </>
            )}
          </>
        )}

        {/* Right section */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>

          {/* Coordinates */}
          {cesiumLoaded && coords.lat != null && (
            <span style={{ color: "#6b7280", fontSize: 12, letterSpacing: "0.01em" }}>
              {coords.lat}°N&nbsp;&nbsp;{coords.lng}°E
            </span>
          )}

          {/* Attribution */}
          {cesiumLoaded && (
            <div style={{ position: "relative" }}>
              <button
                className={`header-btn${showAttribution ? " active" : ""}`}
                onClick={() => setShowAttribution(v => !v)}
                title="Attribution"
              >
                <Info size={14} />
              </button>
              {showAttribution && (
                <div style={{
                  position: "absolute", top: "calc(100% + 6px)", right: 0,
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)", padding: "10px 14px",
                  fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", zIndex: 2000,
                }}>
                  ©{" "}<a href="https://cesium.com" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#6b7280", textDecoration: "none" }}>CesiumJS</a>
                  {" "}©{" "}<a href="https://maps.google.com" target="_blank" rel="noopener noreferrer"
                    style={{ color: "#6b7280", textDecoration: "none" }}>Google</a>
                </div>
              )}
            </div>
          )}

          {/* Search */}
          {cesiumLoaded && (
            <div style={{ position: "relative" }}>
              <button
                className={`header-btn${showSearch ? " active" : ""}`}
                onClick={() => { setShowSearch(v => !v); setSearchQuery(""); setSearchSuggestions([]); }}
                title="Search place"
              >
                <Search size={15} />
              </button>
              {showSearch && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 9999 }}>
                  <input
                    autoFocus
                    type="text"
                    value={searchQuery}
                    placeholder="Search places…"
                    onChange={e => {
                      const q = e.target.value;
                      setSearchQuery(q);
                      clearTimeout(searchDebounceRef.current);
                      if (q.length < 2) { setSearchSuggestions([]); return; }
                      searchDebounceRef.current = setTimeout(async () => {
                        try {
                          const res = await fetch(
                            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=8&addressdetails=0&polygon_geojson=1&polygon_threshold=0.005`,
                            { headers: { "Accept-Language": "en" } }
                          );
                          const data = await res.json();
                          setSearchSuggestions((data || []).map(r => ({
                            id:         r.place_id,
                            place_name: r.display_name,
                            center:     [parseFloat(r.lon), parseFloat(r.lat)],
                            bbox:       r.boundingbox,
                            cls:        r.class,
                            typ:        r.type,
                            geojson:    r.geojson || null,
                          })));
                        } catch { setSearchSuggestions([]); }
                      }, 300);
                    }}
                    onKeyDown={e => e.key === "Escape" && setShowSearch(false)}
                    style={{
                      width: 220, padding: "0 10px", fontSize: 13,
                      height: 36, boxSizing: "border-box",
                      background: "#fff", color: "#111",
                      border: "1px solid #e5e7eb", borderRadius: 8, outline: "none",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                    }}
                  />
                  {searchSuggestions.length > 0 && (
                    <ul style={{
                      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                      margin: 0, padding: 0, listStyle: "none",
                      background: "#fff", borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                      border: "1px solid #e5e7eb",
                      maxHeight: 260, overflowY: "auto",
                    }}>
                      {searchSuggestions.map(f => {
                        const meta  = nominatimMeta(f.cls, f.typ);
                        const parts = f.place_name.split(", ");
                        const name  = parts[0];
                        const addr  = parts.slice(1).join(", ");
                        const shortAddr = addr.length > 42 ? addr.slice(0, 39) + "…" : addr;
                        return (
                          <li
                            key={f.id}
                            onClick={() => {
                              const Cesium = window.Cesium;
                              const viewer = viewerRef.current;
                              if (!viewer || !Cesium) return;
                              searchEntityRef.current.forEach(item => {
                                try {
                                  if (item._col) viewer.scene.primitives.remove(item._col);
                                  else viewer.entities.remove(item);
                                } catch (_) {}
                              });
                              searchEntityRef.current = [];
                              const [lng, lat] = f.center;
                              const altitude   = nominatimAltitude(f.bbox, f.cls, f.typ);
                              const gjType     = f.geojson?.type;
                              const isRoad     = f.cls === "highway";
                              const isBoundary = !isRoad && (gjType === "Polygon" || gjType === "MultiPolygon" || gjType === "LineString" || gjType === "MultiLineString");
                              if (isBoundary && f.geojson) {
                                const rings = [];
                                if (gjType === "Polygon") { rings.push(f.geojson.coordinates[0]); }
                                else if (gjType === "MultiPolygon") { f.geojson.coordinates.forEach(poly => rings.push(poly[0])); }
                                else if (gjType === "LineString") { rings.push(f.geojson.coordinates); }
                                else if (gjType === "MultiLineString") { f.geojson.coordinates.forEach(line => rings.push(line)); }
                                const closed = gjType === "Polygon" || gjType === "MultiPolygon";
                                rings.forEach(ring => {
                                  const coords = closed ? [...ring, ring[0]] : ring;
                                  const positions = coords.map(([lo, la]) => Cesium.Cartesian3.fromDegrees(lo, la, 0));
                                  const shadow = viewer.entities.add({ polyline: { positions, width: 6, material: Cesium.Color.WHITE.withAlpha(0.55), clampToGround: true } });
                                  const main = viewer.entities.add({ polyline: { positions, width: 2.5, material: new Cesium.PolylineDashMaterialProperty({ color: Cesium.Color.fromCssColorString("#d93025"), gapColor: Cesium.Color.TRANSPARENT, dashLength: 24, dashPattern: 0xFF00 }), clampToGround: true } });
                                  searchEntityRef.current.push(shadow, main);
                                });
                              } else if (!isRoad) {
                                const col = new Cesium.PointPrimitiveCollection();
                                col.add({ position: Cesium.Cartesian3.fromDegrees(lng, lat, 0), color: Cesium.Color.fromCssColorString("#d93025"), outlineColor: Cesium.Color.WHITE, outlineWidth: 2, pixelSize: 13, disableDepthTestDistance: Number.POSITIVE_INFINITY, heightReference: Cesium.HeightReference.CLAMP_TO_GROUND });
                                viewer.scene.primitives.add(col);
                                searchEntityRef.current.push({ _col: col });
                              } else {
                                const roadName = f.place_name.split(", ")[0];
                                const labelE = viewer.entities.add({ position: Cesium.Cartesian3.fromDegrees(lng, lat, 0), billboard: { ...makeLabelBillboard(roadName, { pixelOffsetY: 0 }), heightReference: Cesium.HeightReference.CLAMP_TO_GROUND } });
                                searchEntityRef.current.push(labelE);
                              }
                              viewer.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(lng, lat, altitude), duration: 1.5 });
                              setShowSearch(false);
                              setSearchQuery("");
                              setSearchSuggestions([]);
                            }}
                            style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
                              {shortAddr && <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{shortAddr}</div>}
                            </div>
                            {meta.label && <span style={{ fontSize: 10, color: "#555", flexShrink: 0 }}>{meta.label}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Admin back */}
          {onBack && (
            <button className="header-btn" onClick={onBack} title="Back to Admin">
              <Shield size={15} />
            </button>
          )}

          {/* Help / onboarding re-trigger */}
          <button
            className="header-btn"
            onClick={() => setOnboardPhase("modal")}
            title="Getting started guide"
            style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            ?
          </button>

          {/* User menu */}
          <div style={{ position: "relative" }} ref={userMenuRef}>
            <button
              className="header-btn"
              onClick={() => setShowUserMenu(v => !v)}
              title={displayName}
            >
              <User size={15} />
            </button>
            {showUserMenu && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 2000,
                background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)", minWidth: 190, overflow: "hidden" }}>
                <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid #e5e7eb" }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#111827" }}>{displayName}</p>
                  <p style={{ margin: "1px 0 0", fontSize: 11, color: "#6b7280" }}>{userEmail}</p>
                </div>
                <button onClick={() => { setShowUserMenu(false); window.location.href = PORTAL_API_URL; }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", border: "none", background: "transparent",
                    cursor: "pointer", fontSize: 13, color: "#111827", textAlign: "left" }}>
                  <LayoutGrid size={13} color="#6b7280" /> Go to Portal
                </button>
                <div style={{ height: 1, background: "#e5e7eb" }} />
                <button onClick={() => { setShowUserMenu(false); onLogout(); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", border: "none", background: "transparent",
                    cursor: "pointer", fontSize: 13, color: "#dc2626", textAlign: "left" }}>
                  <LogOut size={13} color="#dc2626" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Floating dots button — open layers panel ── */}
      {!isSidebarOpen && (
        <button
          data-onboard="sidebar-toggle"
          onClick={() => setIsSidebarOpen(true)}
          title="Open layers"
          style={{
            position: "fixed", top: 62, right: 10, zIndex: 1201,
            background: "#232231", border: "1px solid #2d2c3f",
            borderRadius: 6, padding: "6px 8px",
            cursor: "pointer", color: "#9ca3af",
            display: "flex", alignItems: "center",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#2d2c3f"; e.currentTarget.style.color = "#f9fafb"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#232231"; e.currentTarget.style.color = "#9ca3af"; }}
        >
          <Layers size={15} />
        </button>
      )}

      {/* ── Layers panel (right side, floating) ── */}
      <div data-onboard="sidebar" className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        {/* Sidebar header — matches tool-panel-header style */}
        <div className="tool-panel-header" style={{ borderRadius: "10px 10px 0 0" }}>
          <span className="tool-panel-title">Project</span>
          <button className="tool-panel-close" onClick={() => setIsSidebarOpen(false)} title="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: "10px 10px 12px" }}>

          <ProjectSelector
            onProjectSelect={handleProjectSelect}
            onProjectChange={() => handleProjectSelect(null)}
            authToken={authToken}
            onLoaded={(count) => {
              setProjectCount(count);
              setLoadProgress(prev => Math.max(prev, 90));
              setLoadStep("Loading monitoring data…");
              projectsReadyRef.current = true;
              maybeDoneRef.current?.();
            }}
          />

          {/* ── Layers — only shown when a project is selected ── */}
          {selectedProject && <div>

            {/* Empty state when no layers are assigned to this project */}
            {projectR2Layers.length === 0 && (
              <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 4px", lineHeight: 1.5 }}>
                No layers provided.
              </div>
            )}

            {/* Dynamic layer list — one row per R2 layer assigned to this project */}
            {projectR2Layers.map(layer => {
              const st      = layerStates[layer.key] ?? { status: "idle", count: 0 };
              const isLoaded  = st.status === "loaded";
              const isLoading = st.status === "loading";
              const isError   = st.status === "error";
              const color     = isLoaded ? layerColor(layer.key) : "#999";
              const isGeoNames = layer.key === "test/points_10m.parquet";
              return (
                <div key={layer.key} style={{ marginBottom: 2 }}>
                  {/* ── Layer header row ── */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 6px", borderRadius: 6,
                    background: isLoaded ? "#f3f4f6" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!isLoaded) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { if (!isLoaded) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div onClick={isLoading ? undefined : () => toggleLayer(layer.key)}
                      style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `2px solid ${isError ? "#ef4444" : isLoaded ? "#232231" : "#d1d5db"}`,
                        background: isLoaded ? "#232231" : "transparent",
                        cursor: isLoading ? "wait" : "pointer", transition: "all 0.15s",
                      }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{ fontSize: 12, color: "#333", fontWeight: 500,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          cursor: isLoaded ? "pointer" : "default",
                          textDecoration: isLoaded && hoveredLayer === layer.key ? "underline" : "none",
                        }}
                        onMouseEnter={() => isLoaded && setHoveredLayer(layer.key)}
                        onMouseLeave={() => setHoveredLayer(null)}
                        onClick={() => {
                          if (!isLoaded) return;
                          const { minLat, maxLat, minLon, maxLon } = st;
                          if (minLat == null || minLat > maxLat) return;
                          const Cesium = window.Cesium;
                          const viewer = viewerRef.current;
                          if (!viewer || !Cesium) return;
                          const center = Cesium.Cartesian3.fromDegrees((minLon + maxLon) / 2, (minLat + maxLat) / 2);
                          const corner = Cesium.Cartesian3.fromDegrees(minLon, minLat);
                          const radius = Cesium.Cartesian3.distance(center, corner);
                          viewer.camera.flyToBoundingSphere(
                            new Cesium.BoundingSphere(center, radius),
                            { duration: 1.5, offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, 0) }
                          );
                        }}
                      >
                        {layer.name}
                      </div>
                      <div style={{ fontSize: 10, color: "#999", marginTop: 1 }}>
                        {st.status === "idle"    && (!dbReady ? (dbError ? "DuckDB error" : "DuckDB loading…") : "Click to load")}
                        {st.status === "loading" && (
                          <>
                            <span>Rendering point layer… {st.progress > 0 ? `${st.progress}%` : ""}</span>
                            {st.progress > 0 && (
                              <div style={{ height: 3, borderRadius: 2, background: "#e0e0e0", marginTop: 4, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${st.progress}%`, background: "#111827", transition: "width 0.15s" }} />
                              </div>
                            )}
                          </>
                        )}
                        {st.status === "loaded"  && `${st.count.toLocaleString()} points rendered`}
                        {st.status === "error"   && "Load failed — check console"}
                      </div>
                    </div>
                    {isLoading && <Loader2 size={11} color="#9ca3af" style={{ animation: "spin 1s linear infinite" }} />}
                    {isLoaded  && (
                      <button
                        title="Fly to layer"
                        onClick={e => {
                          e.stopPropagation();
                          const { minLat, maxLat, minLon, maxLon } = st;
                          if (minLat == null || minLat > maxLat) return;
                          const Cesium = window.Cesium;
                          const viewer = viewerRef.current;
                          if (!viewer || !Cesium) return;
                          const center = Cesium.Cartesian3.fromDegrees((minLon + maxLon) / 2, (minLat + maxLat) / 2);
                          const corner = Cesium.Cartesian3.fromDegrees(minLon, minLat);
                          const radius = Cesium.Cartesian3.distance(center, corner);
                          viewer.camera.flyToBoundingSphere(
                            new Cesium.BoundingSphere(center, radius),
                            { duration: 1.5, offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, 0) }
                          );
                        }}
                        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2, color: "#111827", display: "flex", flexShrink: 0 }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                          <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                          <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                          <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                          <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>
                        </svg>
                      </button>
                    )}
                    {isError   && <span style={{ fontSize: 10, color: "#ef4444" }}>✕</span>}
                  </div>

                  {/* ── GeoNames legend ── */}
                  {isGeoNames && isLoaded && (
                    <div style={{
                      padding: "4px 6px 8px 28px",
                      fontSize: 10, color: "#555",
                    }}>
                      {[
                        ["#ff6b35","Populated places"],["#4488ff","Water"],
                        ["#44cc66","Vegetation"],["#a0856b","Terrain"],
                        ["#ffcc00","Spots / buildings"],["#cc44ff","Administrative"],["#aaaaaa","Roads"],
                      ].map(([c, lbl]) => (
                        <div key={lbl} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:2 }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background:c, flexShrink:0 }} />
                          {lbl}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Value legend or generic swatch ── */}
                  {!isGeoNames && isLoaded && (
                    st.hasVel ? (
                      <div style={{ padding: "2px 6px 8px 28px", fontSize: 10, color: "#555" }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 4 }}>
                          <span style={{ color: "#111827", fontWeight: 500 }}>{st.minV?.toFixed(1)} mm</span>
                          <span style={{ color: "#111827", fontWeight: 500 }}>0</span>
                          <span style={{ color: "#111827", fontWeight: 500 }}>{st.maxV?.toFixed(1)} mm</span>
                        </div>
                        <LegendRangeSlider
                          colorStops={st.colorStops || DEFAULT_PALETTE.stops}
                          filter={layerFilters[layer.key] || { lo: 0, hi: 1 }}
                          onChange={f => {
                            setLayerFilters(prev => ({ ...prev, [layer.key]: f }));
                            if (filterRafRef.current[layer.key]) cancelAnimationFrame(filterRafRef.current[layer.key]);
                            filterRafRef.current[layer.key] = requestAnimationFrame(() => {
                              delete filterRafRef.current[layer.key];
                              applyLayerFilter(layer.key, f.lo, f.hi);
                            });
                          }}
                          onChangeEnd={f => {
                            if (filterRafRef.current[layer.key]) {
                              cancelAnimationFrame(filterRafRef.current[layer.key]);
                              delete filterRafRef.current[layer.key];
                            }
                            setLayerFilters(prev => ({ ...prev, [layer.key]: f }));
                            applyLayerFilter(layer.key, f.lo, f.hi);
                          }}
                        />
                        {(() => {
                          const flt = layerFilters[layer.key] || { lo: 0, hi: 1 };
                          const isFiltered = flt.lo > 0.001 || flt.hi < 0.999;
                          const range = Math.max(Math.abs(st.minV ?? 0), Math.abs(st.maxV ?? 0), 0.001);
                          const loV = ((flt.lo * 2 - 1) * range).toFixed(1);
                          const hiV = ((flt.hi * 2 - 1) * range).toFixed(1);
                          return (
                            <div style={{ marginTop: 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span style={{ color: "#9ca3af" }}>
                                {isFiltered ? `${loV} → ${hiV} mm` : "Total deformation (mm)"}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                {isFiltered && (
                                  <button
                                    onClick={() => {
                                      const reset = { lo: 0, hi: 1 };
                                      setLayerFilters(prev => ({ ...prev, [layer.key]: reset }));
                                      applyLayerFilter(layer.key, 0, 1);
                                    }}
                                    title="Reset filter"
                                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 3px",
                                      borderRadius: 3, display: "flex", alignItems: "center", color: "#9ca3af", fontSize: 10 }}
                                    onMouseEnter={e => { e.currentTarget.style.color = "#232231"; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = "#9ca3af"; }}
                                  >✕</button>
                                )}
                                <button
                                  onClick={() => setLegendStyleKey(legendStyleKey === layer.key ? null : layer.key)}
                                  title="Change colours"
                                  style={{
                                    background: legendStyleKey === layer.key ? "#eeeef2" : "none",
                                    border: "none", cursor: "pointer", padding: "2px 3px",
                                    borderRadius: 3, display: "flex", alignItems: "center",
                                    color: legendStyleKey === layer.key ? "#232231" : "#9ca3af",
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = "#eeeef2"; e.currentTarget.style.color = "#232231"; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = legendStyleKey === layer.key ? "#eeeef2" : "none"; e.currentTarget.style.color = legendStyleKey === layer.key ? "#232231" : "#9ca3af"; }}
                                >
                                  <Palette size={11} />
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                        {/* ── Collapsible layer metadata ── */}
                        {(st.startDate || st.satPlatform || st.processingMethod || st.alosLos != null) && (
                          <div style={{ marginTop: 6, borderTop: "1px solid #f0f0f5" }}>
                            <button
                              onClick={() => setLayerInfoOpen(prev => ({ ...prev, [layer.key]: !prev[layer.key] }))}
                              style={{ display: "flex", alignItems: "center", gap: 4, background: "none",
                                border: "none", cursor: "pointer", padding: "4px 0 2px", color: "#9ca3af",
                                fontSize: 10, width: "100%" }}
                            >
                              <ChevronRight size={9}
                                style={{ transform: layerInfoOpen[layer.key] ? "rotate(90deg)" : "rotate(0deg)",
                                  transition: "transform 0.15s", flexShrink: 0 }} />
                              Layer info
                            </button>
                            {layerInfoOpen[layer.key] && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 3, paddingLeft: 12, paddingBottom: 4 }}>
                                {[
                                  ["Platform", st.satPlatform
                                    ? SAT_URLS[st.satPlatform]
                                      ? <a href={SAT_URLS[st.satPlatform]} target="_blank" rel="noreferrer"
                                            style={{ color: "#4F7FD1", fontWeight: 600, textDecoration: "none" }}>
                                            {st.satPlatform} ↗
                                          </a>
                                      : st.satPlatform
                                    : "N/A"],
                                  ["Method", st.processingMethod || "N/A"],
                                  ["Start",  fmtDCol(st.startDate)  || "N/A"],
                                  ["End",    fmtDCol(st.endDate)    || "N/A"],
                                  ["LOS",    st.alosLos != null ? `${Number(st.alosLos).toFixed(1)}°` : "N/A"],
                                ].map(([lbl, val]) => (
                                  <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{ color: "#9ca3af", width: 48, flexShrink: 0 }}>{lbl}</span>
                                    <span style={{ color: "#374151", fontWeight: 500 }}>{val}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        padding: "2px 6px 8px 28px",
                        fontSize: 10, color: "#9ca3af",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        <span style={{ width:8, height:8, borderRadius: 2, background: color, flexShrink:0 }} />
                        Point layer
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>}

          {/* ── Saved annotation layers — inline below point layers ── */}
          {selectedProject && annoLayers.length > 0 && (
            <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase",
              letterSpacing: "0.06em", margin: "10px 4px 6px" }}>
              Annotations
            </div>
          )}
          {selectedProject && annoLayers.length > 0 && annoLayers.map(al => {
            const aState  = annoLayerStates[al.key] || "idle";
            const isLoaded  = aState === "loaded";
            const isLoading = aState === "loading";
            return (
              <div key={al.key} style={{ marginBottom: 2 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "7px 6px",
                  borderRadius: 6,
                  background: isLoaded ? "#f3f4f6" : "transparent",
                  cursor: isLoading ? "wait" : "pointer", userSelect: "none",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (!isLoaded) e.currentTarget.style.background = "#f9fafb"; }}
                onMouseLeave={e => { if (!isLoaded) e.currentTarget.style.background = "transparent"; }}
                onClick={() => isLoading ? undefined : (isLoaded ? unloadAnnoLayer(al.key) : loadAnnoLayer(al.key))}>
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    border: `2px solid ${isLoaded ? "#111827" : "#d1d5db"}`,
                    background: isLoaded ? "#111827" : "transparent",
                    cursor: isLoading ? "wait" : "pointer", transition: "all 0.2s",
                  }} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#333",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {al.name}
                  </span>
                  {isLoading && <Loader2 size={11} color="#9ca3af" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />}
                  {isLoaded && (isAdmin || !al.created_by || al.created_by === userEmail) && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        if (annoEditKey === al.key) {
                          setAnnoEditKey(null);
                        } else {
                          deactivateAllTools();
                          setAnnoEditKey(al.key);
                          setAnnoEditFeatures([...(annoFeaturesRef.current[al.key] || [])]);
                          setAnnoEditLayerName(al.name);
                        }
                      }}
                      title="Edit layer"
                      onMouseEnter={() => setHoveredAnnoBtn({ key: al.key, btn: "edit" })}
                      onMouseLeave={() => setHoveredAnnoBtn(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0,
                        color: hoveredAnnoBtn?.key === al.key && hoveredAnnoBtn?.btn === "edit" ? "#1a73e8" : "#9ca3af" }}>
                      <Edit3 size={11} />
                    </button>
                  )}
                  {!isDemoUser && (isAdmin || !al.created_by || al.created_by === userEmail) && (
                    <button
                      onClick={e => { e.stopPropagation(); if (window.confirm(`Delete annotation layer "${al.name}"?`)) deleteAnnoLayer(al.key); }}
                      title="Delete layer"
                      onMouseEnter={() => setHoveredAnnoBtn({ key: al.key, btn: "delete" })}
                      onMouseLeave={() => setHoveredAnnoBtn(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0,
                        color: hoveredAnnoBtn?.key === al.key && hoveredAnnoBtn?.btn === "delete" ? "#ef4444" : "#9ca3af" }}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* ── Additional Layers (user-uploaded GeoJSON/KML → Parquet) ── */}
          {selectedProject && (
            <div style={{ marginTop: 12 }}>
              {/* Section header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 4px 6px" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Additional Layers
                </span>
                <button
                  onClick={() => setShowClientUpload(v => !v)}
                  title="Upload a layer"
                  style={{
                    background: showClientUpload ? "#6366f1" : "none",
                    border: `1px solid ${showClientUpload ? "#6366f1" : "#d1d5db"}`,
                    borderRadius: 4, padding: "2px 7px",
                    fontSize: 10, fontWeight: 600,
                    color: showClientUpload ? "#fff" : "#6b7280",
                    cursor: "pointer", lineHeight: 1.6,
                  }}
                >
                  + Upload
                </button>
              </div>

              {/* Upload panel */}
              {showClientUpload && (
                <div style={{ marginBottom: 10, padding: "10px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <ClientLayerUpload
                    db={dbRef.current}
                    projectId={selectedProject.id}
                    getToken={async () => authTokenRef.current || await getToken()}
                    onUploaded={(layer) => {
                      setClientLayers(prev => [layer, ...prev]);
                      setClientUsage(prev => ({ ...prev, used: prev.used + (layer.size_bytes || 0) }));
                      setShowClientUpload(false);
                    }}
                  />
                </div>
              )}

              {/* Storage usage bar */}
              {(clientLayers.length > 0 || clientUsage.used > 0) && (
                <div style={{ margin: "0 4px 8px" }}>
                  <div style={{ height: 3, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 2, background: "#6366f1",
                      width: `${Math.min((clientUsage.used / clientUsage.limit) * 100, 100).toFixed(1)}%`,
                    }} />
                  </div>
                  <div style={{ fontSize: 9, color: "#9ca3af", marginTop: 3 }}>
                    {(clientUsage.used / 1024 / 1024).toFixed(1)} MB of {(clientUsage.limit / 1024 / 1024).toFixed(0)} MB used
                  </div>
                </div>
              )}

              {/* Layer rows */}
              {clientLayers.length === 0 && !showClientUpload && (
                <div style={{ fontSize: 11, color: "#9ca3af", padding: "4px 4px", lineHeight: 1.5 }}>
                  No layers yet — upload a GeoJSON or KML file.
                </div>
              )}
              {clientLayers.map(cl => {
                const st        = layerStates[cl.key] ?? { status: "idle" };
                const isLoaded  = st.status === "loaded";
                const isLoading = st.status === "loading";
                const isError   = st.status === "error";
                const color     = isLoaded ? layerColor(cl.key) : "#999";
                const canEdit   = cl.can_edit !== false; // own layer or admin
                const isEditing = editingClientId === cl.id;
                return (
                  <div key={cl.id} style={{ marginBottom: 2 }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "7px 6px",
                      borderRadius: 6, background: isLoaded ? "#f3f4f6" : "transparent", transition: "background 0.15s",
                    }}
                    onMouseEnter={e => { if (!isLoaded) e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={e => { if (!isLoaded) e.currentTarget.style.background = "transparent"; }}>

                      {/* Toggle checkbox */}
                      <div
                        onClick={isLoading ? undefined : () => toggleLayer(cl.key)}
                        style={{
                          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                          border: `2px solid ${isError ? "#ef4444" : isLoaded ? color : "#d1d5db"}`,
                          background: isLoaded ? color : "transparent",
                          cursor: isLoading ? "wait" : "pointer", transition: "all 0.15s",
                        }}
                      />

                      {/* Name — inline edit for own layers */}
                      {isEditing ? (
                        <input
                          autoFocus
                          defaultValue={cl.name}
                          style={{
                            flex: 1, fontSize: 11, border: "1px solid #6366f1",
                            borderRadius: 4, padding: "1px 5px", outline: "none",
                          }}
                          onBlur={async e => {
                            const newName = e.target.value.trim();
                            setEditingClientId(null);
                            if (!newName || newName === cl.name) return;
                            try {
                              const token = authTokenRef.current || await getToken();
                              await fetch(`${PORTAL_API_URL}/api/client-layers?id=${cl.id}`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ name: newName }),
                              });
                              setClientLayers(prev => prev.map(l => l.id === cl.id ? { ...l, name: newName } : l));
                            } catch {}
                          }}
                          onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingClientId(null); }}
                        />
                      ) : (
                        <span
                          style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={cl.name}
                        >
                          {cl.name}
                          {cl.is_shared ? <span style={{ marginLeft: 4, fontSize: 9, color: "#6366f1", verticalAlign: "middle" }}>●</span> : null}
                        </span>
                      )}

                      {/* Loading spinner */}
                      {isLoading && <Loader2 size={11} color="#9ca3af" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />}

                      {/* Owner controls: rename + share toggle + delete */}
                      {canEdit && !isLoading && !isEditing && (
                        <>
                          {/* Rename */}
                          <button
                            onClick={e => { e.stopPropagation(); setEditingClientId(cl.id); }}
                            title="Rename"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0, color: "#d1d5db" }}
                            onMouseEnter={e => { e.currentTarget.style.color = "#6366f1"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = "#d1d5db"; }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          {/* Share toggle */}
                          <button
                            onClick={async e => {
                              e.stopPropagation();
                              const newShared = !cl.is_shared;
                              try {
                                const token = authTokenRef.current || await getToken();
                                await fetch(`${PORTAL_API_URL}/api/client-layers?id=${cl.id}`, {
                                  method: "PATCH",
                                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ is_shared: newShared }),
                                });
                                setClientLayers(prev => prev.map(l => l.id === cl.id ? { ...l, is_shared: newShared } : l));
                              } catch {}
                            }}
                            title={cl.is_shared ? "Shared with project — click to make private" : "Private — click to share with project"}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0, color: cl.is_shared ? "#6366f1" : "#d1d5db" }}
                            onMouseEnter={e => { e.currentTarget.style.color = "#6366f1"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = cl.is_shared ? "#6366f1" : "#d1d5db"; }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                          </button>
                          {/* Delete */}
                          <button
                            onClick={async e => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete "${cl.name}"?`)) return;
                              if (isLoaded) toggleLayer(cl.key);
                              try {
                                const token = authTokenRef.current || await getToken();
                                await fetch(`${PORTAL_API_URL}/api/client-layers?id=${cl.id}`, {
                                  method: "DELETE",
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                setClientLayers(prev => prev.filter(l => l.id !== cl.id));
                                setClientUsage(prev => ({ ...prev, used: Math.max(0, prev.used - (cl.size_bytes || 0)) }));
                              } catch {}
                            }}
                            title="Delete layer"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", flexShrink: 0, color: "#d1d5db" }}
                            onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = "#d1d5db"; }}
                          >
                            <Trash2 size={11} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>{/* /padding wrapper */}
      </div>{/* /sidebar */}

      {/* ── Client layer feature property card ── */}
      {clientFeatureCard && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          zIndex: 1200, background: "#fff", border: "1px solid #e5e7eb",
          borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          minWidth: 240, maxWidth: 380, fontFamily: "'Inter','Helvetica Neue',sans-serif",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb",
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
              {clientFeatureCard.name}
            </span>
            <button
              onClick={() => setClientFeatureCard(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 2, display: "flex" }}
            >
              ✕
            </button>
          </div>
          {/* Properties */}
          <div style={{ padding: "8px 12px", maxHeight: 280, overflowY: "auto" }}>
            {clientFeatureCard.lat != null && (
              <div style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 11 }}>
                <span style={{ color: "#6b7280", minWidth: 80 }}>Latitude</span>
                <span style={{ color: "#111827", fontWeight: 500 }}>{clientFeatureCard.lat.toFixed(6)}</span>
              </div>
            )}
            {clientFeatureCard.lon != null && (
              <div style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 11 }}>
                <span style={{ color: "#6b7280", minWidth: 80 }}>Longitude</span>
                <span style={{ color: "#111827", fontWeight: 500 }}>{clientFeatureCard.lon.toFixed(6)}</span>
              </div>
            )}
            {Object.keys(clientFeatureCard.props).length > 0
              ? Object.entries(clientFeatureCard.props).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 11 }}>
                    <span style={{ color: "#6b7280", minWidth: 80, wordBreak: "break-word" }}>{k}</span>
                    <span style={{ color: "#111827", fontWeight: 500, wordBreak: "break-word" }}>{v}</span>
                  </div>
                ))
              : <div style={{ fontSize: 11, color: "#9ca3af" }}>No attributes</div>
            }
          </div>
        </div>
      )}

      {/* ── Legend Style Panel ── */}
      {/* ── Annotation layer edit panel — hidden while draw panel is active ── */}
      {annoEditKey && annoLayerStates[annoEditKey] === "loaded" && !drawTool && drawFeatures.length === 0 && (
        <ToolPanel
          title="Edit Annotation Layer"
          sidebarOpen={isSidebarOpen}
          width={300}
          onClose={() => { setAnnoEditKey(null); }}
        >
          {/* Layer name + feature count */}
          <div style={{ marginBottom: 12 }}>
            <input
              value={annoEditLayerName}
              onChange={e => { const v = e.target.value; setAnnoEditLayerName(v); annoEditLayerNameRef.current = v; scheduleAutoSave(annoEditKey); }}
              placeholder="Layer name…"
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #d1d5db",
                borderRadius: 6, fontSize: 13, fontWeight: 600, outline: "none",
                boxSizing: "border-box", color: "#111827", background: "#fff" }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, paddingLeft: 2 }}>
              <span style={{ fontSize: 10, color: "#9ca3af" }}>
                {annoEditFeatures.length} feature{annoEditFeatures.length !== 1 ? "s" : ""}
              </span>
              {savingAnnoEdit && (
                <Loader2 size={10} style={{ animation: "spin 1s linear infinite", color: "#9ca3af" }} />
              )}
            </div>
          </div>

          {annoEditFeatures.length === 0 && (
            <div style={{ fontSize: 11, color: "#9ca3af", padding: "4px 0 8px" }}>No features in this layer.</div>
          )}

          {annoEditFeatures.map(f => {
            let labelVal = f.label;
            return (
              <div key={f.id} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 8px", marginBottom: 5, borderRadius: 7,
                border: "1px solid #e5e7eb", background: "#f9fafb",
              }}>
                {/* Type badge */}
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 3, flexShrink: 0,
                  background: f.type === "line" ? "#eff6ff" : "#f5f3ff",
                  color: f.type === "line" ? "#3b82f6" : "#7c3aed", letterSpacing: 0.3,
                }}>
                  {f.type === "line" ? "LINE" : "POLY"}
                </span>

                {/* Label input */}
                <input
                  key={f.id + f.label}
                  defaultValue={f.label}
                  onChange={e => { labelVal = e.target.value; }}
                  onBlur={() => updateAnnoEditFeatureLabel(f.id, labelVal)}
                  onKeyDown={e => { if (e.key === "Enter") { updateAnnoEditFeatureLabel(f.id, labelVal); e.target.blur(); } }}
                  placeholder="Label…"
                  style={{ flex: 1, fontSize: 12, color: "#374151", border: "none",
                    background: "transparent", outline: "none", minWidth: 0 }}
                />

                {/* Colour picker */}
                <label title="Change colour" style={{ cursor: "pointer", flexShrink: 0 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 3, display: "block",
                    background: f.color, border: "1.5px solid #d1d5db" }} />
                  <input type="color" defaultValue={f.color}
                    onInput={e => updateAnnoFeatureColorInPlace(f.id, e.target.value)}
                    onChange={e => updateAnnoEditFeatureColor(f.id, e.target.value)}
                    style={{ width: 0, height: 0, opacity: 0, padding: 0, border: 0, position: "absolute" }} />
                </label>

                {/* Fly to feature */}
                <button
                  title="Fly to feature"
                  onClick={() => {
                    const verts = f.vertices;
                    if (!verts?.length) return;
                    const Cesium = window.Cesium;
                    const viewer = viewerRef.current;
                    if (!viewer || !Cesium) return;
                    const lons = verts.map(v => v.lon);
                    const lats = verts.map(v => v.lat);
                    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
                    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
                    const center = Cesium.Cartesian3.fromDegrees((minLon + maxLon) / 2, (minLat + maxLat) / 2);
                    const corner = Cesium.Cartesian3.fromDegrees(minLon, minLat);
                    const radius = Math.max(Cesium.Cartesian3.distance(center, corner), 100);
                    viewer.camera.flyToBoundingSphere(
                      new Cesium.BoundingSphere(center, radius),
                      { duration: 1.2, offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, 0) }
                    );
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 2,
                    color: "#9ca3af", display: "flex", flexShrink: 0 }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>
                  </svg>
                </button>

                {/* Delete feature */}
                <button onClick={() => deleteAnnoEditFeature(f.id)} title="Delete feature"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 2,
                    color: "#9ca3af", display: "flex", flexShrink: 0 }}>
                  <Trash2 size={11} />
                </button>
              </div>
            );
          })}

        </ToolPanel>
      )}

      {/* ── Draw: active tool / unsaved features panel ── */}
      {(drawTool || drawFeatures.length > 0) && !showSaveDrawing && !drawEditLabel && (
        <ToolPanel
          title={drawTool === "line" ? "Draw Line" : drawTool === "polygon" ? "Draw Polygon" : "Annotations"}
          sidebarOpen={isSidebarOpen}
          width={290}
          onClose={() => { stopDrawTool(); clearDrawEntities(); setDrawFeatures([]); }}
        >
          {/* Active tool: instruction */}
          {drawTool && drawFeatures.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              {drawTool === "line"
                ? <PenLine size={32} style={{ marginBottom: 12, color: "#d1d5db" }} />
                : <Hexagon  size={32} style={{ marginBottom: 12, color: "#d1d5db" }} />}
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 6 }}>
                {drawTool === "line" ? "Draw a line" : "Draw a polygon"}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                {drawTool === "line"
                  ? "Click on the map to add points. Double-click to finish the line."
                  : "Click on the map to add vertices. Click the first vertex or double-click to close the polygon."}
              </div>
            </div>
          )}
          {drawTool && drawFeatures.length > 0 && (
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>
              {drawTool === "line"
                ? "Click to add more points. Double-click to finish."
                : "Click to add vertices. Click first vertex or double-click to close."}
            </div>
          )}

          {/* Feature list */}
          {drawFeatures.length > 0 && (
            <div>
              {drawFeatures.map(f => (
                <div key={f.id} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 8px", marginBottom: 4, borderRadius: 7,
                  border: "1px solid #e5e7eb", background: "#f9fafb",
                }}>
                  {f.type === "line"
                    ? <PenLine size={12} color={f.color} style={{ flexShrink: 0 }} />
                    : <Hexagon  size={12} color={f.color} style={{ flexShrink: 0 }} />}
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: "#374151",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.label || (f.type === "line" ? "Line" : "Polygon")}
                  </span>
                  {/* Per-feature colour swatch */}
                  <label title="Change colour" style={{ cursor: "pointer", flexShrink: 0 }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: 3, display: "block",
                      background: f.color, border: "1.5px solid #d1d5db",
                    }} />
                    <input type="color" value={f.color}
                      onChange={e => updateFeatureColor(f.id, e.target.value)}
                      style={{ width: 0, height: 0, opacity: 0, padding: 0, border: 0, position: "absolute" }} />
                  </label>
                  <button onClick={() => setDrawEditLabel(f.id)} title="Edit label"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2,
                      color: "#6b7280", display: "flex", flexShrink: 0 }}>
                    <Type size={12} />
                  </button>
                  <button onClick={() => deleteDrawFeature(f.id)} title="Delete feature"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 2,
                      color: "#9ca3af", display: "flex", flexShrink: 0 }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 6,
                marginTop: 6, paddingTop: 8, borderTop: "1px solid #f3f4f6" }}>
                <button title="Discard all features"
                  onClick={() => { stopDrawTool(); clearDrawEntities(); setDrawFeatures([]); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center",
                    width: 30, height: 28, borderRadius: 6,
                    border: "1px solid #fca5a5", background: "#fff7f7",
                    cursor: "pointer", color: "#ef4444" }}>
                  <Trash2 size={13} />
                </button>
                {!isDemoUser && (
                  <button title="Save annotation layer"
                    onClick={() => {
                      if (annoEditKey) { appendDrawToAnnoLayer(annoEditKey); return; }
                      setSaveDrawingTarget(annoLayers.length > 0 ? annoLayers[0].key : "new");
                      setSaveDrawingName("");
                      setShowSaveDrawing(true);
                    }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 28, borderRadius: 6,
                      border: "none", background: "#111827",
                      cursor: "pointer", color: "#fff" }}>
                    <Save size={13} />
                  </button>
                )}
              </div>
            </div>
          )}
        </ToolPanel>
      )}

      {/* ── Draw: label-edit panel ── */}
      {drawEditLabel && (() => {
        const feat = drawFeatures.find(f => f.id === drawEditLabel);
        if (!feat) return null;
        let labelVal = feat.label;
        return (
          <ToolPanel
            title="Edit label"
            sidebarOpen={isSidebarOpen}
            width={280}
            onClose={() => setDrawEditLabel(null)}
          >
            <input
              autoFocus
              defaultValue={feat.label}
              onChange={e => { labelVal = e.target.value; }}
              onKeyDown={e => {
                if (e.key === "Enter") updateFeatureLabel(drawEditLabel, labelVal);
                if (e.key === "Escape") setDrawEditLabel(null);
              }}
              placeholder="e.g. Building A boundary…"
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #d1d5db",
                borderRadius: 6, fontSize: 12, outline: "none", boxSizing: "border-box", color: "#111827" }}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDrawEditLabel(null)} title="Skip"
                style={{ display: "flex", alignItems: "center", justifyContent: "center",
                  width: 30, height: 28, borderRadius: 6,
                  border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", color: "#6b7280" }}>
                <X size={13} />
              </button>
              <button onClick={() => updateFeatureLabel(drawEditLabel, labelVal)} title="Save label"
                style={{ display: "flex", alignItems: "center", justifyContent: "center",
                  width: 30, height: 28, borderRadius: 6,
                  border: "none", background: "#111827", cursor: "pointer", color: "#fff" }}>
                <CheckCircle size={13} />
              </button>
            </div>
          </ToolPanel>
        );
      })()}

      {/* ── Draw: save layer panel ── */}
      {showSaveDrawing && !isDemoUser && (
        <ToolPanel
          title="Save annotation layer"
          sidebarOpen={isSidebarOpen}
          width={280}
          onClose={() => setShowSaveDrawing(false)}
        >
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
            {drawFeatures.length} feature{drawFeatures.length !== 1 ? "s" : ""} · {selectedProject?.name || "—"}
          </div>
          {annoLayers.length > 0 && (
            <select
              value={saveDrawingTarget}
              onChange={e => setSaveDrawingTarget(e.target.value)}
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #d1d5db",
                borderRadius: 6, fontSize: 12, outline: "none", boxSizing: "border-box",
                color: "#111827", marginBottom: 8, background: "#fff" }}
            >
              <option value="new">+ New layer</option>
              {annoLayers.map(l => (
                <option key={l.key} value={l.key}>{l.name}</option>
              ))}
            </select>
          )}
          {saveDrawingTarget === "new" && (
            <input
              autoFocus
              value={saveDrawingName}
              onChange={e => setSaveDrawingName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && saveDrawingName.trim()) saveAnnotationLayer(saveDrawingName); }}
              placeholder="Layer name…"
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #d1d5db",
                borderRadius: 6, fontSize: 12, outline: "none", boxSizing: "border-box", color: "#111827" }}
            />
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setShowSaveDrawing(false)} title="Cancel"
              style={{ display: "flex", alignItems: "center", justifyContent: "center",
                width: 30, height: 28, borderRadius: 6,
                border: "1px solid #e5e7eb", background: "#f9fafb", cursor: "pointer", color: "#6b7280" }}>
              <X size={13} />
            </button>
            <button
              disabled={(saveDrawingTarget === "new" && !saveDrawingName.trim()) || savingDrawing || savingAnnoEdit}
              onClick={() => saveDrawingTarget === "new"
                ? saveAnnotationLayer(saveDrawingName)
                : appendDrawToAnnoLayer(saveDrawingTarget)
              }
              title="Save to project"
              style={{ display: "flex", alignItems: "center", justifyContent: "center",
                width: 30, height: 28, borderRadius: 6, border: "none",
                background: (saveDrawingTarget !== "new" || saveDrawingName.trim()) && !savingDrawing && !savingAnnoEdit ? "#111827" : "#9ca3af",
                cursor: (saveDrawingTarget !== "new" || saveDrawingName.trim()) && !savingDrawing && !savingAnnoEdit ? "pointer" : "not-allowed",
                color: "#fff" }}
            >
              {(savingDrawing || savingAnnoEdit)
                ? <Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} />
                : <Save size={13} />}
            </button>
          </div>
        </ToolPanel>
      )}

      {/* ── Point Inspector panel ── */}
      {isIdentifyMode && (
        <ToolPanel
          title="Point Inspector"
          sidebarOpen={isSidebarOpen}
          width={420}
          info="Click any data point on the globe to view its deformation time series and site statistics."
          onClose={() => { clearIdentifyTool(); setIsIdentifyMode(false); }}
        >
          <TrendLine
            selectedPoints={selectedPoints}
            multiSelect={multiSelect}
            setMultiSelect={setMultiSelect}
            allowedTools={allowedTools}
          />
        </ToolPanel>
      )}

      {/* ── 3D Surface Modeler panel ── */}
      {isPolygonMode && (() => {
        const loadedEntry   = Object.entries(layerStates).find(([, s]) => s.status === "loaded" && s.hasVel);
        const rangeMin      = loadedEntry ? loadedEntry[1].minV : null;
        const rangeMax      = loadedEntry ? loadedEntry[1].maxV : null;
        const layerColorStops = (loadedEntry ? loadedEntry[1].colorStops : null) || DEFAULT_PALETTE.stops;

        return (
          <ToolPanel
            title="3D Surface Modeler"
            sidebarOpen={isSidebarOpen}
            width={480}
            onClose={() => { clearPolygonTool(); setIsPolygonMode(false); }}
          >
            <Surface3DChart
              surfaceData={surfaceData}
              polygonDrawn={surfaceData.length > 0}
              rangeMin={rangeMin}
              rangeMax={rangeMax}
              valueLabel={surfaceLabel}
              variant="cesium"
              colorStops={layerColorStops}
              epochs={surfaceEpochs}
              selectedEpoch={surfaceSelectedEpoch}
              onEpochChange={setSurfaceSelectedEpoch}
              epochsLoading={surfaceEpochsLoading}
              onLoadEpochs={loadSurfaceEpochs}
              onCloseEpochs={() => {
                setSurfaceEpochs([]);
                setSurfaceSelectedEpoch(null);
                surfaceRawCacheRef.current = {};
              }}
              onAxisModeChange={(mode) => {
                setSurfaceAxisMode(mode);
                surfaceAxisModeRef.current = mode;
              }}
              onIdwPowerChange={(power) => {
                setSurfaceIdwPower(power);
                surfaceIdwPowerRef.current = power;
              }}
              onOpacityChange={(val) => {
                setSurfaceOpacity(val);
                surfaceOpacityRef.current = val;
              }}
              onPointsOpacityChange={setSurfacePointsOpacity}
              onDiffChange={(a, b) => {
                setSurfaceDiffEpochA(a);
                setSurfaceDiffEpochB(b);
              }}
              onExportGif={handleSurfaceGifExport}
              onExportDiffPng={handleSurfaceDiffPngExport}
              onApplyColorStops={(stops) => {
                const entry = Object.entries(layerStates).find(([, s]) => s.status === "loaded");
                if (!entry) return;
                const [key] = entry;
                setLayerStates(prev => ({
                  ...prev,
                  [key]: { ...prev[key], colorStops: stops },
                }));
              }}
            />
          </ToolPanel>
        );
      })()}

      {/* ── Legend Colours panel ── */}
      {legendStyleKey != null && (() => {
        const st = layerStates[legendStyleKey];
        if (!st) return null;
        const currentStops = st.colorStops || DEFAULT_PALETTE.stops;
        const STOP_LABELS  = ["Neg max", "Neg mid", "Zero", "Pos mid", "Pos max"];
        return (
          <ToolPanel
            title="Legend Colours"
            width={240}
            onClose={() => setLegendStyleKey(null)}
          >
            <div style={{ padding: "4px 0 8px" }}>
              {/* Live gradient preview */}
              <div style={{ height: 10, borderRadius: 3, background: stopsToGradient(currentStops), marginBottom: 14 }} />
              {/* 5 colour pickers */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {currentStops.map((hex, idx) => (
                  <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <input
                      type="color"
                      value={hex}
                      onChange={e => {
                        const next = [...currentStops];
                        next[idx] = e.target.value;
                        recolorLayer(legendStyleKey, st.activeCol, next);
                      }}
                      style={{ width: 30, height: 30, border: "1px solid #e5e7eb", borderRadius: 4, cursor: "pointer", padding: 1 }}
                    />
                    <span style={{ fontSize: 9, color: "#9ca3af", textAlign: "center", lineHeight: 1.2 }}>{STOP_LABELS[idx]}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => recolorLayer(legendStyleKey, st.activeCol, DEFAULT_PALETTE.stops)}
                style={{
                  marginTop: 14, width: "100%", padding: "7px 0",
                  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
                  fontSize: 12, fontWeight: 500, color: "#6b7280", cursor: "pointer",
                }}
              >
                Reset to default
              </button>
            </div>
          </ToolPanel>
        );
      })()}

      {/* ── Area & Perimeter — fixed card ── */}
      {isAreaMode && (
        <ToolPanel
          title="Area & Perimeter"
          width={264}
          onClose={() => { clearAreaTool(); setIsAreaMode(false); }}
        >
          {!areaResult ? (
            <div style={{ padding: "24px 18px", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eeeef2",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Hexagon size={20} color="#232231" />
              </div>
              <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                Measure area & perimeter
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
                Click on the map to place vertices. Double-click to close the polygon.
              </p>
            </div>
          ) : (
            <div style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Area</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                    {areaResult.area >= 1e6
                      ? `${(areaResult.area / 1e6).toFixed(3)} km²`
                      : `${areaResult.area.toFixed(1)} m²`}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Perimeter</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                    {areaResult.perim >= 1000
                      ? `${(areaResult.perim / 1000).toFixed(3)} km`
                      : `${areaResult.perim.toFixed(1)} m`}
                  </span>
                </div>
                {areaResult.count > 0 && (
                  <>
                    <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Points inside</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{areaResult.count.toLocaleString()}</span>
                    </div>
                    {areaResult.avgVel !== null && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Avg. velocity</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{areaResult.avgVel.toFixed(2)} mm/yr</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              <button
                onClick={() => clearAreaTool()}
                style={{
                  marginTop: 14, width: "100%", padding: "7px 0",
                  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
                  fontSize: 12, fontWeight: 500, color: "#6b7280", cursor: "pointer",
                }}
              >
                Clear & draw new polygon
              </button>
            </div>
          )}
        </ToolPanel>
      )}

      {/* ── Distance tool — fixed card ── */}
      {isDistanceMode && (
        <ToolPanel
          title="Distance"
          width={264}
          onClose={() => { clearDistanceTool(); setIsDistanceMode(false); }}
        >
          {!distanceResult ? (
            <div style={{ padding: "24px 18px", textAlign: "center" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eeeef2",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
                <Ruler size={20} color="#232231" />
              </div>
              <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                {distanceStep === 1 ? "Click point B on the map" : "Measure distance"}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>
                {distanceStep === 1
                  ? "Click a second point to compute the geodesic distance."
                  : "Click point A on the map, then click point B."}
              </p>
            </div>
          ) : (
            <div style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Distance</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                    {distanceResult.dist >= 1000
                      ? `${(distanceResult.dist / 1000).toFixed(3)} km`
                      : `${distanceResult.dist.toFixed(1)} m`}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Point A</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>
                    {Math.abs(distanceResult.p1.lat).toFixed(4)}°{distanceResult.p1.lat >= 0 ? "N" : "S"}&nbsp;
                    {Math.abs(distanceResult.p1.lon).toFixed(4)}°{distanceResult.p1.lon >= 0 ? "E" : "W"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280" }}>Point B</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>
                    {Math.abs(distanceResult.p2.lat).toFixed(4)}°{distanceResult.p2.lat >= 0 ? "N" : "S"}&nbsp;
                    {Math.abs(distanceResult.p2.lon).toFixed(4)}°{distanceResult.p2.lon >= 0 ? "E" : "W"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => { clearDistanceTool(); }}
                style={{
                  marginTop: 14, width: "100%", padding: "7px 0",
                  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
                  fontSize: 12, fontWeight: 500, color: "#6b7280", cursor: "pointer",
                }}
              >
                Measure again
              </button>
            </div>
          )}
        </ToolPanel>
      )}

      {/* ── Profile Viewer panel ── */}
      {isSectionMode && (
        <ToolPanel
          title="Profile Viewer"
          sidebarOpen={isSidebarOpen}
          width={480}
          info="Click two points on the globe to extract a deformation profile along the transect between them."
          onClose={() => { clearSectionTool(); setIsSectionMode(false); }}
        >
          <SectionProfileChart
            variant="cesium"
            hideHistory={true}
            profileData={sectionProfileData}
            hoveredIndexFromMap={sectionHoveredIndex}
            onHoverIndex={(idx) => {
              if (idx === sectionHoveredIndexRef.current) return;
              sectionHoveredIndexRef.current = idx;
              setSectionHoveredIndex(idx);
              const e = sectionHoverEntityRef.current;
              if (e) {
                e.show = idx !== null;
                if (idx !== null && sectionSamplesRef.current[idx]) {
                  const pos = sectionSurfacePos(sectionSamplesRef.current[idx]);
                  if (pos) e.position = new window.Cesium.ConstantPositionProperty(pos);
                  else e.show = false;
                }
              }
            }}
            onClear={clearSectionTool}
          />
        </ToolPanel>
      )}

      {/* ── Onboarding modal + tour ── */}
      <OnboardingModal
        visible={onboardPhase === "modal" && appReady}
        onStart={() => {
          localStorage.setItem("inspekt_onboarded", "1");
          setOnboardPhase("done");
        }}
      />
      <OnboardingTour
        active={false}
        onDismiss={() => {}}
      />

    </div>
  );
}
